import { Component, OnInit, OnDestroy, inject, Input, Output, EventEmitter, SimpleChanges, ViewChild, ViewChildren, QueryList, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, Observable, of, firstValueFrom, forkJoin } from 'rxjs';
import { map, catchError, finalize, timeout, switchMap, take } from 'rxjs/operators';
import { UploadDialogState, UploadMediaDialogService } from '../services/upload-media-dialog.service';
import {
  AiServiceService,
  DocumentCategoryService,
  DocumentService,
  FileService,
  ResumableUploadService,
  SidebarService,
  GlobalUploadQueueService,
  UploadManagerService,
  AccessPermissionService,
  DepartmentService,
  UserService,
  SingleToastMessageService,
  VenueService,
  SettingService,
  UploadStatus,
  LookupMasterService,
  WorkflowService,
  MessageService,
} from '@doc/api/server-bo';
import { Router } from '@angular/router';
import { ComponentSharedModule, DocConfirmService, DocNgbDateRangeComponent } from '@doc/component/shared';
import { ResumableUploadComponent } from '../resumable-upload/resumable-upload.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EnvConfig, Environment } from '@doc/core';
import { formatAmmViewLogDateTime } from '../view-log/format-view-log-datetime';
import { ViewLogComponent, ViewLogItem } from '../view-log/view-log.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
type UploadDisplayState = 'pending' | 'uploading' | 'uploaded' | 'failed';

interface UploadItemDisplay {
  key: string;
  type: 'file' | 'folder';
  displayName: string;
  originalName: string;
  fileCount: number;
  totalSize: number;
  files: File[];
  uploadedCount: number;
  totalCount: number;
  status: UploadDisplayState;
  statusLabel: string;
}

@Component({
  selector: 'app-upload-media-dialog',
  templateUrl: './upload-media-dialog.component.html',
  styleUrls: ['./upload-media-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    ComponentSharedModule,
    ResumableUploadComponent,
    TranslateModule,
    NgbTooltipModule,
    ViewLogComponent],
  providers: [{ provide: MessageService, useClass: SingleToastMessageService }],
})
export class UploadMediaDialogComponent implements OnInit, OnDestroy {
  @Input() visible: boolean = false;
  @Input() editMode: boolean = false;
  @Input() folderMetadataEditMode: boolean = false;
  @Input() editEntityType: 'file' | 'folder' = 'file';
  @Input() existingData: any = null;
  @Input() existingFolderData: any = null;
  @Input() folderMetadataId: number = 0;
  @Input() fileId: number = 0;
  @Input() parentId: number = 0;
  @Input() folderId: number = 0;

  private readonly bytesPerGb = 1024 * 1024 * 1024;
  private readonly bytesPerMb = 1024 * 1024;
  private readonly bytesPerTb = 1024 * 1024 * 1024 * 1024;
  private readonly bytesPerPb = 1024 * 1024 * 1024 * 1024 * 1024;
  private readonly bytesPerEb = 1024 * 1024 * 1024 * 1024 * 1024 * 1024;
  private readonly unlimitedQuotaBytes = -1;

  // Library context forwarded from the host view (My Files / Department /
  // Org Assets). Used when the backend cannot infer the library from a
  // parent folder (root-level uploads or freshly created subfolders).
  libraryType: number  = 1;
  private libraryDepartmentId: number | null = null;

  @Output() onCloseDialog = new EventEmitter<void>();
  /** Emits folderId when upload succeeds. Named without `on` prefix to avoid DOM `on*` collision in templates. */
  @Output() uploadSuccess = new EventEmitter<number>();

  private emitUploadSuccess(folderId: number): void {
    const normalizedFolderId = Number.isFinite(Number(folderId)) ? Number(folderId) : 0;
    this.uploadSuccess.emit(normalizedFolderId);
    this.uploadDialogService.notifyUploadSuccess(
      normalizedFolderId,
      this.getUploadDestinationLibraryType()
    );
  }

  getUploadDestinationLibraryType(): number {
    if (this.folderPickerIsDepartment) {
      return 2;
    }

    return this.libraryType && this.libraryType > 0 ? this.libraryType : 1;
  }

  // When the upload target is the user's root (folderId <= 0) we have
  // to tell the backend which library the file belongs to; otherwise
  // the backend inherits from the parent folder's (librarytype,
  // departmentid) and any UI hint is ignored. Always sending the hint
  // is harmless and keeps DTOs consistent across flows.
  private appendLibraryContextToFormData(formData: FormData, folderId: number): void {
    const effectiveLibraryType = this.folderPickerIsDepartment
      ? 2
      : this.libraryType && this.libraryType > 0
        ? this.libraryType
        : 1;
    formData.append('LibraryType', String(effectiveLibraryType));
    formData.append('IsUploaded', 'true');

    // DepartmentId is appended once via appendUploadDepartmentIdToFormData
    // so the dropdown selection cannot be overridden by a stale library context.
  }

  /** Prefer the Department dropdown value for all upload/create payloads. */
  private getEffectiveUploadDepartmentId(): number | null {
    const selected = Number(this.selectedUploadDepartmentId);
    if (Number.isFinite(selected) && selected > 0) {
      return selected;
    }
    return null;
  }

  private appendUploadDepartmentIdToFormData(formData: FormData, folderId?: number): void {
    const deptId = this.getEffectiveUploadDepartmentId();
    formData.append('DepartmentId', String(deptId && deptId > 0 ? deptId : -1));
  }

  private appendUploadCategoryIdToFormData(formData: FormData): void {
    if (this.selectedCategoryId != null && Number(this.selectedCategoryId) > 0) {
      formData.append('CategoryId', String(this.selectedCategoryId));
    }
    // When unset, omit CategoryId so the backend receives null/absent.
  }

  private getUploadDestinationDepartmentId(parentId: number): number | null {
    const effectiveLibraryType = this.getUploadDestinationLibraryType();
    if (effectiveLibraryType !== 2) {
      return null;
    }

    // Nested under a parent folder: use that folder's department (0 → treat as unset/-1).
    if (parentId > 0) {
      const parentNode = this.findNodeById(this.folderNodes, parentId);
      const nodeDept = Number(
        parentNode?.data?.departmentId ??
        parentNode?.data?.DepartmentId ??
        0
      );
      return Number.isFinite(nodeDept) && nodeDept > 0 ? nodeDept : null;
    }

    // Always honor the Department dropdown when the user has selected one.
    const selected = this.getEffectiveUploadDepartmentId();
    if (selected && selected > 0) {
      return selected;
    }

    // Root level with no selection → null (callers map to -1).
    return null;
  }

  private shouldApplyMetadataToUploadedItems(folderId: number): boolean {
    const normalizedFolderId = Number(folderId || 0);
    return !(
      this.isNewFolderOnUpload &&
      this.newlyCreatedFolderId > 0 &&
      normalizedFolderId === Number(this.newlyCreatedFolderId)
    );
  }

  private async applyMetadataToNewlyCreatedUploadFolderIfNeeded(): Promise<void> {
    if (!this.isNewFolderOnUpload || !this.newlyCreatedFolderId || this.newlyCreatedFolderId <= 0) {
      return;
    }
    if (!this.hasSelectedFolderMetadataForInheritance()) {
      return;
    }

    const folderId = Number(this.newlyCreatedFolderId);
    const folderResponse = await firstValueFrom(this.documentService.getFolderById(folderId));
    if (folderResponse?.httpStatus !== 200 || !folderResponse?.data) {
      throw new Error(folderResponse?.errorMessage || `Unable to load folder ${folderId} for metadata update.`);
    }

    const payload = this.buildFolderMetadataPayload(folderId, folderResponse.data);
    const updateResponse = await firstValueFrom(this.documentService.updateFolder(folderId, payload));
    if (updateResponse?.httpStatus !== 200) {
      throw new Error(updateResponse?.errorMessage || `Unable to update metadata for folder ${folderId}.`);
    }
    if(this.envConfig.isEventsSecExpanded){
      if (this.getNewEventName()?.trim()) {
        this.sidebarService.refreshEvents().subscribe();
      }
    }
    if (this.getNewTagsArray().length > 0) {
      this.sidebarService.refreshTags().subscribe();
    }
    if(this.envConfig.isLocationSecExpanded){
       this.loadLocations();
    }
  }

  selectedFolderName: string = 'My Files';
  folderNodes: any[] = [];
  filteredFolderNodes: any[] = [];
  folderSearchTerm: string = '';
  selectedNode: any = null;
  isLoadingFolders = false;
  isCreatingFolder = false;
  showNewFolderDialog = false;
  folderNameDraft = '';
  private optimisticCreatedFolderNamesByParent = new Map<number, Set<string>>();
  private folderPathCache = new Map<string, number>();
  private folderStructureCreatedFolderIds = new Set<number>();
  private folderStructureRootFolderIds = new Set<number>();
  folderStructureCreatedFolderIdsForUpload: number[] = [];
  perFileFolderIds: number[] = [];
  isFolderTreeVisible: boolean = false;
  locations: any[] = [];
  private readonly preferredLocationStorageKey = 'uploadMedia.preferredLocation';
  // Backing field for selectedLocation
  private _selectedLocation: any = null;
  // Stable array property for location chips view
  selectedLocationChips: any[] = [];
  // Public property used by upload logic - keeps single object interface
  get selectedLocation(): any {
    return this._selectedLocation;
  }
  set selectedLocation(value: any) {
    this._selectedLocation = value;
    this.selectedLocationChips = value ? [value] : [];
  }
  filteredLocations: any[] = [];

  private translateService = inject(TranslateService);
  private aiService = inject(AiServiceService);

  // dma-ai: state for the "send for AI" block shown when editing a file.
  aiEnabled = false;
  aiJobId: number | null = null;
  aiJobStage: string | null = null;
  aiSending = false;
  private subscription: Subscription | null = null;
  private uploadProgressSubscription?: Subscription;
  @ViewChild('resumableUpload') resumableUploadComponent!: any; // Reference to resumable upload component
  @ViewChild('titleInput') titleInput?: any;
  @ViewChildren('pickerFolderRenameInput') pickerFolderRenameInputs?: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('uploadItemRenameInput') uploadItemRenameInputs?: QueryList<ElementRef<HTMLInputElement>>;

  // Form fields
  title: string = '';
  editedFileName: string = '';
  selectedFileType: 'document' | 'image' | 'video' | null = null;
  selectedCategoryId: number | null = null;
  resubmitWorkflowOnUpdate = false;
  returnComment = '';
  resubmitComment = '';

  // Document detail fields aligned with document-page (excluding subject/template/content/attachment).
  recipient: string = '';
  signingAuthority: string = '';
  signingAuthorityId: number | null = null;
  signingAuthorities: Array<{ id: number; label: string }> = [];
  isLoadingSigningAuthorities = false;
  enableWorkflow = false;
  shareWithRecipients = false;
  workflowId: number | null = null;
  /** Extensions that may enter approval workflow on upload. */
  private static readonly WORKFLOW_DOCUMENT_EXTENSIONS = [
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
  ];
  workflows: Array<{ id: number; label: string; departmentId?: number | null; categoryIds?: number[] }> = [];
  isLoadingWorkflows = false;
  shareRecipients: Array<{ id: number; label: string }> = [];
  selectedShareRecipientIds: number[] = [];
  pendingShareRecipients: Array<{ id: number; label: string }> = [];
  isLoadingShareRecipients = false;
  systemDate: string = '';
  documentDate: Date | null = null;
  fileNumber: string = '';
  documentNumber: string = '';
  documentTypeId: string = '';
  documentTypeOptions: { value: string; label: string }[] = [];
  // Explicit Department selected for the upload; sent as DepartmentId in the payload.
  selectedUploadDepartmentId: number | null = null;

  // Guards against a stale getNextDocumentNumbers response overwriting a newer one.
  isLoadingDocumentNumbers = false;
  private documentNumbersLoadToken = 0;
  isLoadingDocumentTypes = false;
  private documentTypesLoadToken = 0;
  private workflowsLoadToken = 0;
  private signingAuthoritiesLoadToken = 0;
  private shareRecipientsLoadToken = 0;

  /** Local (not UTC) today as 'YYYY-MM-DD', used as the earliest selectable document Date. */
  get todayIsoDate(): string {
    const d = new Date();
    const offsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offsetMs).toISOString().slice(0, 10);
  }
  isCategoryLocked = false;
  get showFolderUploadOption(): boolean {
    return this.IS_FOLDER_UPLOAD_ENABLED;
  }
  /** Show the Department Assets picker when the upload queue is permitted. */
  get showFolderSelectionInFileUpload(): boolean {
    return (
      !this.editMode &&
      !this.folderMetadataEditMode &&
      this.canEnableDepartmentFolderPickerSource
    );
  }
  // Full category list from the API; the shown list is filtered by the selected Department.
  allUploadCategories: Array<{ label: string; value: number; departmentId: number | null; entityId: number }> = [];
  /** Skip category→folder sync when Category is set programmatically from folder context. */
  private suppressCategoryFolderSync = false;

  /** Categories visible in the dropdown: prefer isCategoryRoot folder ↔ entityId, else department. */
  get uploadCategories(): Array<{ label: string; value: number; departmentId: number | null; entityId: number }> {
    const categoryRootIds = this.getCategoryRootFolderIdsFromSelection();
    if (categoryRootIds.length > 0) {
      const linked = this.getCategoriesLinkedToFolderIds(categoryRootIds);
      if (linked.length > 0) {
        const deptId = this.selectedUploadDepartmentId;
        if (deptId) {
          const deptScoped = linked.filter((cat) => cat.departmentId === deptId);
          // Prefer department soft-filter only when it still leaves results (Business Admin fix).
          if (deptScoped.length > 0) {
            return this.ensureSelectedCategoryInList(deptScoped);
          }
        }
        return this.ensureSelectedCategoryInList(linked);
      }
    }

    const deptId = this.selectedUploadDepartmentId;
    if (!deptId) {
      return this.allUploadCategories;
    }
    const scoped = this.allUploadCategories.filter((cat) => cat.departmentId === deptId);
    return this.ensureSelectedCategoryInList(scoped);
  }

  /** Keep the currently selected category visible even if filters would hide it. */
  private ensureSelectedCategoryInList(
    list: Array<{ label: string; value: number; departmentId: number | null; entityId: number }>
  ): Array<{ label: string; value: number; departmentId: number | null; entityId: number }> {
    const selected = this.allUploadCategories.find(
      (cat) => cat.value === this.selectedCategoryId
    );
    if (selected && !list.some((cat) => cat.value === selected.value)) {
      return [selected, ...list];
    }
    return list;
  }
  readonly fileTypeOptions = [
    { label: 'Document', value: 'document' as const },
    { label: 'Image', value: 'image' as const },
    { label: 'Video', value: 'video' as const }
  ];
  
  // Backing field for selectedEvent
  private _selectedEvent: any = null;
  
  // Public property used by logic - maintains single object interface
  get selectedEvent(): any {
    return this._selectedEvent;
  }
  
  set selectedEvent(value: any) {
    const wasExistingEvent = this.isExistingEventValue(this._selectedEvent);
    this._selectedEvent = value;
    // Sync the view array
    this.selectedEventChips = value ? [value] : [];
    const isExistingEvent = this.isExistingEventValue(value);
    if (isExistingEvent) {
      this.applySelectedEventDateRange(value);
    } else if (wasExistingEvent && !this.resumeMetadataLocked) {
      // Switching from an existing dropdown event to "new event"/empty should require user date selection.
      this.eventDateRange = null;
    }
  }

  get isEventDateRangeLocked(): boolean {
    return (
      this.resumeMetadataLocked ||
      (this.isExistingEventValue(this.selectedEvent) &&
        this.hasValidDateInEvent(this.selectedEvent))
    );
  }

  // Stable array property for p-autoComplete view
  selectedEventChips: any[] = [];

  date: Date = new Date();
  /** Event date range [start, end] for uploads (required for submit). */
  eventDateRange: Date[] | null = null;
  /** Month shown when the range overlay opens (current month on each open). */
  eventRangeCalendarDefaultDate: Date = new Date();
  tags: any[] = [];
  /** Prevents sidebar tag refresh from overwriting user-edited tag selections. */
  private fileTagsHydrated = false;
  markAsSecure: boolean = false; // Default unchecked
  shareType: 'internal' | 'external' = 'internal';
  shareByDepartment: boolean = false;
  sharePermissionLevel: any[] = [];
  fileFolderPermissions: any[] = [];
  allFilePermissions: any[] = [];
  allFolderPermissions: any[] = [];
  users: any[] = [];
  allUsers: any[] = [];
  selectedUsers: any[] = [];
  selectedExternalUsers: any[] = [];
  externalUsers: any[] = [];
  sharedUsersList: any[] = [];
  selectedDepartment: any[] = [];
  departments: any[] = [];
  externalEmailsText: string = '';
  internalEmailsText: string = '';
  internalEmails: string[] = [];
  nonInternalDomainEmails: string[] = [];
  duplicateInternalEmails: string[] = [];
  alreadySharedInternalEmails: string[] = [];
  invalidInternalEmails: string[] = [];
  externalEmails: string[] = [];
  invalidExternalEmails: string[] = [];
  duplicateInputEmails: string[] = [];
  shareExpireDate: Date | null = null;
  today = new Date();
  selectedFiles: any[] = [];
  isDragging: boolean = false;
  /** Highlights Upload File vs Browse Folder tiles in the drop zone. */
  activeBrowseMode: 'file' | 'folder' = 'file';
  private dragDepth = 0;
  browseMenuOpen: boolean = false;
  renameDrafts: Record<string, string> = {};
  private editingUploadItemKey: string | null = null;
  private duplicateItemKeys = new Set<string>();
  private existingDuplicateFileNames = new Set<string>();
  private existingDuplicateFolderNames = new Set<string>();
  private selectionSourceKeyCounter = 0;
  private readonly fileStatusMap = new Map<string, UploadDisplayState>();
  readonly supportedFileTypes: string[] = [
    '.jpg', '.jpeg', '.tif', '.tiff', '.svg', '.ai', '.pdf', '.psd', '.pb', '.bmp',
    '.png', '.gif', '.mp4', '.mov', '.dng', '.zip', '.rar', '.dmg', '.mp3', '.wav',
    '.avi', '.mkv', '.webm', '.m4a', '.webp', '.flac', '.heic'
  ];
  readonly acceptedFileTypes: string = this.supportedFileTypes.join(',');
  readonly supportedFileTypesTooltip: string = `Supported file types: ${this.supportedFileTypes.join(', ')}`;

  // Dropdown options
  events: any[] = [];
  allEventsIncludingInactive: any[] = [];
  availableTags: any[] = [];
  filteredTags: any[] = [];
  filteredEvents: any[] = [];
  tagIds: number[] = [];
  folderMetadataName: string = '';
  folderMetaCreatedBy: string = '-';
  folderMetaCreatedOn: string = '-';
  folderMetaLastUpdatedBy: string = '-';
  folderMetaLastUpdatedOn: string = '-';
  folderMetaTotalViews: string = '0';
  folderMetaTotalDownloads: string = '0';
  fileMetaCreatedBy: string = '-';
  fileMetaCreatedOn: string = '-';
  fileMetaLastUpdatedBy: string = '-';
  fileMetaLastUpdatedOn: string = '-';
  fileMetaTotalViews: string = '0';
  fileMetaTotalDownloads: string = '0';
  mediaActivityLogs: ViewLogItem[] = [];
  showMetadataLogDialog: boolean = false;
  private initialEditStateSnapshot: string | null = null;

  getMetadataLogDialogHeader(): string {
    const itemName = this.resolveMetadataLogItemName();
    return itemName ? `View Log - ${itemName}` : 'View Log';
  }

  private resolveMetadataLogItemName(): string {
    const itemName = this.folderMetadataEditMode
      ? this.folderMetadataName
      : this.editedFileName ||
        this.editedFile?.fileName ||
        this.existingData?.fileName ||
        this.existingData?.entityName ||
        this.existingData?.titleEn;

    return String(itemName || '').trim();
  }

  // Create folder properties
  readonly folderNameMaxLength = 100;
  newFolderName: string = '';
  newlyCreatedFolderId: number = 0; // Track newly created folder to use for file upload
  isNewFolderOnUpload: boolean = false;
  /** Folder id from picker's Create Folder dialog; while set, that row is renameable when selected. */
  pickerCreatedFolderId: number | null = null;
  folderRenameDraft: string = '';

  // Current folder ID (can be passed from parent or set to 0 for root)
  currentFolderId: number = 0;

  /** Folder picker: Department / Org library tree vs My Files. */
  folderPickerIsDepartment = false;
  /** When true, hide My Files / Department Assets toggle (opened from a scoped route). */
  folderPickerSourceLocked = false;
  /** Full-access: narrow hierarchy to this department before loading tree. */
  pickerFilterDepartmentId: number | null = null;
  // Resumable upload configuration from environment
  get RESUMABLE_UPLOAD_ENABLED(): boolean {
    return this.envConfig?.resumableUpload?.enabled ?? true;
  }

  get RESUMABLE_UPLOAD_THRESHOLD(): number {
    return this.envConfig?.resumableUpload?.thresholdBytes ?? (100 * 1024 * 1024);
  }

  get IS_FOLDER_UPLOAD_ENABLED(): boolean {
    return this.envConfig?.enableUploadFolder ?? true;
  }

  get ALLOWED_UPLOAD_TYPES(): string[] {
    if (this.configuredAllowedUploadTypes.length > 0) {
      return this.configuredAllowedUploadTypes;
    }

    return this.envConfig?.allowedUploadTypes && this.envConfig.allowedUploadTypes.length > 0
      ? this.envConfig.allowedUploadTypes
      : [
          'image/jpeg',
          'image/png',
          'video/mp4',
          'video/quicktime',
          'application/pdf',
          'application/zip'
        ];
  }

  get UPLOAD_ACCEPT_ATTR(): string {
    return this.ALLOWED_UPLOAD_TYPES.join(', ');
  }

  get ALLOWED_UPLOAD_EXTENSIONS_LABEL(): string {
    if (this.configuredAllowedUploadTypes.length > 0) {
      return this.configuredAllowedUploadTypes.join(' ');
    }

    const mimeToExtension: Record<string, string> = {
      'image/jpeg': '.jpg, .jpeg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'video/mp4': '.mp4',
      'video/quicktime': '.mov',
      'application/pdf': '.pdf',
      'application/zip': '.zip'
    };

    const normalizedExtensions = this.ALLOWED_UPLOAD_TYPES.flatMap(type => {
      const normalizedType = (type || '').toLowerCase().trim();
      if (!normalizedType) {
        return [];
      }
      if (normalizedType.startsWith('.')) {
        return [normalizedType];
      }
      if (mimeToExtension[normalizedType]) {
        return mimeToExtension[normalizedType]
          .split(',')
          .map(extension => extension.trim())
          .filter(Boolean);
      }
      return [];
    });

    const uniqueExtensions = Array.from(new Set(normalizedExtensions));
    return uniqueExtensions.length > 0 ? uniqueExtensions.join(', ') : 'Based on configured types';
  }

  // Resumable upload properties
  useResumableUpload: boolean = false;
  showResumableUpload: boolean = false;
  completedResumableSessions: { filePath: string; fileName: string; fileSize: number }[] = [];
  editedFile: any = null;
  /** When editing, file has eventId but no EventDate on payload yet — apply date from sidebar events once they load. */
  private pendingEventDateFromSidebarEvents = false;
  allUploadsCompleted: boolean = false;
  resumeMetadataLocked: boolean = false;
  private pendingResumeSession: any | null = null;
  private isUploadActionInProgress: boolean = false;
  private uploadAutoCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private incompleteToastShown: boolean = false;
  private resumableAutoCloseTriggered: boolean = false;
  private resumableSuccessToastShown: boolean = false;
  /** One summary toast per upload batch — avoids flicker from per-file errors. */
  private resumableUploadErrorToastShown = false;
  private pendingEmptyFolderSelection = false;
  private pendingEmptyFolderRelativePaths = new Set<string>();
  showEmptyFolderDialog = false;
  private awaitingFolderPickerResult = false;
  private folderPickerHandledResult = false;
  private activeFolderInputEl: HTMLInputElement | null = null;
  private folderPickerFallbackTimer: ReturnType<typeof setTimeout> | null = null;
  // Store uploaded files to restore them when dialog reopens
  private uploadedFiles: File[] = [];
  incompleteSessionCount: number = 0;
  private isPlaceholderFile(file: File): boolean {
    return (file as any).__isPlaceholder === true;
  }

  private getRealSelectedFiles(): File[] {
    return (this.selectedFiles || []).filter(file => !this.isPlaceholderFile(file));
  }

  get visibleSelectedFiles(): File[] {
    return (this.selectedFiles || []).filter(file => !this.isPlaceholderFile(file));
  }

  get uploadItemsForDisplay(): UploadItemDisplay[] {
    if (this.editMode) {
      return [];
    }
    const files = this.visibleSelectedFiles.map(file => this.ensureUploadFileMetadata(file));
    const uploadStateBySourceKey = new Map<string, UploadDisplayState>();
    this.uploadManager.getAllUploads().forEach(upload => {
      const sourceKey = (upload.file as any).__sourceKey;
      if (!sourceKey) {
        return;
      }
      uploadStateBySourceKey.set(
        sourceKey,
        this.mapUploadStatusToDisplay(upload.status, upload.progress, !!upload.sessionId)
      );
    });
    const groups = new Map<string, { type: 'file' | 'folder'; originalName: string; files: File[] }>();

    files.forEach(file => {
      const relativePath = this.getNormalizedRelativePath(file);
      const parts = relativePath.split('/').filter(Boolean);
      const isFolder = parts.length > 1;
      const topToken = (file as any).__topLevelToken || (isFolder ? parts[0] : relativePath);
      const key = `${isFolder ? 'folder' : 'file'}:${topToken}`;

      if (!groups.has(key)) {
        groups.set(key, {
          type: isFolder ? 'folder' : 'file',
          originalName: (file as any).__originalTopLevelName || (isFolder ? parts[0] : file.name),
          files: []
        });
      }
      groups.get(key)!.files.push(file);
    });

    return Array.from(groups.entries()).map(([key, group]) => {
      const currentDisplayName = group.type === 'folder'
        ? this.getCurrentTopLevelFolderName(group.files[0])
        : group.files[0].name;
      const emptyNestedFolderCount = group.type === 'folder'
        ? Array.from(this.pendingEmptyFolderRelativePaths).filter(path => {
            const root = path.split('/').filter(Boolean)[0] || '';
            return root.toLowerCase() === currentDisplayName.toLowerCase();
          }).length
        : 0;
      let totalItemCount = group.files.length + emptyNestedFolderCount;
      const fileStates = group.files.map(file => this.getFileDisplayState(file, uploadStateBySourceKey));
      let uploadedCount = fileStates.filter(state => state === 'uploaded').length + emptyNestedFolderCount;
      let totalSize = group.files.reduce((sum, file) => sum + file.size, 0);
      const resumableFolderStats = group.type === 'folder'
        ? this.getResumableFolderStats(currentDisplayName)
        : null;
      if (resumableFolderStats) {
        uploadedCount = resumableFolderStats.uploadedCount;
        totalItemCount = resumableFolderStats.totalCount;
        totalSize = resumableFolderStats.totalBytes;
      }

      const hasFailed = fileStates.some(state => state === 'failed');
      let hasUploading = fileStates.some(state => state === 'uploading');
      let allUploaded = totalItemCount > 0 && uploadedCount >= totalItemCount;
      if (resumableFolderStats) {
        hasUploading = resumableFolderStats.isUploading && !resumableFolderStats.isComplete;
        allUploaded = resumableFolderStats.isComplete;
      }

      let status: UploadDisplayState = 'pending';
      if (hasFailed) {
        status = 'failed';
      } else if (allUploaded) {
        status = 'uploaded';
      } else if (hasUploading) {
        status = 'uploading';
      }

      let statusLabel = 'Pending';
      if (group.type === 'folder') {
        if (allUploaded) {
          statusLabel = 'Upload Complete';
        } else if (resumableFolderStats?.isPaused) {
          statusLabel = `Paused... ${uploadedCount} / ${totalItemCount} items`;
        } else if (hasUploading) {
          statusLabel = `Uploading... ${uploadedCount} / ${totalItemCount} items`;
        } else {
          statusLabel = `Pending... ${uploadedCount} / ${totalItemCount} items`;
        }
      } else if (status === 'uploaded') {
        statusLabel = 'Uploaded';
      } else if (status === 'uploading') {
        statusLabel = 'Uploading...';
      } else if (status === 'failed') {
        statusLabel = 'Failed';
      }

      return {
        key,
        type: group.type,
        displayName: currentDisplayName,
        originalName: group.originalName,
        fileCount: group.files.length,
        totalSize,
        files: group.files,
        uploadedCount,
        totalCount: totalItemCount,
        status,
        statusLabel
      };
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  get userId(): number {
    return Number(localStorage.getItem('userId')) || 1;
  }

  /** Public: used in template (e.g. isSharePermission). */
  readonly envConfig = inject<Environment>(EnvConfig);
  private configuredAllowedUploadTypes: string[] = [];
  isDepartmentShareEnabled: boolean = this.envConfig.isDepartemntShareEnable ?? false;
  allFolders: any[] = [];
  selectedFolderId: number | null = 0;
  /** Canonical department root folder id from hierarchy (org-asset uploads). */
  private cachedPickerDepartmentRootFolderId: number | null = null;

  constructor(
    private uploadDialogService: UploadMediaDialogService,
    private documentService: DocumentService,
    private fileService: FileService,
    private messageService: MessageService,
    private confirmationService: DocConfirmService,
    private resumableUploadService: ResumableUploadService,
    private sidebarService: SidebarService,
    private router: Router,
    private globalUploadQueue: GlobalUploadQueueService,
    private uploadManager: UploadManagerService,
    private departmentService: DepartmentService,
    private documentCategoryService: DocumentCategoryService,
    private userService: UserService,
    private venueService: VenueService,
    private settingService: SettingService,
    public accessService: AccessPermissionService,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private lookupMasterService: LookupMasterService,
    private workflowService: WorkflowService
  ) {
    this.isDepartmentShareEnabled = this.envConfig.isDepartemntShareEnable ?? false;
    if (!this.isDepartmentShareEnabled) {
      this.shareByDepartment = false;
    }
    this.systemDate = this.formatDisplayDate(new Date());
  }

  get orgAssetsRootLabel(): string {
    return this.sidebarService.getOrgAssetsRootLabel();
  }

ngOnInit(): void {
  if (!this.isDepartmentShareEnabled) {
    this.shareByDepartment = false;
  }

  this.loadAllowedUploadTypesFromConfiguration();

  this.uploadProgressSubscription = this.uploadManager.uploads$.subscribe(() => {
    if (this.useResumableUpload && this.visible) {
      this.cdr.detectChanges();
    }
  });

  this.subscription = this.uploadDialogService.dialogVisible$.subscribe(
    (state: UploadDialogState) => {
      console.log('Dialog state changed:', state);
      this.visible = state.visible;

      if (!state.visible) {
        this.editMode = false;
        this.folderMetadataEditMode = false;
        this.folderPickerSourceLocked = false;
        this.resubmitWorkflowOnUpdate = false;
        this.returnComment = '';
        this.resubmitComment = '';
        this.existingData = null;
        this.existingFolderData = null;
        return;
      }

      if (state.visible) {
        // Refresh active extensions each open so admin activate/deactivate shows without page reload.
        this.loadAllowedUploadTypesFromConfiguration();
        this.clearUploadStateIfAllCompleted();
        this.resumableAutoCloseTriggered = false;
        this.folderPickerSourceLocked = state.folderPickerSourceLocked === true;
        this.resubmitWorkflowOnUpdate = state.resubmitWorkflowOnUpdate === true;
        this.returnComment = String(state.returnComment ?? '').trim();
        this.resubmitComment = '';
        // Snapshot library context for this dialog session. Fall back to
        // Personal library (1) when no caller provided context so legacy
        // flows keep working.
        this.libraryType = typeof state.libraryType === 'number' && state.libraryType > 0
          ? state.libraryType
          : 1;
        this.libraryDepartmentId = state.departmentId ?? null;
        // Only seed Department dropdown when the host explicitly passed a department.
        // At Org Assets root with no selection, leave it empty so payload uses folderId 0 / dept null.
        if (this.libraryDepartmentId && this.libraryDepartmentId > 0) {
          this.selectedUploadDepartmentId = this.libraryDepartmentId;
        } else {
          this.selectedUploadDepartmentId = null;
        }
        // There is one destination source: Department Assets. Full-access users
        // receive the complete hierarchy; other users are filtered to their department.
        this.folderPickerIsDepartment = true;
        this.syncPickerDepartmentFilterFromContext();
        this.syncLibraryContextFromPicker();
        // Non–full-access: if opener didn't pass a department, keep department empty
        // at library root, but never wipe an explicit department from the opener.
        if (
          !this.selectedUploadDepartmentId &&
          this.libraryDepartmentId &&
          this.libraryDepartmentId > 0 &&
          Number(state.folderId) > 0
        ) {
          this.selectedUploadDepartmentId = this.libraryDepartmentId;
        }
        this.resetFolderPickerTreeForSession();
        if (this.showDepartmentDropdownForPicker && this.departments.length === 0) {
          this.getDepartments();
        }
        const pendingSelectedFiles = this.uploadDialogService.consumePendingSelectedFiles();
        // ✅ Apply auto-populated values
        if (state.autoPopulate) {
          console.log('✅ Applying auto-populate data:', state.autoPopulate);
          this.title = state.autoPopulate.title;
          if(this.envConfig.isEventsSecExpanded){
          this.selectedEvent = state.autoPopulate.selectedEvent;
          }
          if(this.envConfig.isLocationSecExpanded){
          if (state.autoPopulate.selectedLocation) {
            const resolvedAutoLocation = this.normalizePreferredLocation(state.autoPopulate.selectedLocation);
            if (resolvedAutoLocation) {
              this.selectedLocation = resolvedAutoLocation;
            }
          }
        }
          const autoDate = this.toSafeDate(state.autoPopulate.date);
          this.date = autoDate ?? new Date();
          const s = this.toSafeDate(state.autoPopulate.eventDateRangeStart);
          const e = this.toSafeDate(state.autoPopulate.eventDateRangeEnd);
          if (s && e) {
            this.eventDateRange = [s, e];
          } else if (s) {
            this.eventDateRange = [s, s];
          }
          this.tags = state.autoPopulate.tags;
          this.markAsSecure = state.autoPopulate.markAsSecure;
          this.shareType = state.autoPopulate.shareType || 'internal';
          if (this.shareType === 'external') {
            this.selectedExternalUsers = state.autoPopulate.shareUserIds || [];
            this.selectedUsers = [];
          } else {
            this.selectedUsers = state.autoPopulate.shareUserIds || [];
            this.selectedExternalUsers = [];
          }
          this.externalEmails = state.autoPopulate.shareEmails || [];
          this.externalEmailsText = this.externalEmails.join(', ');
          this.shareExpireDate = state.autoPopulate.shareExpireDate ? new Date(state.autoPopulate.shareExpireDate) : null;
          this.sharePermissionLevel = state.autoPopulate.sharePermissionLevel || [];
          this.selectedFileType = state.autoPopulate.selectedFileType ?? null;
          this.selectedCategoryId = state.autoPopulate.selectedCategoryId ?? null;
          this.isCategoryLocked = state.autoPopulate.categoryLocked === true;
          if (this.markAsSecure) {
            this.loadShareLookups();
          }
        } else {
          // Refresh date to current time if not auto-populated
          this.date = new Date();
          this.isCategoryLocked = false;
          if(this.envConfig.isLocationSecExpanded){
            this.selectedLocation = null;
          }
        }

        // Prefer the opener folder whenever we are on Department Assets.
        const initialFolderId =
          this.folderPickerIsDepartment || state.libraryType === 2
            ? Number(state.folderId) || 0
            : 0;
        this.applyInitialFolderSelection(initialFolderId);
        this.existingData = state.existingData ?? null;
        this.existingFolderData = state.existingFolderData ?? null;

        // Host dialog lives in app shell (no parent [editMode] binding). Drive edit vs upload purely from service state.
        const editingFile = !!(state.fileId && Number(state.fileId) > 0);
        const hasEditFolder = !!(state.editFolderId && Number(state.editFolderId) > 0);

        if (hasEditFolder) {
          this.folderMetadataId = state.editFolderId!;
          this.folderMetadataEditMode = true;
          this.editEntityType = 'folder';
        } else {
          this.folderMetadataEditMode = false;
          if (!editingFile) {
            this.folderMetadataId = 0;
          }
          this.editEntityType = 'file';
        }
        this.editMode = editingFile || hasEditFolder;
        this.mediaActivityLogs = [];

        if (this.editMode) {
          this.showResumableUpload = false;
        }

        // Avoid redundant event-list API calls while editing; reuse already-loaded events.
        const isEditFlow = editingFile || hasEditFolder;
        if (!isEditFlow) {
                 if(this.envConfig.isEventsSecExpanded){
          this.sidebarService.refreshEvents().subscribe();
                 }
        }

        if (hasEditFolder) {
          this.populateFolderMetadataForEdit();
          this.loadFolderMetadataFromApi();
          this.loadViewDownloadLogsForEdit('folder', this.folderMetadataId);
        } else if (editingFile) {
          this.getFileById(state.fileId!);
          this.loadViewDownloadLogsForEdit('file', state.fileId!);
        }

        // Document Type + Department dropdowns apply to file upload and file-metadata edit (not folders).
        if (!hasEditFolder) {
          this.loadDocumentTypeOptions();
          this.loadSigningAuthorityOptions();
          this.loadWorkflowOptions();
          if (this.departments.length === 0) {
            this.getDepartments();
          }
          if (!this.systemDate) {
            this.systemDate = this.formatDisplayDate(new Date());
          }
        }

        if (!this.editMode) {
          this.restoreActiveUploads();
          this.restoreUploadedFiles();
          // Fetch the auto-generated File/Document numbers for this new upload.
          this.loadNextDocumentNumbers();
          if (pendingSelectedFiles.length > 0) {
            this.prepareAndAddFiles(pendingSelectedFiles);
          }

          // Check for incomplete uploads only on first open after reload
          if (state.firstOpenAfterReload) {
            this.checkForIncompleteUploads();
          }
        }
      }
    }
  );

  this.loadFoldersForMove();
  if(this.envConfig.isEventsSecExpanded){
    this.loadEvents();
  }
  this.getTags();
  this.loadUploadCategories();
  if(this.envConfig.isLocationSecExpanded){
      this.loadLocations();
  }
  // Run once on init to allow background auto-resume after refresh/login
  this.checkForIncompleteUploads();
}

private loadUploadCategories(): void {
  const payload = {
    pageIndex: 0,
    pageSize: 1000,
    searchTerm: ''
  };

  this.documentCategoryService.getDocumentCategoryList(payload).subscribe({
    next: (response: any) => {
      const data = response?.data;
      const items = Array.isArray(data?.items)
        ? data.items
        : (Array.isArray(data) ? data : []);

      const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
      this.allUploadCategories = items
        .map((item: any) => {
          const value = Number(item.categoryId ?? item.CategoryId);
          const en = (item.categoryNameEn ?? item.CategoryNameEn ?? '').trim();
          const ar = (item.categoryNameAr ?? item.CategoryNameAr ?? '').trim();
          const label = useArabic ? (ar || en) : (en || ar);
          const deptRaw = Number(item.departmentId ?? item.DepartmentId);
          const departmentId = Number.isFinite(deptRaw) && deptRaw > 0 ? deptRaw : null;
          const entityIdRaw = Number(item.entityId ?? item.EntityId);
          // Only keep a real folder link — never fall back to categoryId (breaks folder matching).
          const entityId =
            Number.isFinite(entityIdRaw) && entityIdRaw > 0 ? entityIdRaw : 0;
          return Number.isFinite(value) && value > 0 && label
            ? { label, value, departmentId, entityId }
            : null;
        })
        .filter(Boolean) as Array<{ label: string; value: number; departmentId: number | null; entityId: number }>;

      if (this.selectedNode) {
        this.autoSelectDeptAndCategoryFromNode(this.selectedNode);
      } else if (Number(this.selectedFolderId) > 0) {
        this.autoSelectCategoryFromFolderId(Number(this.selectedFolderId));
      }
    },
    error: () => {
      this.allUploadCategories = [];
    }
  });
}

/** When the Department changes, keep payload context in sync and refresh dependent fields. */
onUploadDepartmentChange(): void {
  const selected = Number(this.selectedUploadDepartmentId);
  this.libraryDepartmentId =
    Number.isFinite(selected) && selected > 0 ? selected : null;

  if (
    this.selectedCategoryId != null &&
    this.allUploadCategories.length > 0 &&
    !this.allUploadCategories.some((cat) => {
      if (cat.value !== this.selectedCategoryId) {
        return false;
      }
      // Keep categories with no department link, or matching the selected department.
      return (
        cat.departmentId == null ||
        cat.departmentId === this.libraryDepartmentId
      );
    })
  ) {
    this.selectedCategoryId = null;
  }

  if (!this.editMode) {
    this.loadNextDocumentNumbers(this.libraryDepartmentId);
  }

  if (this.enableWorkflow) {
    this.prefillWorkflowFromCategoryOrDepartment();
  }

  if (this.shareWithRecipients) {
    this.loadShareRecipientsByDepartment();
  }
}

onUploadCategoryChange(): void {
  if (this.suppressCategoryFolderSync) {
    return;
  }
  this.autoSelectFolderFromCategory(this.selectedCategoryId);
  if (this.enableWorkflow) {
    this.prefillWorkflowFromCategoryOrDepartment();
  }
}

/** Set Category without triggering folder re-sync (used when folder already drove category). */
private setSelectedCategoryIdFromFolderContext(categoryId: number | null): void {
  this.suppressCategoryFolderSync = true;
  this.selectedCategoryId = categoryId;
  // ngModelChange is sync; clear on next macrotask so user edits still sync.
  setTimeout(() => {
    this.suppressCategoryFolderSync = false;
  }, 0);
}

onEnableWorkflowChange(enabled: boolean): void {
  if (enabled && !this.isWorkflowAllowedForUpload) {
    this.enableWorkflow = false;
    this.workflowId = null;
    this.messageService.add({
      key: 'custom',
      severity: 'warn',
      summary: this.translateService.instant('DocumentPage.Workflow'),
      detail: this.translateService.instant('DocumentPage.WorkflowDocumentsOnlyWarning'),
      life: 5000,
    });
    return;
  }

  this.enableWorkflow = !!enabled;
  if (!this.enableWorkflow) {
    this.workflowId = null;
    return;
  }
  this.prefillWorkflowFromCategoryOrDepartment();
}

/** Document files only (PDF / Word / Excel) — not images or videos. */
get isWorkflowAllowedForUpload(): boolean {
  if (this.editMode) {
    const editName =
      this.editedFileName ||
      this.editedFile?.fileName ||
      this.editedFile?.FileName ||
      '';
    if (editName) {
      return this.isDocumentUploadFileName(String(editName));
    }
    if (this.selectedFileType) {
      return this.selectedFileType === 'document';
    }
  }

  const files = (this.selectedFiles || []).filter(
    (file) => !this.isPlaceholderFile(file)
  );
  if (!files.length) {
    return true;
  }
  return files.every((file) => this.isDocumentUploadFileName(file.name));
}

getEffectiveWorkflowIdForUpload(): number | null {
  if (!this.enableWorkflow || !this.isWorkflowAllowedForUpload) {
    return null;
  }
  const id = Number(this.workflowId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

isDocumentUploadFileName(fileName: string): boolean {
  const extension = (fileName.split('.').pop() || '').toLowerCase();
  return UploadMediaDialogComponent.WORKFLOW_DOCUMENT_EXTENSIONS.includes(extension);
}

private syncWorkflowAvailabilityForSelectedFiles(): void {
  if (!this.enableWorkflow || this.isWorkflowAllowedForUpload) {
    return;
  }
  this.enableWorkflow = false;
  this.workflowId = null;
  this.messageService.add({
    key: 'custom',
    severity: 'warn',
    summary: this.translateService.instant('DocumentPage.Workflow'),
    detail: this.translateService.instant('DocumentPage.WorkflowDocumentsOnlyWarning'),
    life: 5000,
  });
}

onShareWithRecipientsChange(enabled: boolean): void {
  this.shareWithRecipients = !!enabled;
  if (!this.shareWithRecipients) {
    this.selectedShareRecipientIds = [];
    this.shareRecipients = [];
    this.pendingShareRecipients = [];
    this.recipient = '';
    return;
  }
  this.loadShareRecipientsByDepartment();
}

isShareRecipientSelected(item: { id: number } | number | null | undefined): boolean {
  const id = typeof item === 'number' ? item : Number(item?.id);
  return this.selectedShareRecipientIds.some((selected) => selected === id);
}

getShareRecipientLabel(item: any): string {
  if (item == null) {
    return '';
  }
  if (typeof item === 'string' || typeof item === 'number') {
    const recipient = this.shareRecipients.find((entry) => entry.id === Number(item));
    return recipient?.label || String(item);
  }
  return item.label || item.name || '';
}

getRemainingShareRecipientsTooltip(items: any[], visibleCount = 2): string {
  return (items || [])
    .slice(visibleCount)
    .map((item) => this.getShareRecipientLabel(item))
    .filter(Boolean)
    .join(', ');
}

selectAllShareRecipients(event?: Event): void {
  event?.preventDefault();
  event?.stopPropagation();
  this.selectedShareRecipientIds = this.shareRecipients.map((recipient) => recipient.id);
}

clearAllShareRecipients(event?: Event): void {
  event?.preventDefault();
  event?.stopPropagation();
  this.selectedShareRecipientIds = [];
}

onUploadFileTypeChange(): void {
  if (!this.selectedFileType || !this.selectedFiles?.length) {
    return;
  }

  if (!this.areSelectedFilesAllowedForSelectedFileType()) {
    this.messageService.add({
      key: 'custom',
      severity: 'warn',
      summary: 'File Type Mismatch',
      detail: 'Selected files do not match the chosen file type.',
      life: 5000
    });
  }
}

areSelectedFilesAllowedForSelectedFileType(): boolean {
  if (!this.selectedFileType || !this.selectedFiles?.length) {
    return false;
  }

  return this.selectedFiles.every((file) => this.isFileAllowedForSelectedType(file.name));
}

private isFileAllowedForSelectedType(fileName: string): boolean {
  const extension = (fileName.split('.').pop() || '').toLowerCase();
  const allowedByType: Record<'document' | 'image' | 'video', string[]> = {
    document: ['pdf', 'doc', 'docx', 'xls', 'xlsx'],
    image: ['jpeg', 'jpg', 'png'],
    video: ['mp4', 'avi', 'mpeg', 'mpg']
  };

  return allowedByType[this.selectedFileType!].includes(extension);
}

private loadAllowedUploadTypesFromConfiguration(): void {
  this.settingService.getAllowedUploadTypes().subscribe({
    next: (response: any) => {
      this.configuredAllowedUploadTypes = this.normalizeAllowedUploadTypes(response?.data);
      this.cdr.markForCheck();
    },
    error: (error) => {
      console.warn('Unable to load allowed upload types from application configuration.', error);
    }
  });
}

private normalizeAllowedUploadTypes(rawValue: unknown): string[] {
  const inputValues = Array.isArray(rawValue)
    ? rawValue
    : typeof rawValue === 'string'
      ? rawValue.split(/[\s;\r\n\t]+/g)
      : [];

  const normalized = inputValues
    .map(value => String(value || '').trim().toLowerCase())
    .filter(value => !!value)
    .map(value => {
      if (value.includes('/') || value.startsWith('.')) {
        return value;
      }
      return `.${value}`;
    });

  return Array.from(new Set(normalized));
}

loadLocations(): void {
  const payload = {    
    pageSize: 100,
    pageIndex: 0
  };
  this.documentService.getAllLocations(payload).subscribe({
    next: (res: any) => {
      if (res && res.data) {
        const items = res.data.items;
        this.locations = Array.isArray(items)
          ? items
          : (Array.isArray(res.data) ? res.data : []);
      }
    },
    error: (err) => {
      console.error('Error loading locations:', err);
      this.locations = [];
    }
  });
}

filterLocations(event: any): void {
  const query = (event?.query || '').toLowerCase().trim();
  if (!query) {
    this.filteredLocations = [...this.locations];
    return;
  }

  const filtered = this.locations.filter((loc: any) =>
    (loc?.locationNameEn || '').toLowerCase().includes(query)
  );

  const exactMatch = this.locations.find(
    (loc: any) => (loc?.locationNameEn || '').toLowerCase() === query
  );

  if (!exactMatch) {
    filtered.unshift({
      locationId: 0,
      locationNameEn: event?.query ? String(event.query).trim() : query,
      isNew: true
    });
  }

  this.filteredLocations = filtered;
}

  get eventSelectItems(): any[] {
    return this.filteredEvents?.length ? this.filteredEvents : this.events;
  }

  get tagSelectItems(): any[] {
    return this.filteredTags?.length ? this.filteredTags : this.availableTags;
  }

  get locationSelectItems(): any[] {
    return this.filteredLocations?.length ? this.filteredLocations : this.locations;
  }

  onEventSearch(event: { term: string }): void {
    this.filterEvents({ query: event?.term || '' });
  }

  onTagSearch(event: { term: string }): void {
    this.filterTags({ query: event?.term || '' });
  }

  onLocationSearch(event: { term: string }): void {
    this.filterLocations({ query: event?.term || '' });
  }

  onEventModelChange(event: any): void {
    if (event?.isInactive || event?.disabled) {
      this.messageService.add({
        severity: 'error',
        summary: 'Inactive Event',
        detail: `This event is inactive. Please reactivate it from Events Management or choose a different event.`,
        life: 5000
      });
      this.selectedEvent = this._selectedEvent;
      return;
    }

    this.selectedEvent = event;
  }

  onLocationModelChange(event: any): void {
    if (!event) {
      this.selectedLocation = null;
      return;
    }

    this.selectedLocation = event?.isNew
      ? this.resolveLocationInputValue(event?.locationNameEn || '')
      : event;
  }

  onTagsModelChange(selected: any[]): void {
    if (selected?.length > 1) {
      const lastTag = selected[selected.length - 1];
      const lastTagName = (typeof lastTag === 'string' ? lastTag : lastTag.tag).toLowerCase();
      const isDuplicate = selected.slice(0, -1).some((t: any) =>
        (typeof t === 'string' ? t : t.tag).toLowerCase() === lastTagName
      );

      if (isDuplicate) {
        this.tags = selected.slice(0, -1);
      }
    }
  }

  addEventTag = (term: string): any | null => {
    const value = term.trim();
    if (!value) {
      return null;
    }

    const inactiveMatch = this.allEventsIncludingInactive.find(
      (e: any) => e.eventNameEn?.toLowerCase() === value.toLowerCase() && e.isActivated === false
    );
    if (inactiveMatch) {
      this.messageService.add({
        severity: 'error',
        summary: 'Inactive Event',
        detail: `An event named "${value}" already exists but is inactive. Please reactivate it from Events Management or choose a different name.`,
        life: 6000
      });
      return null;
    }

    const eventDateStr = this.getEventDateForApi();
    return {
      eventId: 0,
      eventNameEn: value,
      eventNameAr: value,
      isNew: true,
      ...(eventDateStr ? { eventDate: eventDateStr } : {}),
    };
  };

  addTagFn = (term: string): any => ({ tag: term.trim(), isNew: true, tagId: 0 });

  addLocationTag = (term: string): any | null => {
    const value = term.trim();
    if (!value) {
      return null;
    }
    return this.resolveLocationInputValue(value);
  };

  compareEvents = (a: any, b: any): boolean => {
    if (!a || !b) {
      return a === b;
    }
    if (a.eventId && b.eventId) {
      return a.eventId === b.eventId;
    }
    return (a.eventNameEn || '').toLowerCase() === (b.eventNameEn || '').toLowerCase();
  };

  compareTags = (a: any, b: any): boolean => {
    const tagA = (typeof a === 'string' ? a : a?.tag || '').toLowerCase();
    const tagB = (typeof b === 'string' ? b : b?.tag || '').toLowerCase();
    return tagA === tagB;
  };

  compareLocations = (a: any, b: any): boolean => {
    if (!a || !b) {
      return a === b;
    }
    if (a.locationId && b.locationId) {
      return a.locationId === b.locationId;
    }
    return (a.locationNameEn || '').toLowerCase() === (b.locationNameEn || '').toLowerCase();
  };

  /**
   * Restore active uploads from global queue when dialog opens
   */
  private restoreActiveUploads(): void {
    const activeUploads = this.globalUploadQueue.getActiveUploads();
    if (activeUploads.length > 0) {
      // If there are active uploads, show resumable upload component
      this.useResumableUpload = true;
      this.showResumableUpload = true;
    }
  }

  /**
   * Check for incomplete uploads from backend (e.g., after logout/login)
   */
  private async checkForIncompleteUploads(): Promise<void> {
    try {
      // Use cached result if available to avoid duplicate calls
      const incompleteSessions = await this.resumableUploadService.getIncompleteUploads().toPromise();
      
      if (incompleteSessions && incompleteSessions.length > 0) {
        this.incompleteSessionCount = incompleteSessions.length;
        console.log(`[RESUME] Found ${incompleteSessions.length} incomplete upload(s) from backend`);
        
        // Prepopulate event and tag details from the first incomplete session
        const firstSession = incompleteSessions[0];
        this.resumeMetadataLocked = true;
        this.pendingResumeSession = firstSession;
        this.prepopulateFromIncompleteSession(firstSession);
        
        // Show resumable upload component to display incomplete uploads
        this.useResumableUpload = true;
        this.showResumableUpload = true;
        
        // Create File objects from incomplete sessions to show in drag section
        // We'll create placeholder files that will be replaced when actual files are selected
        for (const session of incompleteSessions) {
          // Check if file already exists in selectedFiles
          const existingFile = this.selectedFiles.find(f => 
            f.name === session.fileName && f.size === session.fileSize
          );
          
          if (!existingFile) {
            // Create a File object from session info to show in drag section
            // This is a placeholder - actual file will be used when selected
            const placeholderFile = new File([], session.fileName, { 
              type: session.contentType || 'application/octet-stream' 
            });
            (placeholderFile as any).__isPlaceholder = true;
            (placeholderFile as any).__resumableSessionId = session.sessionId;
            // Override size for display (File.size is read-only, but we can track it separately)
            Object.defineProperty(placeholderFile, 'size', { 
              value: session.fileSize, 
              writable: false, 
              configurable: true 
            });
            
            this.selectedFiles.push(placeholderFile);
            console.log(`[RESUME] Added placeholder file to selection: ${session.fileName}`);
          }
        }
        
        // Trigger the resumable upload component to check and resume
        // Use setTimeout to ensure the component is initialized
        setTimeout(() => {
          if (this.resumableUploadComponent) {
            // Load incomplete sessions into the upload list
            this.resumableUploadComponent.loadIncompleteSessions(incompleteSessions);
            // Check for auto-resume if files are already selected
            if (this.selectedFiles && this.selectedFiles.length > 0) {
              this.resumableUploadComponent.checkAndAutoResumeFilesPublic(this.selectedFiles);
            }
          }
        }, 500);
        
        // Reduced notification - badge indicator is the primary indicator
        // Only show a brief info message
        if (!this.incompleteToastShown) {
          this.messageService.add({
            key: 'custom',
            severity: 'info',
            summary: 'Incomplete Uploads Found',
            detail: `Found ${incompleteSessions.length} incomplete upload(s). Please re-select the file(s) to resume.`,
            life: 3000
          });
          this.incompleteToastShown = true;
        }
      } else {
        this.resumeMetadataLocked = false;
        this.pendingResumeSession = null;
        this.incompleteSessionCount = 0;
        this.incompleteToastShown = false;
      }
    } catch (error) {
      console.error('[RESUME] Error checking for incomplete uploads:', error);
    }
  }
  
  /**
   * Prepopulate form fields from incomplete session metadata
   */
  private prepopulateFromIncompleteSession(session: any): void {
    this.applyResumeMetadataFromSession(session);
  }

  private applyResumeMetadataFromSession(session: any): void {
    if (!session) {
      return;
    }

    if (session.titleEn && session.titleEn.trim() && (this.resumeMetadataLocked || !this.title)) {
      this.title = session.titleEn;
    }

    const hasSelectedEvent = !!this.selectedEvent && Object.keys(this.selectedEvent || {}).length > 0;
    if (!hasSelectedEvent) {
      if (session.eventId && session.eventId !== null) {
        const matchingEvent =
          this.events.find((event) => event.eventId === session.eventId) ||
          this.allEventsIncludingInactive.find((event) => event.eventId === session.eventId);
        if (matchingEvent) {
          this.selectedEvent = matchingEvent;
        }
      } else if (session.newEvent && session.newEvent.trim()) {
        this.selectedEvent = {
          eventId: 0,
          eventNameEn: session.newEvent,
          eventNameAr: session.newEvent,
          isNew: true,
          eventDate: session.eventDate
        };
      }
    }

    const dateToUse = this.toSafeDate(session.eventDate) ?? this.toSafeDate(session.fileCreationDate);
    if (dateToUse && (this.resumeMetadataLocked || !this.date)) {
      this.date = dateToUse;
      if (!this.eventDateRange?.[0]) {
        this.eventDateRange = [dateToUse, dateToUse];
      }
    }

    if (this.availableTags.length > 0 && session.tagIds && (this.resumeMetadataLocked || this.tags.length === 0)) {
      try {
        let tagIdArray: number[] = [];
        if (typeof session.tagIds === 'string') {
          if (session.tagIds.trim().startsWith('[')) {
            tagIdArray = JSON.parse(session.tagIds);
          } else if (session.tagIds.trim()) {
            tagIdArray = session.tagIds
              .split(',')
              .map((id: string) => parseInt(id.trim()))
              .filter((id: number) => !isNaN(id));
          }
        } else if (Array.isArray(session.tagIds)) {
          tagIdArray = session.tagIds;
        }

        if (tagIdArray.length > 0) {
          const resolvedTags = tagIdArray
            .map((tagId: number) => {
              const matchingTag = this.availableTags.find(
                (tag) => tag.tagId === tagId
              );
              return matchingTag || { tag: tagId.toString(), tagId };
            })
            .filter((tag: any) => tag !== null);
          this.tags = resolvedTags;
          this.tagIds = tagIdArray;
        }
      } catch (e) {
        console.warn('Error parsing tagIds:', e);
      }
    }

    if (session.newTags && (this.resumeMetadataLocked || this.tags.length === 0)) {
      try {
        let newTagsArray: string[] = [];
        if (typeof session.newTags === 'string') {
          if (session.newTags.trim().startsWith('[')) {
            newTagsArray = JSON.parse(session.newTags);
          } else if (session.newTags.trim()) {
            newTagsArray = session.newTags
              .split(',')
              .map((tag: string) => tag.trim())
              .filter((tag: string) => tag.length > 0);
          }
        } else if (Array.isArray(session.newTags)) {
          newTagsArray = session.newTags;
        }

        if (newTagsArray.length > 0) {
          newTagsArray.forEach((newTag: string) => {
            if (
              newTag &&
              !this.tags.some((t: any) =>
                (typeof t === 'string' && t === newTag) ||
                (typeof t === 'object' && t.tag === newTag)
              )
            ) {
              this.tags.push({ tag: newTag, isNew: true });
            }
          });
        }
      } catch (e) {
        console.warn('Error parsing newTags:', e);
      }
    }

    const markSecureValue = session.isMarkSecure ?? session.IsMarkSecure ?? false;
    if (this.resumeMetadataLocked || markSecureValue) {
      this.markAsSecure = !!markSecureValue;
    }

    const shareTypeValue = (session.shareType || session.ShareType) as 'internal' | 'external' | undefined;
    const shareUserIds = this.parseNumberArray(session.userIds ?? session.UserIds ?? session.sharedToUserIds ?? session.SharedToUserIds);
    const shareEmails = this.parseStringArray(session.emails ?? session.Emails ?? session.userEmails ?? session.UserEmails);
    const expireOnValue = session.expireOn ?? session.ExpireOn;

    if (this.resumeMetadataLocked || this.markAsSecure) {
      if (this.markAsSecure) {
        this.loadShareLookups();
      }
      const inferredShareType: 'internal' | 'external' | undefined = shareTypeValue
        ? shareTypeValue
        : (shareEmails.length > 0 ? 'external' : (shareUserIds.length > 0 ? 'internal' : undefined));
      if (inferredShareType) {
        this.shareType = inferredShareType;
      }
      if (this.shareType === 'external') {
        this.selectedExternalUsers = shareUserIds;
        this.selectedUsers = [];
        this.externalEmails = shareEmails;
        this.externalEmailsText = shareEmails.join(', ');
      } else {
        this.selectedUsers = shareUserIds;
        this.selectedExternalUsers = [];
        this.externalEmails = [];
        this.externalEmailsText = '';
      }
      if (expireOnValue) {
        const parsedExpire = new Date(expireOnValue);
        if (!isNaN(parsedExpire.getTime())) {
          this.shareExpireDate = parsedExpire;
        }
      }
    }
  }

  private tryAutoStartResume(): void {
    if (!this.resumeMetadataLocked || !this.useResumableUpload) {
      return;
    }

    const hasRealFiles = this.selectedFiles.some(file => !this.isPlaceholderFile(file));
    if (!hasRealFiles) {
      return;
    }

    setTimeout(() => {
      if (!this.resumableUploadComponent) {
        return;
      }

      this.resumableUploadComponent.checkAndAutoResumeFilesPublic(this.selectedFiles);

      const hasActiveUploads = this.globalUploadQueue.getActiveUploads().length > 0;
      if (!hasActiveUploads) {
        this.resumableUploadComponent.startUploads();
      }
    }, 0);
  }

  clearResumableSessions(): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('MEDIALIBRARY.clearAllUploadsConfirmation'),
      header: this.translateService.instant('MEDIALIBRARY.clearAllUploadsHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.doClearResumableSessions(),
    });
  }

  private doClearResumableSessions(): void {
    const resumableUploads = this.uploadManager
      .getAllUploads()
      .filter(upload => !!upload.sessionId);

    resumableUploads.forEach(upload => {
      if (this.resumableUploadComponent) {
        this.resumableUploadComponent.cancelUpload(upload.fileId);
      } else {
        this.uploadManager.cancelUpload(upload.fileId);
      }
    });

    // Remove all uploads from manager to clear progress bars
    this.uploadManager.clearAll();
    this.resumableUploadService.invalidateIncompleteUploadsCache();
    this.resumableUploadService.updateIncompleteUploadCount(0);

    // Clear file selection and placeholder files
    this.selectedFiles = [];
    this.pendingEmptyFolderRelativePaths.clear();
    this.resumeMetadataLocked = false;
    this.pendingResumeSession = null;
    this.incompleteSessionCount = 0;
    this.incompleteToastShown = false;
    this.completedResumableSessions = [];
    this.useResumableUpload = false;
    this.showResumableUpload = false;

    // Clear metadata and unlock inputs
    this.title = '';
    this.selectedEvent = null;
    this.selectedLocation = null;
    this.date = new Date();
    this.tags = [];
    this.tagIds = [];
    this.markAsSecure = false;
    this.resetShareFields();

    // Re-evaluate upload mode based on remaining real files
    this.checkFileSizeAndToggleUploadMode();
  }

  getNewEventName(): string {
    if (!this.selectedEvent) {
      return '';
    }
    if (typeof this.selectedEvent === 'string') {
      return this.selectedEvent;
    }
    if (this.selectedEvent.isNew) {
      return this.selectedEvent.eventNameEn || '';
    }
    return '';
  }

  /**
   * When a catalog event is selected (real eventId, not "new event" from Enter), event date comes from that event only.
   */
  get isEventDateReadOnly(): boolean {
    const ev = this.selectedEvent;
    if (!ev || typeof ev !== 'object' || Array.isArray(ev)) {
      return false;
    }
    if (ev.isNew === true) {
      return false;
    }
    const id = Number(ev.eventId);
    return Number.isFinite(id) && id > 0;
  }

  getResolvedFolderId(): number {
    if (this.selectedFolderId !== null) {
      const selected = Number(this.selectedFolderId);
      if (Number.isFinite(selected) && selected > 0) {
        return selected;
      }
      return 0;
    }
    return (
      this.newlyCreatedFolderId ||
      this.currentFolderId ||
      0
    );
  }

  private getNormalizedRelativePath(file: File): string {
    const currentRelativePath = ((file as any).__currentRelativePath || '').replace(/\\/g, '/').trim();
    if (currentRelativePath) {
      return currentRelativePath.replace(/^\/+|\/+$/g, '');
    }
    const relativePath = ((file as any).webkitRelativePath || '').replace(/\\/g, '/').trim();
    if (!relativePath) {
      return file.name;
    }
    return relativePath.replace(/^\/+|\/+$/g, '');
  }

  private getFileIdentity(file: File): string {
    return this.getNormalizedRelativePath(file).toLowerCase().trim();
  }

  private getOriginalRelativePath(file: File): string {
    return ((file as any).__originalRelativePath || this.getNormalizedRelativePath(file)).replace(/\\/g, '/').trim();
  }

  private getSourceKey(file: File): string {
    return (file as any).__sourceKey || `${this.getOriginalRelativePath(file)}|${file.size}|${file.lastModified}`;
  }

  private ensureUploadFileMetadata(file: File): File {
    const normalizedPath = this.getNormalizedRelativePath(file);
    const parts = normalizedPath.split('/').filter(Boolean);
    const isFolderFile = parts.length > 1;
    const topLevelToken = (file as any).__topLevelToken || (isFolderFile ? parts[0] : normalizedPath);

    if (!(file as any).__originalRelativePath) {
      (file as any).__originalRelativePath = normalizedPath;
    }
    if (!(file as any).__currentRelativePath) {
      (file as any).__currentRelativePath = normalizedPath;
    }
    if (!(file as any).__originalName) {
      (file as any).__originalName = file.name;
    }
    if (!(file as any).__topLevelToken) {
      (file as any).__topLevelToken = topLevelToken;
    }
    if (!(file as any).__itemType) {
      (file as any).__itemType = isFolderFile ? 'folder' : 'file';
    }
    if (!(file as any).__originalTopLevelName) {
      (file as any).__originalTopLevelName = isFolderFile ? parts[0] : file.name;
    }
    if (!(file as any).__selectionInstanceId) {
      this.selectionSourceKeyCounter += 1;
      (file as any).__selectionInstanceId = this.selectionSourceKeyCounter;
    }
    if (!(file as any).__sourceKey) {
      (file as any).__sourceKey = `${(file as any).__itemType}:${topLevelToken}:${(file as any).__originalRelativePath}:${file.size}:${file.lastModified}:${(file as any).__selectionInstanceId}`;
    }

    const sourceKey = this.getSourceKey(file);
    if (!this.fileStatusMap.has(sourceKey)) {
      this.fileStatusMap.set(sourceKey, 'pending');
    }
    return file;
  }

  private syncUploadMetadataState(): void {
    const visibleFiles = this.visibleSelectedFiles.map(file => this.ensureUploadFileMetadata(file));
    const validSourceKeys = new Set(visibleFiles.map(file => this.getSourceKey(file)));
    Array.from(this.fileStatusMap.keys()).forEach(key => {
      if (!validSourceKeys.has(key)) {
        this.fileStatusMap.delete(key);
      }
    });
  }

  private getCurrentTopLevelFolderName(file: File): string {
    const relativePath = this.getNormalizedRelativePath(file);
    const parts = relativePath.split('/').filter(Boolean);
    return parts[0] || file.name;
  }

  private mapUploadStatusToDisplay(status: string, progress: number, hasSession: boolean): UploadDisplayState {
    if (status === 'completed') return 'uploaded';
    if (status === 'failed' || status === 'cancelled') return 'failed';
    if (status === 'uploading' || status === 'initializing') return 'uploading';
    if (status === 'queued' && progress > 0 && hasSession) return 'uploading';
    return 'pending';
  }

  /** Folder progress from upload manager — keeps top summary in sync with resumable progress card */
  private getResumableFolderStats(folderDisplayName: string): {
    uploadedCount: number;
    totalCount: number;
    totalBytes: number;
    isPaused: boolean;
    isUploading: boolean;
    isComplete: boolean;
  } | null {
    if (!this.useResumableUpload || !this.hasUploadsInManager) {
      return null;
    }

    const normalizedFolder = folderDisplayName.toLowerCase();
    const folderUploads = this.uploadManager.getAllUploads().filter(upload => {
      const relativePath = (
        (upload.file as any)?.__currentRelativePath ||
        (upload.file as any)?.webkitRelativePath ||
        upload.file.name ||
        ''
      ).replace(/\\/g, '/').trim();
      const parts = relativePath.split('/').filter(Boolean);
      return parts.length > 1 && parts[0].toLowerCase() === normalizedFolder;
    });

    if (folderUploads.length === 0) {
      return null;
    }

    const uploadedCount = folderUploads.filter(upload => upload.status === 'completed').length;
    const totalCount = folderUploads.length;
    const totalBytes = folderUploads.reduce((sum, upload) => sum + (upload.totalBytes || 0), 0);
    const isPaused = folderUploads.some(upload => upload.status === 'paused');
    const isUploading = folderUploads.some(upload =>
      upload.status === 'uploading' ||
      upload.status === 'initializing' ||
      (upload.status === 'queued' && upload.progress > 0 && !!upload.sessionId)
    );
    const isComplete = totalCount > 0 && uploadedCount === totalCount;

    return { uploadedCount, totalCount, totalBytes, isPaused, isUploading, isComplete };
  }

  private getFileDisplayState(file: File, uploadStateBySourceKey?: Map<string, UploadDisplayState>): UploadDisplayState {
    const sourceKey = this.getSourceKey(file);
    if (uploadStateBySourceKey?.has(sourceKey)) {
      return uploadStateBySourceKey.get(sourceKey)!;
    }
    return this.fileStatusMap.get(sourceKey) || 'pending';
  }

  private setFilesState(files: File[], state: UploadDisplayState): void {
    files.forEach(file => this.fileStatusMap.set(this.getSourceKey(file), state));
  }

  private buildUploadSuccessMessage(successFileCount: number): string {
    const filePart = 'File uploaded successfully';
    const emptyFolderCount = this.pendingEmptyFolderRelativePaths.size;
    if (emptyFolderCount <= 0) {
      return `${filePart}.`;
    }
    const folderPart = emptyFolderCount === 1
      ? '1 empty folder was created'
      : `${emptyFolderCount} empty folders were created`;
    return `${filePart}. ${folderPart}.`;
  }

  private buildResumableCompletionMessage(uploadedFiles: Array<{ fileName: string }>): string {
    const fileCount = uploadedFiles.length;
    const fileNames = uploadedFiles.map(r => r.fileName).join(', ');
    const emptyFolderCount = this.pendingEmptyFolderRelativePaths.size;
    if (emptyFolderCount <= 0) {
      return `Upload has been completed and ${fileCount} file(s) have been created: ${fileNames}.`;
    }
    const folderPart = emptyFolderCount === 1
      ? '1 empty folder has'
      : `${emptyFolderCount} empty folders have`;
    return `Upload has been completed and ${fileCount} file(s) have been created: ${fileNames}. ${folderPart} also been created.`;
  }

  private getBaseUploadMetadata(folderId?: number) {
    const startIso = this.getEventRangeStartIso();
    const endIso = this.getEventRangeEndIso();
    const resolvedFolderId = folderId ?? this.getResolvedFolderId();
    const effectiveLibraryType = this.getUploadDestinationLibraryType();
    const destinationDepartmentId = this.getUploadDestinationDepartmentId(
      Number(resolvedFolderId || 0)
    );
    return {
      folderId: resolvedFolderId,
      libraryType: effectiveLibraryType,
      departmentId:
        effectiveLibraryType === 2
          ? (destinationDepartmentId && destinationDepartmentId > 0 ? destinationDepartmentId : -1)
          : undefined,
      titleEn: this.title,
      titleAr: '',
      eventId: this.selectedEvent?.eventId,
      eventDate: startIso,
      eventStartDate: startIso,
      eventEndDate: endIso,
      tagIds: this.getTagIdsArray(),
      newTags: this.getNewTagsArray(),
      newEvent: this.getNewEventName(),
      isMarkSecure: this.markAsSecure,
      userEmails: this.getShareEmails().join(','),
      shareType: this.shareType,
      sharePermissionLevel: this.sharePermissionLevel,
      shareUserIds: [
        ...new Set([...this.getShareUserIds(), ...this.getDocumentDetailShareUserIds()]),
      ],
      shareEmails: this.getShareEmails(),
      shareExpireOn: this.getShareExpireOn() ?? undefined,
      userId: this.userId,
      fileCreationDate: this.resolveFileCreationDateIso(),
      recipient: this.getShareRecipientLabelsCsv() || this.recipient || '',
      signingAuthority: this.getSelectedSigningAuthorityLabel(),
      signingAuthorityId: this.signingAuthorityId ?? undefined,
      fileNumber: this.fileNumber || '',
      documentNumber: this.documentNumber || '',
      documentTypeId: this.documentTypeId || '',
      workflowId: this.getEffectiveWorkflowIdForUpload() ?? undefined,
      systemDate: new Date().toISOString(),
    };
  }

  private getUploadIdBySourceKey(sourceKey: string): string | undefined {
    const upload = this.uploadManager
      .getAllUploads()
      .find(item => this.getSourceKey(item.file) === sourceKey && item.status !== 'cancelled');
    return upload?.fileId;
  }

  private ensureNormalUploadTracking(files: File[], folderIds?: number[]): Map<string, string> {
    if (!files.length) {
      return new Map<string, string>();
    }

    const metadata = this.getBaseUploadMetadata();
    const perFileMetadata = folderIds && folderIds.length === files.length
      ? files.map((_, index) => ({
          ...metadata,
          folderId: folderIds[index] ?? metadata.folderId
        }))
      : undefined;

    this.uploadManager.addFiles(files, metadata, perFileMetadata);

    const trackerMap = new Map<string, string>();
    files.forEach(file => {
      const sourceKey = this.getSourceKey(file);
      const uploadId = this.getUploadIdBySourceKey(sourceKey);
      if (uploadId) {
        trackerMap.set(sourceKey, uploadId);
      }
    });

    return trackerMap;
  }

  private setNormalUploadTrackerStatus(
    files: File[],
    status: UploadStatus,
    trackerMap?: Map<string, string>
  ): void {
    files.forEach(file => {
      const sourceKey = this.getSourceKey(file);
      const uploadId = trackerMap?.get(sourceKey) || this.getUploadIdBySourceKey(sourceKey);
      if (uploadId) {
        this.uploadManager.updateStatus(uploadId, status);
      }
    });
  }

  private cloneFileForRename(file: File, newFileName: string, newRelativePath: string): File {
    const renamedFile = new File([file], newFileName, {
      type: file.type,
      lastModified: file.lastModified
    });
    const normalizedRelativePath = newRelativePath.replace(/\\/g, '/').trim();
    const currentRelativePath = this.getNormalizedRelativePath(file);

    if (normalizedRelativePath && normalizedRelativePath !== newFileName) {
      Object.defineProperty(renamedFile, 'webkitRelativePath', {
        value: normalizedRelativePath,
        writable: false,
        configurable: true
      });
    } else if ((file as any).webkitRelativePath && currentRelativePath.includes('/')) {
      Object.defineProperty(renamedFile, 'webkitRelativePath', {
        value: normalizedRelativePath,
        writable: false,
        configurable: true
      });
    }

    (renamedFile as any).__originalRelativePath = (file as any).__originalRelativePath;
    (renamedFile as any).__currentRelativePath = normalizedRelativePath || newFileName;
    (renamedFile as any).__originalName = (file as any).__originalName;
    (renamedFile as any).__topLevelToken = (file as any).__topLevelToken;
    (renamedFile as any).__itemType = (file as any).__itemType;
    (renamedFile as any).__originalTopLevelName = (file as any).__originalTopLevelName;
    (renamedFile as any).__sourceKey = (file as any).__sourceKey;
    return renamedFile;
  }

  private normalizeRenamedFileName(inputName: string, originalName: string): string {
    const sanitized = inputName.trim();
    const originalDotIndex = originalName.lastIndexOf('.');
    const originalExtension = originalDotIndex > 0 ? originalName.slice(originalDotIndex) : '';

    if (!originalExtension) {
      return sanitized;
    }
    if (sanitized.toLowerCase().endsWith(originalExtension.toLowerCase())) {
      return sanitized;
    }
    return `${sanitized}${originalExtension}`;
  }

  onUploadItemNameChange(item: UploadItemDisplay, value: string): void {
    const trimmedValue = (value || '').trim();
    if (!trimmedValue) {
      return;
    }

    if (item.type === 'file') {
      const normalizedName = this.normalizeRenamedFileName(trimmedValue, item.originalName);
      const duplicate = this.uploadItemsForDisplay.some(existing =>
        existing.key !== item.key && existing.displayName.toLowerCase() === normalizedName.toLowerCase()
      );
      if (duplicate) {
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: 'Duplicate Name',
          detail: `An item named '${normalizedName}' already exists in this selection.`,
          life: 5000
        });
        return;
      }

      const fileToRename = item.files[0];
      const sourceKey = this.getSourceKey(fileToRename);
      const index = this.selectedFiles.findIndex(file => this.getSourceKey(file) === sourceKey);
      if (index !== -1) {
        const renamedFile = this.cloneFileForRename(fileToRename, normalizedName, normalizedName);
        this.selectedFiles[index] = renamedFile;
        this.syncUploadMetadataState();
      }
      return;
    }

    const duplicateFolderName = this.uploadItemsForDisplay.some(existing =>
      existing.key !== item.key && existing.displayName.toLowerCase() === trimmedValue.toLowerCase()
    );
    if (duplicateFolderName) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Duplicate Name',
        detail: `An item named '${trimmedValue}' already exists in this selection.`,
        life: 5000
      });
      return;
    }

    const topLevelToken = (item.files[0] as any).__topLevelToken;
    this.selectedFiles = this.selectedFiles.map(file => {
      const enhanced = this.ensureUploadFileMetadata(file);
      if ((enhanced as any).__topLevelToken !== topLevelToken) {
        return enhanced;
      }
      const originalRelativePath = this.getOriginalRelativePath(enhanced);
      const originalParts = originalRelativePath.split('/').filter(Boolean);
      const suffixParts = originalParts.slice(1);
      const updatedRelativePath = [trimmedValue, ...suffixParts].join('/');
      return this.cloneFileForRename(enhanced, enhanced.name, updatedRelativePath);
    });
    this.pendingEmptyFolderRelativePaths = new Set(
      Array.from(this.pendingEmptyFolderRelativePaths).map(path => {
        const parts = path.split('/').filter(Boolean);
        if (parts.length === 0) {
          return path;
        }
        if (parts[0].toLowerCase() !== String(topLevelToken || '').toLowerCase()) {
          return path;
        }
        return [trimmedValue, ...parts.slice(1)].join('/');
      })
    );
    this.syncUploadManagerFilesWithSelection();
    this.syncUploadMetadataState();
  }

  private syncUploadManagerFilesWithSelection(): void {
    const latestFileBySourceKey = new Map<string, File>();
    this.selectedFiles.forEach(file => {
      const enhanced = this.ensureUploadFileMetadata(file);
      latestFileBySourceKey.set(this.getSourceKey(enhanced), enhanced);
    });

    const uploads = this.uploadManager.getAllUploads();
    uploads.forEach(upload => {
      const sourceKey = (upload.file as any)?.__sourceKey;
      if (!sourceKey) {
        return;
      }
      const latestFile = latestFileBySourceKey.get(sourceKey);
      if (latestFile && latestFile !== upload.file) {
        upload.file = latestFile;
        this.uploadManager.updateMetadata(upload.fileId, upload.metadata || {});
      }
    });
  }

  removeUploadItem(item: UploadItemDisplay): void {
    this.confirmationService.confirm({
      message: this.translateService.instant('MEDIALIBRARY.removeUploadItemConfirmation', {
        itemName: item.displayName,
      }),
      header: this.translateService.instant('MEDIALIBRARY.removeUploadItemHeader'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.doRemoveUploadItem(item),
    });
  }

  private doRemoveUploadItem(item: UploadItemDisplay): void {
    const sourceKeySet = new Set(item.files.map(file => this.getSourceKey(file)));
    this.selectedFiles = this.selectedFiles.filter(file => !sourceKeySet.has(this.getSourceKey(file)));
    const topLevelToken = String((item.files?.[0] as any)?.__topLevelToken || '').toLowerCase();
    if (topLevelToken) {
      this.pendingEmptyFolderRelativePaths = new Set(
        Array.from(this.pendingEmptyFolderRelativePaths).filter(path => {
          const token = String(path.split('/').filter(Boolean)[0] || '').toLowerCase();
          return token !== topLevelToken;
        })
      );
    }
    this.perFileFolderIds = [];
    if (this.editingUploadItemKey === item.key) {
      this.editingUploadItemKey = null;
    }
    delete this.renameDrafts[item.key];
    this.updateSharePermissionOptions();
    this.syncUploadMetadataState();
    this.checkFileSizeAndToggleUploadMode();
    this.syncFolderPickerSourceWithUploadPermissions();
  }

  startUploadItemRename(item: UploadItemDisplay): void {
    this.editingUploadItemKey = item.key;
    this.renameDrafts[item.key] = item.displayName;
    this.focusUploadItemRenameInput();
  }

  private focusUploadItemRenameInput(): void {
    setTimeout(() => {
      const input = this.uploadItemRenameInputs?.first?.nativeElement;
      input?.focus();
      input?.setSelectionRange(input.value.length, input.value.length);
    });
  }

  isUploadItemRenaming(item: UploadItemDisplay): boolean {
    return this.editingUploadItemKey === item.key;
  }

  saveUploadItemRename(item: UploadItemDisplay): void {
    const draftValue = (this.renameDrafts[item.key] || '').trim();
    this.editingUploadItemKey = null;
    if (!draftValue || draftValue === item.displayName) {
      delete this.renameDrafts[item.key];
      return;
    }
    this.onUploadItemNameChange(item, draftValue);
    this.duplicateItemKeys.delete(item.key);
    delete this.renameDrafts[item.key];
  }

  isDuplicateItem(item: UploadItemDisplay): boolean {
    return this.duplicateItemKeys.has(item.key);
  }

  trackByUploadItem(_: number, item: UploadItemDisplay): string {
    return item.key;
  }

  private hasFolderStructure(files: File[]): boolean {
    return files.some(file => this.getNormalizedRelativePath(file).includes('/'));
  }

  /**
   * When the user picks the same folder again (same original top-level token, e.g. webkit "hfg/..."),
   * replace the prior in-memory selection for that folder instead of stacking files (which caused
   * doubled item counts and extra "(1)" suffix passes).
   */
  private collectIncomingFolderTopLevelTokens(files: File[]): Set<string> {
    const tokens = new Set<string>();
    for (const raw of files) {
      const file = this.ensureUploadFileMetadata(raw);
      const path = this.getNormalizedRelativePath(file);
      if (!path.includes('/')) {
        continue;
      }
      const token = String((file as any).__topLevelToken || path.split('/').filter(Boolean)[0] || '').toLowerCase();
      if (token) {
        tokens.add(token);
      }
    }
    return tokens;
  }

  /**
   * Original leaf file names for incoming non-folder picks. Matches `__originalName` on files that
   * were already auto-renamed (e.g. image.jpg → image(1).jpg) so re-choosing the same file replaces
   * the old entry instead of stacking a second rename pass (image(1)(1)).
   */
  private collectIncomingFlatFileOriginalNames(files: File[]): Set<string> {
    const names = new Set<string>();
    for (const raw of files) {
      const file = this.ensureUploadFileMetadata(raw);
      if (this.getNormalizedRelativePath(file).includes('/')) {
        continue;
      }
      const orig = String((file as any).__originalName || file.name).toLowerCase();
      if (orig) {
        names.add(orig);
      }
    }
    return names;
  }

  private getTopLevelUploadItemName(file: File): string {
    const path = this.getNormalizedRelativePath(file);
    const parts = path.split('/').filter(Boolean);
    return parts.length > 1 ? parts[0] : file.name;
  }

  private getFileExtension(fileName: string): string {
    const idx = fileName.lastIndexOf('.');
    if (idx < 0) return '';
    return fileName.slice(idx).toLowerCase();
  }

  private getFileNameParts(fileName: string): { baseName: string; extensionWithDot: string } {
    const safeName = (fileName || '').trim();
    const idx = safeName.lastIndexOf('.');
    if (idx <= 0) {
      return { baseName: safeName, extensionWithDot: '' };
    }
    return {
      baseName: safeName.substring(0, idx),
      extensionWithDot: safeName.substring(idx),
    };
  }

  /** Keep the original extension when renaming from Edit File Metadata. */
  private finalizeEditFileName(inputName: string): string {
    const trimmed = (inputName || '').replace(/\s+/g, ' ').trim();
    if (!trimmed) {
      return '';
    }

    const originalName = this.getOriginalEditedFileName();
    const { extensionWithDot } = this.getFileNameParts(originalName);
    let sanitizedBase = trimmed;

    if (extensionWithDot) {
      const extensionRegex = extensionWithDot.replace('.', '\\.');
      const trailingExtPattern = new RegExp(`(?:${extensionRegex})+$`, 'i');
      sanitizedBase = sanitizedBase.replace(trailingExtPattern, '').trim();
    }

    if (!sanitizedBase) {
      return '';
    }

    return `${sanitizedBase}${extensionWithDot}`;
  }

  private isAllowedFileType(file: File): boolean {
    const allowedTypes = this.ALLOWED_UPLOAD_TYPES.map(type => type.toLowerCase().trim());
    const mimeType = (file.type || '').toLowerCase();
    const extension = this.getFileExtension(file.name);

    return allowedTypes.some(type => {
      if (type.startsWith('.')) {
        return extension === type;
      }
      if (type.endsWith('/*')) {
        const prefix = type.replace('/*', '/');
        return mimeType.startsWith(prefix);
      }
      if (type.includes('/')) {
        return mimeType === type;
      }
      return false;
    });
  }

  private splitFilesByAllowedTypes(files: File[]): { allowedFiles: File[]; rejectedFiles: File[] } {
    const allowedFiles: File[] = [];
    const rejectedFiles: File[] = [];
    files.forEach(file => {
      if (this.isAllowedFileType(file)) {
        allowedFiles.push(file);
      } else {
        rejectedFiles.push(file);
      }
    });
    return { allowedFiles, rejectedFiles };
  }

  private getNextAvailableFileName(baseFileName: string, usedNames: Set<string>): string {
    const dotIndex = baseFileName.lastIndexOf('.');
    const hasExtension = dotIndex > 0;
    const baseName = hasExtension ? baseFileName.slice(0, dotIndex) : baseFileName;
    const extension = hasExtension ? baseFileName.slice(dotIndex) : '';

    let counter = 1;
    let nextName = `${baseName} (${counter})${extension}`;
    while (usedNames.has(nextName.toLowerCase())) {
      counter += 1;
      nextName = `${baseName} (${counter})${extension}`;
    }
    return nextName;
  }

  private applyLocalSelectionAutoSuffix(newFiles: File[]): { files: File[]; renamedPairs: string[] } {
    const usedDisplayNames = new Set(this.uploadItemsForDisplay.map(item => item.displayName.toLowerCase()));
    const renamedPairs: string[] = [];
    const files = newFiles.map(raw => {
      const file = this.ensureUploadFileMetadata(raw);
      const isFolderItem = this.getNormalizedRelativePath(file).includes('/');
      if (isFolderItem) {
        return file;
      }

      if (!usedDisplayNames.has(file.name.toLowerCase())) {
        usedDisplayNames.add(file.name.toLowerCase());
        return file;
      }

      const nextName = this.getNextAvailableFileName(file.name, usedDisplayNames);
      usedDisplayNames.add(nextName.toLowerCase());
      renamedPairs.push(`${file.name} -> ${nextName}`);
      return this.cloneFileForRename(file, nextName, nextName);
    });

    return { files, renamedPairs };
  }

  private collectDuplicatePayload(files: File[]): { fileNames: string[]; folderNames: string[] } {
    const fileNames = new Set<string>();
    const folderNames = new Set<string>();
    files.forEach(file => {
      const topLevelName = this.getTopLevelUploadItemName(file);
      if (this.getNormalizedRelativePath(file).includes('/')) {
        folderNames.add(topLevelName);
      } else {
        fileNames.add(topLevelName);
      }
    });
    return {
      fileNames: Array.from(fileNames),
      folderNames: Array.from(folderNames)
    };
  }

  private getSelectedFilesForDuplicateCheck(): File[] {
    return this.visibleSelectedFiles.map(file => this.ensureUploadFileMetadata(file));
  }

  private normalizeDuplicateNames(response: any): { duplicateFiles: string[]; duplicateFolders: string[]; existingFiles: string[]; existingFolders: string[] } {
    const data = response?.data || response;
    const duplicateFilesRaw: string[] =
      data?.duplicateFiles || data?.DuplicateFiles || data?.duplicateFileNames || data?.DuplicateFileNames || [];
    const duplicateFoldersRaw: string[] =
      data?.duplicateFolders || data?.DuplicateFolders || data?.duplicateFolderNames || data?.DuplicateFolderNames || [];
    const existingFilesRaw: string[] =
      data?.existingFiles || data?.ExistingFiles || data?.existingFileNames || data?.ExistingFileNames || [];
    const existingFoldersRaw: string[] =
      data?.existingFolders || data?.ExistingFolders || data?.existingFolderNames || data?.ExistingFolderNames || [];
    const normalize = (names: string[]) =>
      (names || [])
        .map(name => (name || '').toString().trim())
        .filter(name => !!name);

    return {
      duplicateFiles: normalize(duplicateFilesRaw),
      duplicateFolders: normalize(duplicateFoldersRaw),
      existingFiles: normalize(existingFilesRaw),
      existingFolders: normalize(existingFoldersRaw)
    };
  }

  private resolveDuplicateItems(newFiles: File[], duplicates: { duplicateFiles: string[]; duplicateFolders: string[] }): void {
    const duplicateFileLower = new Set(duplicates.duplicateFiles.map(name => name.toLowerCase()));
    const duplicateFolderLower = new Set(duplicates.duplicateFolders.map(name => name.toLowerCase()));
    const impactedKeys = new Set<string>();

    newFiles.forEach(file => {
      const topLevelName = this.getTopLevelUploadItemName(file);
      const enhanced = this.ensureUploadFileMetadata(file);
      const isFolder = this.getNormalizedRelativePath(enhanced).includes('/');
      const isDuplicate = isFolder
        ? duplicateFolderLower.has(topLevelName.toLowerCase())
        : duplicateFileLower.has(topLevelName.toLowerCase());
      if (isDuplicate) {
        const token = (enhanced as any).__topLevelToken;
        impactedKeys.add(`${isFolder ? 'folder' : 'file'}:${token}`);
      }
    });

    if (impactedKeys.size === 0) {
      return;
    }

    impactedKeys.forEach(key => this.duplicateItemKeys.add(key));
    const duplicateItems = this.uploadItemsForDisplay.filter(item => impactedKeys.has(item.key));
    if (duplicateItems.length === 0) {
      return;
    }

    const firstItem = duplicateItems[0];
    const namesQuoted = duplicateItems.map(item => `"${item.displayName}"`).join(', ');
    const allFiles = duplicateItems.every(i => i.type === 'file');
    const allFolders = duplicateItems.every(i => i.type === 'folder');
    const pluralKind = allFiles ? 'files' : allFolders ? 'folders' : 'items';
    const singleKind = firstItem.type === 'folder' ? 'folder' : 'file';
    const duplicateCountSuffix = ` (${duplicateItems.length} duplicate items found)`;
    const duplicateMessage =
      duplicateItems.length === 1
        ? `Do you want to rename the ${singleKind} ${namesQuoted}?${duplicateCountSuffix}`
        : `Do you want to rename these duplicate ${pluralKind}: ${namesQuoted}?${duplicateCountSuffix}`;

    this.confirmationService.confirm({
      message: duplicateMessage,
      header: 'Duplicate Name',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Rename',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        // Rename ALL impacted items (not just the first one).
        this.autoRenameDuplicateItems(duplicateItems);

        // If only one duplicate item, keep rename UX consistent with older behavior.
        if (duplicateItems.length === 1) {
          const refreshedItem = this.uploadItemsForDisplay.find(item => item.key === duplicateItems[0].key);
          if (refreshedItem) {
            this.startUploadItemRename(refreshedItem);
            this.renameDrafts[refreshedItem.key] = refreshedItem.displayName;
          }
        }
      },
      reject: () => {
        // User chose not to auto-rename duplicates.
        duplicateItems.forEach(item => this.duplicateItemKeys.delete(item.key));
      }
    });
  }

  private getNextAvailableDuplicateName(item: UploadItemDisplay, usedNames?: Set<string>): string {
    const occupied = usedNames || new Set(this.uploadItemsForDisplay.map(existing => existing.displayName.toLowerCase()));
    const remoteOccupied = item.type === 'folder' ? this.existingDuplicateFolderNames : this.existingDuplicateFileNames;
    remoteOccupied.forEach(name => occupied.add(name));
    const sourceName = item.displayName;
    const dotIndex = sourceName.lastIndexOf('.');
    const hasExtension = item.type === 'file' && dotIndex > 0;
    const baseName = hasExtension ? sourceName.slice(0, dotIndex) : sourceName;
    const extension = hasExtension ? sourceName.slice(dotIndex) : '';

    let counter = 1;
    let nextName = `${baseName} (${counter})${extension}`;
    while (occupied.has(nextName.toLowerCase())) {
      counter += 1;
      nextName = `${baseName} (${counter})${extension}`;
    }
    return nextName;
  }

  private autoRenameDuplicateItems(items: UploadItemDisplay[]): void {
    const usedNames = new Set(this.uploadItemsForDisplay.map(item => item.displayName.toLowerCase()));
    items.forEach(item => {
      const nextName = this.getNextAvailableDuplicateName(item, usedNames);
      this.onUploadItemNameChange(item, nextName);
      usedNames.add(nextName.toLowerCase());
      this.duplicateItemKeys.delete(item.key);
    });
  }

  private recheckDuplicatesForCurrentSelection(): void {
    const files = this.getSelectedFilesForDuplicateCheck();
    if (files.length === 0) {
      this.duplicateItemKeys.clear();
      this.existingDuplicateFileNames.clear();
      this.existingDuplicateFolderNames.clear();
      return;
    }
    this.executeDuplicateCheck(files, false);
  }

  private executeDuplicateCheck(files: File[], showSkipMessage: boolean): void {
    const folderId = this.getDuplicateCheckFolderId();
    const payload = { folderId, ...this.collectDuplicatePayload(files) };

    this.fileService.checkDuplicateFileNames(payload).pipe(
      timeout(8000),
      catchError(() => {
        if (showSkipMessage) {
          this.messageService.add({
            key: 'custom',
            severity: 'warn',
            summary: 'Duplicate Check Skipped',
            detail: 'Duplicate name check did not respond. Continuing without duplicate validation.',
            life: 5000
          });
        }
        return of({
          __duplicateCheckFailed: true,
          data: { duplicateFiles: [], duplicateFolders: [], existingFiles: [], existingFolders: [] }
        });
      })
    ).subscribe({
      next: (response) => {
        const duplicates = this.normalizeDuplicateNames(response);
        this.duplicateItemKeys.clear();
        this.existingDuplicateFileNames = new Set(duplicates.existingFiles.map(name => name.toLowerCase()));
        this.existingDuplicateFolderNames = new Set(duplicates.existingFolders.map(name => name.toLowerCase()));
        if (duplicates.duplicateFiles.length > 0 || duplicates.duplicateFolders.length > 0) {
          this.resolveDuplicateItems(files, duplicates);
        }
      },
      error: () => {
        if (showSkipMessage) {
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: 'Error',
            detail: 'Could not check duplicate file names. Please try again.',
            life: 5000
          });
        }
      }
    });
  }

  private hasFolderStructureSelection(): boolean {
    return this.hasFolderStructure(this.selectedFiles as File[]);
  }

  /**
   * Restore uploaded files from upload manager to show in file picker area
   */
  private restoreUploadedFiles(): void {
    const activeUploads = this.uploadManager
      .getAllUploads()
      .filter(upload => this.isUploadPending(upload.status));

    if (activeUploads.length > 0) {
      // Restore files from upload manager
      const filesToRestore: File[] = [];
      activeUploads.forEach(upload => {
        if (upload.file) {
          filesToRestore.push(upload.file);
        }
      });
      
      if (filesToRestore.length > 0) {
        // Merge with existing selectedFiles, avoiding duplicates
        filesToRestore.forEach(file => {
          this.ensureUploadFileMetadata(file);
          const isDuplicate = this.selectedFiles.some(existing => 
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
          );
          if (!isDuplicate) {
            this.selectedFiles.push(file);
          }
        });
        
        // Show resumable upload component if there are uploads
        this.useResumableUpload = true;
        this.showResumableUpload = true;
        
        // Update completed sessions from upload manager
        this.updateCompletedSessionsFromManager();
        this.syncUploadMetadataState();
      }
    } else {
      // No pending uploads on reopen: keep upload popup clean.
      this.selectedFiles = [];
      this.pendingEmptyFolderRelativePaths.clear();
      this.fileStatusMap.clear();
      this.perFileFolderIds = [];
      this.folderStructureCreatedFolderIds.clear();
      this.folderStructureRootFolderIds.clear();
      this.folderStructureCreatedFolderIdsForUpload = [];
      this.completedResumableSessions = [];
      this.uploadedFiles = [];
      this.useResumableUpload = false;
      this.showResumableUpload = false;
    }
  }

  private clearUploadStateIfAllCompleted(): void {
    const allUploads = this.uploadManager.getAllUploads();
    if (allUploads.length === 0) {
      return;
    }

    const hasPending = allUploads.some(upload => this.isUploadPending(upload.status));
    if (hasPending) {
      return;
    }

    // Clear completed/failed remnants so reopening starts with a clean upload dialog.
    this.uploadManager.clearCompleted();
    this.globalUploadQueue.clearCompleted();
    this.selectedFiles = [];
    this.pendingEmptyFolderRelativePaths.clear();
    this.fileStatusMap.clear();
    this.perFileFolderIds = [];
    this.folderStructureCreatedFolderIds.clear();
    this.folderStructureRootFolderIds.clear();
    this.folderStructureCreatedFolderIdsForUpload = [];
    this.completedResumableSessions = [];
    this.uploadedFiles = [];
    this.useResumableUpload = false;
    this.showResumableUpload = false;
    this.allUploadsCompleted = false;
  }

  private isUploadPending(status: string): boolean {
    return status === 'queued' || status === 'initializing' || status === 'uploading' || status === 'paused';
  }

  /**
   * Update completed sessions from upload manager
   */
  private updateCompletedSessionsFromManager(): void {
    const allUploads = this.uploadManager.getAllUploads();
    this.completedResumableSessions = allUploads
      .filter(upload => upload.status === 'completed' && upload.filePath)
      .map(upload => ({
        filePath: upload.filePath!,
        fileName: upload.file.name,
        fileSize: upload.totalBytes
      }));
  }

  /**
   * Get active uploads count for display
   */
  get activeUploadsCount(): number {
    return this.globalUploadQueue.getUploadCount();
  }

  /**
   * Get active uploads for display in dialog
   */
  get activeUploads(): any[] {
    return this.globalUploadQueue.getActiveUploads();
  }

  /**
   * Check if there are any uploads in the manager (including completed)
   */
  get hasUploadsInManager(): boolean {
    return this.uploadManager.getAllUploads().length > 0;
  }

  get isUploadInProgress(): boolean {
    return this.globalUploadQueue.getActiveUploads().length > 0 || this.isUploadActionInProgress;
  }

  ngOnDestroy(): void {
    if (this.uploadAutoCloseTimer) {
      clearTimeout(this.uploadAutoCloseTimer);
      this.uploadAutoCloseTimer = null;
    }
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.uploadProgressSubscription) {
      this.uploadProgressSubscription.unsubscribe();
    }
  }

  loadEvents(): void {
    this.sidebarService.events$.subscribe((events) => {
      const allEvents = Array.isArray(events) ? events : [];
      // Full list for inactive-name validation
      this.allEventsIncludingInactive = allEvents;
      // Active-only for dropdown suggestions
      this.events = allEvents.filter((e: any) => e.isActivated !== false);

      if (this.pendingResumeSession) {
        this.applyResumeMetadataFromSession(this.pendingResumeSession);
      }
      if (this.pendingEventDateFromSidebarEvents && this.editedFile?.eventId && this.events.length > 0) {
        const match = allEvents.find((e: any) => e.eventId === this.editedFile.eventId);
        if (match?.eventDate) {
          const d = new Date(match.eventDate);
          if (!isNaN(d.getTime())) {
            this.date = d;
          }
        }
        this.pendingEventDateFromSidebarEvents = false;
      }
    });
  }

  /** Same local-date → API string logic as regular upload (preserves calendar day). */
  private getEventDateForApi(d: Date | null | undefined = this.date): string {
    if (!d || isNaN(d.getTime())) {
      return '';
    }
    const offset = d.getTimezoneOffset() * 60000;
    const localDate = new Date(d.getTime() - offset);
    return localDate.toISOString().slice(0, 19);
  }

  private applyEditFormEventDate(matchingEvent: any | null | undefined): void {
    if (!this.editedFile) {
      return;
    }
    const file = this.editedFile;
    const direct = file.eventDate ?? file.EventDate;
    if (direct) {
      const d = new Date(direct);
      if (!isNaN(d.getTime())) {
        this.date = d;
      }
      this.pendingEventDateFromSidebarEvents = false;
      return;
    }
    if (matchingEvent?.eventDate) {
      const d = new Date(matchingEvent.eventDate);
      if (!isNaN(d.getTime())) {
        this.date = d;
      }
      this.pendingEventDateFromSidebarEvents = false;
      return;
    }
    this.pendingEventDateFromSidebarEvents = !!(file.eventId && file.eventId !== 0);
  }

  getTags(): void {
    this.sidebarService.tags$.subscribe((tags) => {
      this.availableTags = tags.map((tag: any) => ({
        tag: tag.tag,
        tagId: tag.tagId,
      }));
      // Edit-file mode race: file metadata can arrive before tag master list.
      // Hydrate once; do not reset after the user removes or adds tags.
      if (this.editedFile && !this.fileTagsHydrated) {
        this.applyEditedFileTagsToForm();
        this.fileTagsHydrated = true;
      }
      if (this.pendingResumeSession) {
        this.applyResumeMetadataFromSession(this.pendingResumeSession);
      }
    });
  }

  /** Looks up this file's AI job (if any) so the block shows live status. */
  private resolveAiJob(fileId: number): void {
    this.aiJobId = null;
    if (!fileId) return;
    this.aiService.isEnabled().subscribe({
      next: (on) => {
        this.aiEnabled = on;
        if (!on) return;
        this.aiService.jobsFor([fileId]).subscribe({
          next: (jobs) => {
            this.aiJobId = jobs.get(fileId)?.documentProcessingId ?? null;
            this.aiJobStage = jobs.get(fileId)?.stage ?? null;
          },
          error: () => (this.aiJobId = null),
        });
      },
      error: () => (this.aiEnabled = false),
    });
  }

  /** "Send for AI processing" on the edit dialog. */
  sendForAi(): void {
    const fileId = Number(this.editedFile?.fileId || this.fileId || 0);
    if (!fileId || this.aiSending) return;
    this.aiSending = true;
    this.aiService.processFile(fileId).subscribe({
      next: (processingId) => {
        this.aiSending = false;
        if (processingId > 0) {
          this.aiJobId = processingId;
          this.aiJobStage = 'queued';
        }
      },
      error: () => (this.aiSending = false),
    });
  }

  /** Open the shared AI results + chat screen for the current edit file. */
  openAiResults(): void {
    if (!this.aiJobId) return;
    this.uploadDialogService.closeDialog();
    this.router.navigate(['/main/document/processed-document', this.aiJobId], {
      queryParams: {
        from: this.libraryType === 2 || this.envConfig.showMyfiles === false ? 'dept-assets' : 'files',
        folderId: this.currentFolderId > 0 ? this.currentFolderId : null,
      },
    });
  }

  getFileById(fileId: number): void {
    this.fileTagsHydrated = false;
    this.resolveAiJob(fileId);
    this.fileService.getFileById(fileId).subscribe((response) => {
      if (response.status) {
        this.editedFile = response.data;
        this.editedFileName =
          this.editedFile?.fileName ||
          this.editedFile?.FileName ||
          this.editedFile?.displayName ||
          this.editedFile?.titleEn ||
          '';
        this.applyFileMetaDetails(this.editedFile);

        // Set title
        this.title = this.editedFile.titleEn || '';

        // Bind other document metadata fields
        this.recipient = this.editedFile.recipient || this.editedFile.Receipient || '';
        this.signingAuthority =
          this.editedFile.signingAuthority || this.editedFile.SigningAuthority || '';
        this.signingAuthorityId = this.resolveSigningAuthorityId(this.editedFile);
        this.fileNumber = this.editedFile.fileNumber || this.editedFile.FileNumber || '';
        this.documentNumber = this.editedFile.documentNumber || this.editedFile.DocumentNumber || '';
        this.documentTypeId = this.editedFile.documentTypeId ? String(this.editedFile.documentTypeId) : '';
        this.selectedUploadDepartmentId = this.editedFile.departmentId ? Number(this.editedFile.departmentId) : null;
        this.selectedCategoryId = this.editedFile.categoryId ? Number(this.editedFile.categoryId) : null;
        this.documentDate = this.toSafeDate(this.editedFile.documentDate ?? this.editedFile.DocumentDate);
        const systemDateRaw =
          this.editedFile.systemDate ||
          this.editedFile.SystemDate ||
          this.editedFile.system_CreatedOn ||
          this.editedFile.systemCreatedOn;
        const systemDateParsed = this.toSafeDate(systemDateRaw);
        this.systemDate = systemDateParsed
          ? this.formatDisplayDate(systemDateParsed)
          : this.formatDisplayDate(new Date());

        const savedWorkflowId = Number(
          this.editedFile.workflowId ?? this.editedFile.WorkflowId
        );
        this.workflowId =
          Number.isFinite(savedWorkflowId) && savedWorkflowId > 0 ? savedWorkflowId : null;
        this.enableWorkflow = !!this.workflowId;
        const loadedReturnComment = String(
          this.editedFile.returnComment ?? this.editedFile.ReturnComment ?? ''
        ).trim();
        if (loadedReturnComment) {
          this.returnComment = loadedReturnComment;
        }

        const shareIds = this.extractShareUserIdsFromFile(this.editedFile);
        this.shareWithRecipients = shareIds.length > 0;
        this.selectedShareRecipientIds = shareIds;
        this.pendingShareRecipients = shareIds.map((id) => ({
          id,
          label: `#${id}`,
        }));
        if (this.shareWithRecipients) {
          this.loadShareRecipientsByDepartment();
        }
        const fileTypeRaw = this.editedFile.fileType || this.editedFile.FileType || '';
        this.selectedFileType = (fileTypeRaw === 'document' || fileTypeRaw === 'image' || fileTypeRaw === 'video') ? fileTypeRaw : 'document';

        // Find and set the matching event — check active first, then all (including inactive)
        let matchingEvent: any = null;
        if (this.editedFile.eventId) {
          matchingEvent =
            this.events.find((event) => event.eventId === this.editedFile.eventId) ||
            this.allEventsIncludingInactive.find((event) => event.eventId === this.editedFile.eventId);
          if (matchingEvent) {
            this.selectedEvent = matchingEvent;
          } else {
            console.warn(
              'No matching event found for eventId:',
              this.editedFile.eventId
            );
          }
        }
if(this.envConfig.isLocationSecExpanded){
        // Find and set matching location
        if (this.editedFile.locationId && this.locations.length > 0) {
          const matchingLocation = this.locations.find(
            (location) => location.locationId === this.editedFile.locationId
          );
          if (matchingLocation) {
            this.selectedLocation = matchingLocation;
          }
        } else {
          this.selectedLocation = null;
        }
      }
        this.applyEditedFileTagsToForm();
        if (this.availableTags.length > 0) {
          this.fileTagsHydrated = true;
        }
if(this.envConfig.isEventsSecExpanded){
        const evObj =
          this.selectedEvent && typeof this.selectedEvent === 'object'
            ? (this.selectedEvent as { eventDate?: string })
            : null;
        const startSrc =
          (this.editedFile as any)?.eventStartDate ||
          evObj?.eventDate ||
          this.editedFile?.eventDate;
        const endSrc =
          (this.editedFile as any)?.eventEndDate ||
          (this.editedFile as any)?.EventEndDate ||
          startSrc;
        const s = this.toSafeDate(startSrc);
        const e = this.toSafeDate(endSrc) ?? s;
        if (s) {
          this.eventDateRange = e ? [s, e] : [s, s];
        }
      }
        // File creation date remains independent from event duration.
        this.date = this.resolveFileCreationDateFromEditedFile(this.editedFile) ?? new Date();

        this.captureInitialEditState();

        // Open the dialog
        this.visible = true;
      } else {
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: '',
          detail: response?.errorMessage,
        });
      }
    });
  }

  private applyEditedFileTagsToForm(): void {
    const fileTags = (this.editedFile as any)?.tags ?? (this.editedFile as any)?.Tags ?? [];
    if (!Array.isArray(fileTags) || fileTags.length === 0) {
      this.tags = [];
      this.tagIds = [];
      return;
    }

    // Preserve ids even when master tag list is not loaded yet.
    this.tagIds = fileTags
      .map((fileTag: any) => Number(fileTag?.tagId ?? fileTag?.TagId ?? 0))
      .filter((id: number) => Number.isFinite(id) && id > 0);

    this.tags = fileTags.map((fileTag: any) => {
      const fileTagId = Number(fileTag?.tagId ?? fileTag?.TagId ?? 0);
      const fileTagName = String(fileTag?.tag ?? fileTag?.Tag ?? '').trim();

      const matchingTag = this.availableTags.find(
        (availableTag) =>
          (fileTagId > 0 && Number(availableTag?.tagId ?? 0) === fileTagId) ||
          (!!fileTagName && String(availableTag?.tag ?? '').trim().toLowerCase() === fileTagName.toLowerCase())
      );

      if (matchingTag) {
        return matchingTag;
      }

      // Fallback to a compatible object so chips still render.
      return {
        tag: fileTagName || String(fileTag || ''),
        tagId: fileTagId > 0 ? fileTagId : undefined
      };
    });
  }

  filterTags(event: any): void {
    const query = (event?.query || '').toLowerCase().trim();
    const currentTags = this.tags || [];

    if (!query || query === '') {
      // Show all available tags that aren't already selected
      this.filteredTags = this.availableTags.filter(tag => 
        !currentTags.some((selected: any) => 
          (typeof selected === 'string' ? selected : selected.tag) === tag.tag
        )
      );
      return;
    }

    const filtered = this.availableTags.filter((tag) =>
      tag.tag.toLowerCase().includes(query) &&
      !currentTags.some((selected: any) => 
        (typeof selected === 'string' ? selected : selected.tag) === tag.tag
      )
    );

    const exactMatch = this.availableTags.find(
      (tag) => tag.tag.toLowerCase() === query
    );

    // if (!exactMatch && query.length > 0) {
    //   filtered.unshift({
    //     tagId: 0,
    //     tag: event.query ? event.query.trim() : query,
    //     isNew: true
    //   });
    // }

    this.filteredTags = filtered;
  }

  onTagEnter(event: any, autoComplete?: any): void {
    const value = event.target.value.trim();
    if (!value) return;

    // Check if tag already exists in selected tags
    const alreadySelected = this.tags.some((t: any) => 
      (typeof t === 'string' ? t : t.tag).toLowerCase() === value.toLowerCase()
    );

    if (alreadySelected) {
      event.target.value = '';
      if (autoComplete) {
        autoComplete.hide();
      }
      return;
    }

    // Check if tag exists in available tags
    const existingTag = this.availableTags.find((t: any) => 
      t.tag.toLowerCase() === value.toLowerCase()
    );

    if (existingTag) {
      this.tags.push(existingTag);
    } else {
      // Add as new tag object so p-autoComplete field="tag" works correctly
      this.tags.push({ tag: value, isNew: true });
    }

    // Clear input
    event.target.value = '';
    
    // Close dropdown
    if (autoComplete) {
      autoComplete.hide();
    }
  }

  onTagSelect(event: any): void {
    // Check for duplicates in the tags array
    // The newly selected tag is already in this.tags at the end
    if (this.tags.length > 1) {
      const lastTag = this.tags[this.tags.length - 1];
      const lastTagName = (typeof lastTag === 'string' ? lastTag : lastTag.tag).toLowerCase();
      
      // Check if this tag exists earlier in the array
      // We look at everything EXCEPT the last element (which is the one just added)
      const isDuplicate = this.tags.slice(0, -1).some((t: any) => 
        (typeof t === 'string' ? t : t.tag).toLowerCase() === lastTagName
      );

      if (isDuplicate) {
        // Remove the last added tag
        this.tags.pop();
      }
    }
  }

  filterEvents(event: any): void {
    const query = (event?.query || '').toLowerCase().trim();

    if (!query || query === '') {
      this.filteredEvents = [...this.events];
      return;
    }

    // Only show active events in dropdown suggestions
    const filtered = this.events.filter((e) =>
      e.eventNameEn?.toLowerCase().includes(query)
    );

    // If the query matches an inactive event, append a disabled hint entry
    const inactiveMatch = this.allEventsIncludingInactive.find(
      (e: any) => e.eventNameEn?.toLowerCase().includes(query)
        && e.isActivated === false
    );
    if (inactiveMatch && !filtered.some((e) => e.eventId === inactiveMatch.eventId)) {
      filtered.push({
        ...inactiveMatch,
        eventNameEn: `${inactiveMatch.eventNameEn} (Inactive)`,
        isInactive: true,
        disabled: true
      });
    }

    this.filteredEvents = filtered;
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragDepth += 1;
    this.isDragging = true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  toggleBrowseMenu(): void {
    this.browseMenuOpen = !this.browseMenuOpen;
  }

  browseFiles(input: HTMLInputElement): void {
    this.activeBrowseMode = 'file';
    if((this.libraryType === 1 && this.accessService.hasPermission('UPLOAD_MY_FILE')) || (this.libraryType === 2 && this.accessService.hasPermission('UPLOAD_DEPT_FILE'))){
      this.browseMenuOpen = false;
      this.pendingEmptyFolderSelection = false;
      input.click();
    }
    else{
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'No permission to upload files',
        detail: '',
        life: 5000
      });
    }
      
  }

  async browseFolder(input: HTMLInputElement): Promise<void> {
    this.activeBrowseMode = 'folder';
    if((this.libraryType === 1 && this.accessService.hasPermission('UPLOAD_MY_FOLDER')) || (this.libraryType === 2 && this.accessService.hasPermission('UPLOAD_DEPT_FOLDER'))){
      this.browseMenuOpen = false;
      if (!this.IS_FOLDER_UPLOAD_ENABLED) {
        this.messageService.add({
          key: 'custom',
          severity: 'warn',
          summary: 'Folder Upload Disabled',
          detail: 'Folder upload is disabled by configuration.',
          life: 5000
        });
        return;
      }

      const directoryPicker = (window as any)?.showDirectoryPicker;
      if (typeof directoryPicker === 'function') {
        try {
          const rootHandle = await directoryPicker.call(window, { mode: 'read' });
          if (!rootHandle) {
            return;
          }
          const rootName = String((rootHandle as any).name || '').trim();
          const scanResult = await this.collectFilesAndEmptyFoldersFromDirectoryHandle(
            rootHandle,
            rootName ? `${rootName}/` : ''
          );

          if (scanResult.files.length === 0) {
            this.pendingEmptyFolderSelection = true;
            this.showEmptyFolderSelectionDialog();
            return;
          }

          this.pendingEmptyFolderSelection = false;
          this.prepareAndAddFiles(scanResult.files, undefined, scanResult.emptyFolderPaths);
          return;
        } catch (error: any) {
          if (error?.name !== 'AbortError') {
            console.warn('Directory picker failed, falling back to input folder picker.', error);
          } else {
            return;
          }
        }
      }

      this.pendingEmptyFolderSelection = true;
      this.awaitingFolderPickerResult = true;
      this.folderPickerHandledResult = false;
      this.activeFolderInputEl = input;
      if (this.folderPickerFallbackTimer) {
        clearTimeout(this.folderPickerFallbackTimer);
        this.folderPickerFallbackTimer = null;
      }
      // Always clear previous value so selecting the same folder re-triggers change.
      input.value = '';
      input.click();
    }
    else{
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'No permission to upload folders',
        detail: '',
        life: 5000
      });
    }
  }

  private async collectFilesAndEmptyFoldersFromDirectoryHandle(
    directoryHandle: any,
    currentRelativePath: string
  ): Promise<{ files: File[]; emptyFolderPaths: string[] }> {
    const files: File[] = [];
    const emptyFolderPaths: string[] = [];
    const entries: Array<[string, any]> = [];

    for await (const entry of directoryHandle.entries()) {
      entries.push(entry);
    }

    if (entries.length === 0) {
      const normalizedEmptyPath = currentRelativePath.replace(/\/+$/, '');
      if (normalizedEmptyPath) {
        emptyFolderPaths.push(normalizedEmptyPath);
      }
      return { files, emptyFolderPaths };
    }

    for (const [name, handle] of entries) {
      if (handle?.kind === 'file') {
        const file = await handle.getFile();
        const relativePath = `${currentRelativePath}${name}`.replace(/\\/g, '/');
        Object.defineProperty(file, 'webkitRelativePath', {
          value: relativePath,
          writable: false,
          configurable: true
        });
        files.push(file);
        continue;
      }

      if (handle?.kind === 'directory') {
        const nestedPath = `${currentRelativePath}${name}/`;
        const nestedResult = await this.collectFilesAndEmptyFoldersFromDirectoryHandle(handle, nestedPath);
        files.push(...nestedResult.files);
        emptyFolderPaths.push(...nestedResult.emptyFolderPaths);
      }
    }

    return { files, emptyFolderPaths };
  }

  @HostListener('window:focus')
  onWindowFocus(): void {
    if (!this.awaitingFolderPickerResult) {
      return;
    }

    // Browser-dependent: some don't emit change for empty folders.
    // Poll briefly after focus returns to avoid false positives on slow file-list population.
    const startedAt = Date.now();
    const maxWaitMs = 2500;
    const poll = () => {
      if (!this.awaitingFolderPickerResult) {
        return;
      }
      const pickedFileCount = this.activeFolderInputEl?.files?.length || 0;
      if (this.folderPickerHandledResult || pickedFileCount > 0) {
        this.awaitingFolderPickerResult = false;
        this.pendingEmptyFolderSelection = false;
        this.activeFolderInputEl = null;
        this.folderPickerFallbackTimer = null;
        return;
      }
      if (Date.now() - startedAt >= maxWaitMs) {
        this.awaitingFolderPickerResult = false;
        if (this.pendingEmptyFolderSelection) {
          this.showEmptyFolderSelectionDialog();
        }
        this.activeFolderInputEl = null;
        this.folderPickerFallbackTimer = null;
        return;
      }
      this.folderPickerFallbackTimer = setTimeout(poll, 200);
    };

    if (this.folderPickerFallbackTimer) {
      clearTimeout(this.folderPickerFallbackTimer);
    }
    this.folderPickerFallbackTimer = setTimeout(poll, 300);
  }

  private showEmptyFolderSelectionDialog(): void {
    this.showEmptyFolderDialog = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.isDragging = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragDepth = 0;
    this.isDragging = false;
    this.browseMenuOpen = false;


    const items = event.dataTransfer?.items;
    if(((this.getUploadDestinationLibraryType() == 1 && this.accessService.hasPermission('UPLOAD_MY_FOLDER')) || (this.getUploadDestinationLibraryType() == 2 && this.accessService.hasPermission('UPLOAD_DEPT_FOLDER')))){
      if (items) {
        const dropPromises: Promise<{ files: File[]; emptyFolderPaths: string[] }>[] = [];
        const directFiles: File[] = [];
        let skippedFolders = false;
        let hasDirectoryEntry = false;
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.kind === 'file') {
            const entry = (item as any).webkitGetAsEntry?.();
            if (entry) {
              if (entry.isDirectory) {
                hasDirectoryEntry = true;
              }
              if (entry.isDirectory && !this.IS_FOLDER_UPLOAD_ENABLED) {
                skippedFolders = true;
                continue;
              }
              dropPromises.push(this.traverseFileTree(entry));
            } else {
              const droppedFile = item.getAsFile();
              if (droppedFile) {
                directFiles.push(droppedFile);
              }
            }
          }
        }

        if (dropPromises.length > 0 || directFiles.length > 0) {
          Promise.allSettled(dropPromises).then((settledResults) => {
            const traversedResults = settledResults
              .filter(result => result.status === 'fulfilled')
              .map(result => result.status === 'fulfilled' ? result.value : { files: [], emptyFolderPaths: [] });
            const traversedFiles = traversedResults.flatMap(result => result.files);
            const traversedEmptyFolderPaths = traversedResults.flatMap(result => result.emptyFolderPaths);
            const allFiles = [...traversedFiles, ...directFiles];
            if (allFiles.length > 0) {
              this.pendingEmptyFolderSelection = false;
              this.prepareAndAddFiles(allFiles, undefined, traversedEmptyFolderPaths);
            } else if (hasDirectoryEntry && !skippedFolders) {
              this.showEmptyFolderSelectionDialog();
              this.pendingEmptyFolderSelection = true;
            }
            if (skippedFolders) {
              this.messageService.add({
                key: 'custom',
                severity: 'warn',
                summary: 'Folder Upload Disabled',
                detail: 'Folder upload is disabled by configuration.',
                life: 5000
              });
            }
          });
          return;
        }

        if (skippedFolders) {
          this.messageService.add({
            key: 'custom',
            severity: 'warn',
            summary: 'Folder Upload Disabled',
            detail: 'Folder upload is disabled by configuration.',
            life: 5000
          });
        }
      }
    }
    else{
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'No permission to upload folders',
        detail: '',
        life: 5000
      });
    }

    if((this.getUploadDestinationLibraryType() == 1 && this.accessService.hasPermission('UPLOAD_MY_FILE')) || (this.getUploadDestinationLibraryType() == 2 && this.accessService.hasPermission('UPLOAD_DEPT_FILE'))){
      const files = event.dataTransfer?.files;
      if (files && files.length > 0) {
        this.pendingEmptyFolderSelection = false;
        this.prepareAndAddFiles(Array.from(files) as File[]);
        return;
      }
    }
    else{
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'No permission to upload file',
        detail: '',
        life: 5000
      });
    }
  }

  private async traverseFileTree(item: any, path: string = ''): Promise<{ files: File[]; emptyFolderPaths: string[] }> {
    const files: File[] = [];
    const emptyFolderPaths: string[] = [];
    if (item.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (item as any).file(resolve, reject)
      );
      if (path) {
        Object.defineProperty(file, 'webkitRelativePath', {
          value: path + file.name,
          writable: false,
          configurable: true
        });
      }
      files.push(file);
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      const entries = await this.readAllDirectoryEntries(dirReader);
      const currentPath = `${path}${item.name}`.replace(/\/+$/g, '');
      if (entries.length === 0 && currentPath) {
        emptyFolderPaths.push(currentPath);
      }
      for (const entry of entries) {
        const nestedFiles = await this.traverseFileTree(entry, path + item.name + '/');
        files.push(...nestedFiles.files);
        emptyFolderPaths.push(...nestedFiles.emptyFolderPaths);
      }
    }
    return { files, emptyFolderPaths };
  }

  private async readAllDirectoryEntries(dirReader: any): Promise<any[]> {
    const entries: any[] = [];
    let batch: any[] = [];
    do {
      batch = await new Promise<any[]>((resolve) => dirReader.readEntries(resolve));
      entries.push(...batch);
    } while (batch.length > 0);
    return entries;
  }

  private prepareAndAddFiles(newFiles: File[], inputEl?: HTMLInputElement, incomingEmptyFolderPaths: string[] = []): void {
    const { allowedFiles, rejectedFiles } = this.splitFilesByAllowedTypes(newFiles);
    if (rejectedFiles.length > 0) {
      const rejectedNames = rejectedFiles.map(file => file.name).join(', ');
      this.messageService.add({
        key: 'custom',
        severity: 'warn',
        summary: 'Unsupported File Type',
        detail: `Skipped unsupported file(s): ${rejectedNames}`,
        life: 5000
      });
    }
    if (allowedFiles.length === 0) {
      if (inputEl) inputEl.value = '';
      return;
    }
    this.pendingEmptyFolderSelection = false;
    newFiles = this.applySelectedFileTypeFilter(allowedFiles, inputEl);
    if (!newFiles.length) {
      return;
    }

    const adjusted = this.applyLocalSelectionAutoSuffix(newFiles);
    if (adjusted.renamedPairs.length > 0) {
      this.messageService.add({
        key: 'custom',
        severity: 'info',
        summary: 'Duplicate File Added',
        detail: `Auto-renamed: ${adjusted.renamedPairs.join(', ')}`,
        life: 4000
      });
    }
    this.addFilesWithDuplicateCheck(adjusted.files, inputEl, incomingEmptyFolderPaths);
  }

  /**
   * Same Selected File Type criteria for file and folder picks:
   * when a type is chosen, only matching extensions are kept.
   */
  private applySelectedFileTypeFilter(files: File[], inputEl?: HTMLInputElement): File[] {
    if (!this.selectedFileType || !files?.length) {
      return files;
    }

    const matching = files.filter((file) => this.isFileAllowedForSelectedType(file.name));
    const mismatched = files.filter((file) => !this.isFileAllowedForSelectedType(file.name));
    if (mismatched.length > 0) {
      this.messageService.add({
        key: 'custom',
        severity: 'warn',
        summary: 'File Type Mismatch',
        detail: `Skipped files that do not match the chosen type (${this.selectedFileType}): ${mismatched.map((f) => f.name).join(', ')}`,
        life: 5000
      });
    }
    if (matching.length === 0) {
      if (inputEl) inputEl.value = '';
      this.messageService.add({
        key: 'custom',
        severity: 'warn',
        summary: 'File Type Mismatch',
        detail: 'Selected files do not match the chosen file type.',
        life: 5000
      });
    }
    return matching;
  }

  onFolderSelect(event: any): void {
    this.browseMenuOpen = false;
    this.folderPickerHandledResult = true;
    this.awaitingFolderPickerResult = false;
    if (this.folderPickerFallbackTimer) {
      clearTimeout(this.folderPickerFallbackTimer);
      this.folderPickerFallbackTimer = null;
    }
    this.activeFolderInputEl = null;
    const files = event?.target?.files;
    if (!files || files.length === 0) {
      this.pendingEmptyFolderSelection = true;
      this.showEmptyFolderSelectionDialog();
      if (event?.target) {
        event.target.value = '';
      }
      return;
    }

    this.pendingEmptyFolderSelection = false;
    this.onFileSelect(event);
  }

  onFileSelect(event: any): void {
    this.browseMenuOpen = false;
    const files = event.target.files;
    const inputEl = event?.target as HTMLInputElement;
    const isFolderPicker = !!(inputEl?.hasAttribute?.('webkitdirectory') || inputEl?.hasAttribute?.('directory'));
    if (isFolderPicker && (!files || files.length === 0)) {
      this.showEmptyFolderSelectionDialog();
      this.pendingEmptyFolderSelection = true;
      if (event?.target) {
        event.target.value = '';
      }
      return;
    }
    if (files && files.length > 0) {
      if (isFolderPicker) {
        this.pendingEmptyFolderSelection = false;
      }
      let newFiles = Array.from(files) as File[];
      const { allowedFiles, rejectedFiles } = this.splitFilesByAllowedTypes(newFiles);
      if (rejectedFiles.length > 0) {
        const rejectedNames = rejectedFiles.map(file => file.name).join(', ');
        this.messageService.add({
          key: 'custom',
          severity: 'warn',
          summary: 'Unsupported File Type',
          detail: `Skipped unsupported file(s): ${rejectedNames}`,
          life: 5000
        });
      }
      if (allowedFiles.length === 0) {
        event.target.value = '';
        return;
      }
      newFiles = this.applySelectedFileTypeFilter(allowedFiles, event?.target);
      if (!newFiles.length) {
        return;
      }
      if (!this.IS_FOLDER_UPLOAD_ENABLED && newFiles.some(file => this.getNormalizedRelativePath(file).includes('/'))) {
        if (event?.target) {
          event.target.value = '';
        }
        this.messageService.add({
          key: 'custom',
          severity: 'warn',
          summary: 'Folder Upload Disabled',
          detail: 'Folder upload is disabled by configuration.',
          life: 5000
        });
        return;
      }
      
      const adjusted = this.applyLocalSelectionAutoSuffix(newFiles);
      if (adjusted.renamedPairs.length > 0) {
        this.messageService.add({
          key: 'custom',
          severity: 'info',
          summary: 'Duplicate File Added',
          detail: `Auto-renamed: ${adjusted.renamedPairs.join(', ')}`,
          life: 4000
        });
      }
      this.addFilesWithDuplicateCheck(adjusted.files, event.target);
      event.target.value = '';
    }
  }

  private addFilesWithDuplicateCheck(newFiles: File[], inputEl?: HTMLInputElement, incomingEmptyFolderPaths: string[] = []): void {
    this.finalizeFilesForSelection(newFiles, inputEl, incomingEmptyFolderPaths);
    const filesForDuplicateCheck = newFiles.map(file => this.ensureUploadFileMetadata(file));
    this.executeDuplicateCheck(filesForDuplicateCheck, true);
  }

  private finalizeFilesForSelection(newFiles: File[], inputEl?: HTMLInputElement, incomingEmptyFolderPaths: string[] = []): void {
    const preparedNewFiles = newFiles.map(file => this.ensureUploadFileMetadata(file));
    const previousSelection = this.selectedFiles;
    const incomingFolderTokens = this.collectIncomingFolderTopLevelTokens(preparedNewFiles);

    let baseSelectedFiles =
      incomingFolderTokens.size === 0
        ? previousSelection
        : previousSelection.filter(file => {
            const enhanced = this.ensureUploadFileMetadata(file);
            const path = this.getNormalizedRelativePath(enhanced);
            if (!path.includes('/')) {
              return true;
            }
            const token = String((enhanced as any).__topLevelToken || '').toLowerCase();
            return !incomingFolderTokens.has(token);
          });

    if (incomingFolderTokens.size > 0) {
      this.pendingEmptyFolderRelativePaths = new Set(
        Array.from(this.pendingEmptyFolderRelativePaths).filter(path => {
          const token = String(path.split('/').filter(Boolean)[0] || '').toLowerCase();
          return !incomingFolderTokens.has(token);
        })
      );
    }

    const incomingFlatOriginalNames = this.collectIncomingFlatFileOriginalNames(preparedNewFiles);
    if (incomingFlatOriginalNames.size > 0) {
      baseSelectedFiles = baseSelectedFiles.filter(file => {
        const enhanced = this.ensureUploadFileMetadata(file);
        const path = this.getNormalizedRelativePath(enhanced);
        if (path.includes('/')) {
          return true;
        }
        const orig = String((enhanced as any).__originalName || enhanced.name).toLowerCase();
        return !incomingFlatOriginalNames.has(orig);
      });
    }

    // Temporarily add files to check quota
    const tempSelectedFiles = [...baseSelectedFiles, ...preparedNewFiles];
    const originalSelectedFiles = previousSelection;
    this.selectedFiles = tempSelectedFiles;

    // Check storage quota before adding files
    const quotaCheck = this.checkStorageQuota();

    if (!quotaCheck.isValid) {
      // Quota exceeded - revert file selection and show error
      this.selectedFiles = originalSelectedFiles;
      if (inputEl) inputEl.value = '';
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Storage Quota Exceeded',
        detail: quotaCheck.message,
        life: 5000
      });
      return;
    }

    // Quota check passed - keep files and show warning if approaching limit
    if (quotaCheck.message) {
      this.messageService.add({
        key: 'custom',
        severity: 'warn',
        summary: 'Storage Warning',
        detail: quotaCheck.message,
        life: 4000
      });
    }

    this.updateSharePermissionOptions();
    this.checkFileSizeAndToggleUploadMode();
    this.tryAutoStartResume();
    this.syncUploadMetadataState();
    this.syncFolderPickerSourceWithUploadPermissions();
    this.syncWorkflowAvailabilityForSelectedFiles();
    incomingEmptyFolderPaths
      .map(path => path.replace(/\\/g, '/').replace(/^\/+|\/+$/g, '').trim())
      .filter(path => !!path)
      .forEach(path => this.pendingEmptyFolderRelativePaths.add(path));
    if (inputEl) inputEl.value = '';
  }

  private getDuplicateCheckFolderId(): number {
    return this.getUploadDestinationFolderId();
  }

  /** Same folder ID as FormData `FolderId` / upload target — use for refresh after upload. */
  private getUploadDestinationFolderId(): number {
    if (this.selectedFolderId !== null) {
      const selected = Number(this.selectedFolderId);
      if (Number.isFinite(selected) && selected > 0) {
        return selected;
      }
      // Root / no concrete folder → folderId 0
      return 0;
    }
    return (
      this.newlyCreatedFolderId ||
      this.selectedEvent?.folderId ||
      (this.currentFolderId && this.currentFolderId !== 0 ? this.currentFolderId : 0) ||
      0
    );
  }

  checkFileSizeAndToggleUploadMode(): void {
    // Check if resumable upload is enabled in environment config
    if (!this.RESUMABLE_UPLOAD_ENABLED) {
      this.useResumableUpload = false;
      this.showResumableUpload = false;
      this.completedResumableSessions = [];
      return;
    }

    const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const exceedsThreshold = totalSize > this.RESUMABLE_UPLOAD_THRESHOLD;

    if (exceedsThreshold) {
      this.useResumableUpload = true;
      this.showResumableUpload = true;
      console.log(`Files exceed ${this.formatBytes(this.RESUMABLE_UPLOAD_THRESHOLD)} threshold. Resumable upload will be used.`);
      console.log(`Total size: ${this.formatBytes(totalSize)}`);
      // Auto-start resumable upload when files exceed threshold
      // The resumable upload component will handle this via ngOnChanges
    } else {
      this.useResumableUpload = false;
      this.showResumableUpload = false;
      this.completedResumableSessions = [];
    }
  }

  removeSelectedFile(fileToRemove: File): void {
    const index = this.selectedFiles.indexOf(fileToRemove);
    if (index === -1) {
      return;
    }
    this.removeFile(index);
  }

  removeFile(index: number): void {
    const fileToRemove = this.selectedFiles[index];
    
    // Remove from selectedFiles
    this.selectedFiles.splice(index, 1);
    if (this.perFileFolderIds.length > index) {
      this.perFileFolderIds.splice(index, 1);
    }
    
    // Also remove from upload manager and cancel/delete upload if it exists
    if (fileToRemove) {
      const uploads = this.uploadManager.getAllUploads();
      const matchingUpload = uploads.find(upload => 
        (upload.file.name === fileToRemove.name &&
        upload.file.size === fileToRemove.size &&
        upload.file.lastModified === fileToRemove.lastModified) ||
        (upload.totalBytes === fileToRemove.size && upload.file.name === fileToRemove.name)
      );
      
      if (matchingUpload) {
        // Cancel the upload if it's in progress
        if (matchingUpload.status === 'uploading' || matchingUpload.status === 'initializing' || 
            matchingUpload.status === 'queued' || matchingUpload.status === 'paused') {
          // Cancel the upload (this will handle session cleanup)
          if (this.resumableUploadComponent) {
            this.resumableUploadComponent.cancelUpload(matchingUpload.fileId);
          } else {
            // Fallback: cancel directly in manager
            this.uploadManager.cancelUpload(matchingUpload.fileId);
          }
        } else {
          // Just remove if not active
          this.uploadManager.removeUpload(matchingUpload.fileId);
        }
        
        console.log('[DIALOG] Removed file from upload manager:', fileToRemove.name);
      }
    }
    
    this.checkFileSizeAndToggleUploadMode();
    this.syncUploadMetadataState();
  }

  onChipsChange(event: any[]): void {
    if (event && event.length > 0) {
      const last = event[event.length - 1];

      // Reject inactive event selection
      if (last?.isInactive || last?.disabled) {
        this.messageService.add({
          severity: 'error',
          summary: 'Inactive Event',
          detail: `This event is inactive. Please reactivate it from Events Management or choose a different event.`,
          life: 5000
        });
        // Revert to previous selection
        this.selectedEventChips = this._selectedEvent ? [this._selectedEvent] : [];
        return;
      }

      this.selectedEvent = last;
    } else {
      this.selectedEvent = null;
    }
  }

  onLocationChipsChange(event: any[]): void {
    // Keep location behavior same as event: single chip only.
    if (event && event.length > 0) {
      const last = event[event.length - 1];
      this.selectedLocation = last;
    } else {
      this.selectedLocation = null;
    }
  }

  onEventSelect(event: any): void {
    // This might still be useful for side effects if needed, but primary logic is in onChipsChange
    console.log('Selected event:', event);
  }

  onEventEnter(event: any, autoComplete?: any): void {
    const value = event.target.value.trim();
    if (!value) return;

    // Check if the name matches an inactive event
    const inactiveMatch = this.allEventsIncludingInactive.find(
      (e: any) => e.eventNameEn?.toLowerCase() === value.toLowerCase()
        && e.isActivated === false
    );
    if (inactiveMatch) {
      this.messageService.add({
        severity: 'error',
        summary: 'Inactive Event',
        detail: `An event named "${value}" already exists but is inactive. Please reactivate it from Events Management or choose a different name.`,
        life: 6000
      });
      event.target.value = '';
      if (autoComplete) {
        autoComplete.hide();
      }
      return;
    }

    const eventDateStr = this.getEventDateForApi();
    const newEvent = {
      eventId: 0,
      eventNameEn: value,
      eventNameAr: value,
      isNew: true,
      ...(eventDateStr ? { eventDate: eventDateStr } : {}),
    };

    this.selectedEvent = newEvent;
    event.target.value = '';

    if (autoComplete) {
      autoComplete.hide();
    }
  }

  onLocationEnter(event: any, autoComplete?: any): void {
    const value = this.extractAutoCompleteInputValue(event, autoComplete);
    if (!value) return;
    if (event?.target) {
      event.target.value = '';
    }

    const resolvedLocation = this.resolveLocationInputValue(value);
    this.filteredLocations = [];

    // Defer assignment to avoid PrimeNG key handling overwriting model right after Enter.
    setTimeout(() => {
      this.selectedLocation = resolvedLocation;
      if (autoComplete) {
        autoComplete.hide();
        const inputEl = autoComplete.inputEL?.nativeElement;
        if (inputEl) {
          // Show selected value as chip and keep input empty.
          inputEl.value = '';
        }
      }
    });
  }

  onLocationSelect(event: any): void {
    const selected = event?.value ?? event;
    this.selectedLocation = selected?.isNew
      ? this.resolveLocationInputValue(selected?.locationNameEn || '')
      : selected;
    setTimeout(() => {
      const inputEl = event?.originalEvent?.target as HTMLInputElement | undefined;
      if (inputEl) {
        inputEl.value = '';
      }
    });
  }

  onLocationInputBlur(event: any): void {
    const value = this.extractAutoCompleteInputValue(event);
    if (!value) {
      return;
    }
    // Keep free-typed value so upload can send it as NewLocation even if user didn't press Enter.
    // This also lets users overwrite a previously selected location by typing a new value.
    this.selectedLocation = this.resolveLocationInputValue(value);
  }

  private resolveLocationInputValue(rawValue: string): any {
    const value = String(rawValue || '').trim();
    if (!value) {
      return null;
    }

    const matchingLocation = this.locations.find(
      (loc: any) => (loc?.locationNameEn || '').trim().toLowerCase() === value.toLowerCase()
    );

    if (matchingLocation) {
      return matchingLocation;
    }

    return {
      locationId: 0,
      locationNameEn: value,
      isNew: true
    };
  }

  private extractAutoCompleteInputValue(event: any, autoComplete?: any): string {
    const eventValue = String(event?.target?.value || '').trim();
    if (eventValue) {
      return eventValue;
    }
    const autoCompleteValue = String(autoComplete?.inputEL?.nativeElement?.value || '').trim();
    return autoCompleteValue;
  }

  private getLocationDisplayName(location: any): string {
    if (!location) {
      return '';
    }
    if (typeof location === 'string') {
      return location.trim();
    }
    return String(location.locationNameEn || location.locationNameAr || '').trim();
  }

  private persistPreferredLocation(location: any): void {
    const normalized = this.normalizePreferredLocation(location);
    if (!normalized) {
      return;
    }

    try {
      localStorage.setItem(this.preferredLocationStorageKey, JSON.stringify(normalized));
    } catch (error) {
      console.warn('Unable to persist preferred location', error);
    }
  }

  private getStoredPreferredLocation(): any | null {
    try {
      const raw = localStorage.getItem(this.preferredLocationStorageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw);
      return this.normalizePreferredLocation(parsed);
    } catch {
      return null;
    }
  }

  private normalizePreferredLocation(location: any): any | null {
    if (!location) {
      return null;
    }

    if (typeof location === 'string') {
      const resolved = this.resolveLocationInputValue(location);
      return resolved || null;
    }

    const rawId = Number(location.locationId ?? location.LocationId ?? 0);
    const locationId = Number.isFinite(rawId) && rawId > 0 ? rawId : 0;
    const locationNameEn = String(
      location.locationNameEn ??
      location.locationNameAr ??
      location.LocationNameEn ??
      location.LocationNameAr ??
      ''
    ).trim();

    if (!locationId && !locationNameEn) {
      return null;
    }

    if (locationId > 0 && this.locations.length > 0) {
      const matched = this.locations.find(
        (loc: any) => Number(loc?.locationId ?? loc?.LocationId ?? 0) === locationId
      );
      if (matched) {
        return matched;
      }
    }

    return {
      locationId,
      locationNameEn,
      isNew: locationId <= 0
    };
  }

  private restorePreferredLocationIfAny(force = false): void {
    if (!force && this.selectedLocation) {
      return;
    }

    const preferred = this.getStoredPreferredLocation();
    if (!preferred) {
      return;
    }

    this.selectedLocation = preferred;
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  private parseNumberArray(value: any): number[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((v) => Number(v)).filter((v) => !isNaN(v));
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        if (trimmed.startsWith('[')) {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed)
            ? parsed.map((v: any) => Number(v)).filter((v: number) => !isNaN(v))
            : [];
        }
      } catch {
        // fall through to comma split
      }
      return trimmed
        .split(',')
        .map((v) => Number(v.trim()))
        .filter((v) => !isNaN(v));
    }
    return [];
  }

  private parseStringArray(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map((v) => String(v).trim()).filter((v) => v.length > 0);
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        if (trimmed.startsWith('[')) {
          const parsed = JSON.parse(trimmed);
          return Array.isArray(parsed)
            ? parsed.map((v: any) => String(v).trim()).filter((v: string) => v.length > 0)
            : [];
        }
      } catch {
        // fall through to comma split
      }
      return trimmed
        .split(',')
        .map((v) => v.trim())
        .filter((v) => v.length > 0);
    }
    return [];
  }

  loadShareLookups(): void {
    this.getAllFileFolderPermissions();
    if (this.departments.length === 0) {
      this.getDepartments();
    }
    if (this.users.length === 0 && this.externalUsers.length === 0) {
      this.loadShareUserList();
    }
  }

  getAllFileFolderPermissions(): void {
    this.documentService.getAllFileFolderPermissions().subscribe((res: any) => {
      if (res?.status) {
        this.allFilePermissions = res?.data?.filePermissions || [];
        this.allFolderPermissions = res?.data?.folderPermissions || [];
      } else {
        this.allFilePermissions = [];
        this.allFolderPermissions = [];
      }
      this.updateSharePermissionOptions();
    });
  }

  private hasFolderItemsInSelection(): boolean {
    return (this.selectedFiles || []).some(file => this.getNormalizedRelativePath(file).includes('/'));
  }

  private updateSharePermissionOptions(): void {
    const shouldUseFolderPermissions = this.hasFolderItemsInSelection();
    this.fileFolderPermissions = shouldUseFolderPermissions
      ? (this.allFolderPermissions || [])
      : (this.allFilePermissions || []);
    this.ensureSharePermissionDefaults();
  }

  private ensureSharePermissionDefaults(): void {
    /*
      Temporary change: disable manual share permission selection.
      Keep a fixed sharePermissionLevel so the existing share flow remains valid.
      Old dynamic default-selection logic is intentionally disabled for now.
    */
    this.sharePermissionLevel = [{
      isCreate: false,
      isRead: true,
      isUpdate: false,
      isDelete: false,
      canDownload: true
    }];
  }

  isOptionDisabled(option: any): boolean {
    return option?.permissions?.isRead === true &&
      option?.permissions?.isUpdate === false &&
      option?.permissions?.isDelete === false &&
      option?.permissions?.canDownload === false;
  }

  onPermissionChange(event: any): void {
    const selectedPermissions = event?.value || [];
    const hasRead = selectedPermissions.some((perm: any) => perm?.isRead === true);
    if (!hasRead && selectedPermissions.length > 0) {
      const readPermission = this.fileFolderPermissions.find((p: any) => p?.permissions?.isRead === true);
      if (readPermission?.permissions) {
        this.sharePermissionLevel = [...selectedPermissions, readPermission.permissions];
      }
    }
    const hasCanViewOnly = (this.sharePermissionLevel || []).some((perm: any) =>
      perm?.isRead === true &&
      perm?.isUpdate === false &&
      perm?.isDelete === false &&
      perm?.canDownload === false
    );
    if (!hasCanViewOnly) {
      const canViewPermission = this.fileFolderPermissions.find((perm: any) =>
        perm?.permissions?.isRead === true &&
        perm?.permissions?.isUpdate === false &&
        perm?.permissions?.isDelete === false &&
        perm?.permissions?.canDownload === false
      );
      if (canViewPermission?.permissions) {
        this.sharePermissionLevel.unshift(canViewPermission.permissions);
      }
    }
  }

  private getMergedSharePermissions(forceFolderContext: boolean = false): {
    isCreate: boolean;
    isRead: boolean;
    isUpdate: boolean;
    isDelete: boolean;
    canDownload: boolean;
  } {
    if (this.envConfig.isSharePermission === false) {
      return {
        isCreate: false,
        isRead: true,
        isUpdate: false,
        isDelete: false,
        canDownload: true
      };
    }
    const permissions = {
      isCreate: false,
      isRead: false,
      isUpdate: false,
      isDelete: false,
      canDownload: false
    };
    (this.sharePermissionLevel || []).forEach((perm: any) => {
      permissions.isCreate = permissions.isCreate || !!perm?.isCreate;
      permissions.isRead = permissions.isRead || !!perm?.isRead;
      permissions.isUpdate = permissions.isUpdate || !!perm?.isUpdate;
      permissions.isDelete = permissions.isDelete || !!perm?.isDelete;
      permissions.canDownload = permissions.canDownload || !!perm?.canDownload;
    });

    const hasFolderContext = forceFolderContext || this.hasFolderItemsInSelection();
    if (hasFolderContext && (permissions.isUpdate || permissions.isDelete)) {
      permissions.isCreate = true;
    }
    if (permissions.isUpdate || permissions.isDelete || permissions.canDownload) {
      permissions.isRead = true;
    }
    return permissions;
  }

  onShareTypeChange(): void {
    if (this.shareType === 'internal') {
      this.selectedExternalUsers = [];
      this.externalEmails = [];
      this.externalEmailsText = '';
      this.invalidExternalEmails = [];
      this.duplicateInputEmails = [];
    } else {
      // Department-only mode is for internal sharing.
      this.shareByDepartment = false;
      this.selectedUsers = [];
      this.selectedDepartment = [];
      this.internalEmails = [];
      this.internalEmailsText = '';
      this.invalidInternalEmails = [];
      this.nonInternalDomainEmails = [];
      this.duplicateInternalEmails = [];
      this.alreadySharedInternalEmails = [];
    }
  }

  onShareByDepartmentChange(value: boolean): void {
    this.shareByDepartment = value;
    if (value) {
      this.shareType = 'internal';
      this.selectedUsers = [];
      this.selectedExternalUsers = [];
      this.internalEmails = [];
      this.internalEmailsText = '';
      this.nonInternalDomainEmails = [];
      this.duplicateInternalEmails = [];
      this.alreadySharedInternalEmails = [];
      this.invalidInternalEmails = [];
      this.externalEmails = [];
      this.externalEmailsText = '';
      this.invalidExternalEmails = [];
      this.duplicateInputEmails = [];
    }
  }

  isInternalOrganizationEmail(email: string): boolean {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    const domains =
      this.envConfig.allowedDomains && this.envConfig.allowedDomains.length > 0
        ? this.envConfig.allowedDomains.map((d) => d.toLowerCase())
        : ['aspirezone.qa', 'aspire.qa', 'aspirelogistics.qa', 'aspetar.com'];
    return domains.some((d) => normalized.endsWith('@' + d));
  }

  validateInternalEmails(): void {
    this.invalidInternalEmails = [];
    this.nonInternalDomainEmails = [];
    this.duplicateInternalEmails = [];
    this.alreadySharedInternalEmails = [];

    const rawEmails = (this.internalEmailsText || '')
      .split(',')
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);

    if (rawEmails.length === 0) {
      this.internalEmails = [];
      return;
    }

    const invalidFormat = new Set<string>();
    const wrongDomain = new Set<string>();
    const seenInInput = new Set<string>();
    const duplicates = new Set<string>();
    const alreadyShared = new Set<string>();

    rawEmails.forEach((email) => {
      if (!this.isValidEmail(email)) {
        invalidFormat.add(email);
      } else if (!this.isInternalOrganizationEmail(email)) {
        wrongDomain.add(email);
      }
      if (seenInInput.has(email)) {
        duplicates.add(email);
      }
      seenInInput.add(email);
      if (this.sharedUsersList && this.sharedUsersList.length > 0) {
        if (this.sharedUsersList.some((u: any) => (u.email || '').toLowerCase() === email)) {
          alreadyShared.add(email);
        }
      }
    });

    this.invalidInternalEmails = Array.from(invalidFormat);
    this.nonInternalDomainEmails = Array.from(wrongDomain);
    this.duplicateInternalEmails = Array.from(duplicates);
    this.alreadySharedInternalEmails = Array.from(alreadyShared);
    this.internalEmails = Array.from(seenInInput).filter(
      (e) =>
        this.isValidEmail(e) &&
        this.isInternalOrganizationEmail(e) &&
        !duplicates.has(e) &&
        !alreadyShared.has(e)
    );
  }

  validateExternalEmails(): void {
    this.invalidExternalEmails = [];
    this.duplicateInputEmails = [];

    const rawEmails = this.externalEmailsText
      .split(',')
      .map(email => email.trim().toLowerCase())
      .filter(email => email.length > 0);

    if (rawEmails.length === 0) {
      this.externalEmails = [];
      return;
    }

    const invalidFormat = new Set<string>();
    const seenInInput = new Set<string>();
    const duplicates = new Set<string>();

    rawEmails.forEach(email => {
      if (!this.isValidEmail(email)) {
        invalidFormat.add(email);
      }
      if (seenInInput.has(email)) {
        duplicates.add(email);
      }
      seenInInput.add(email);
    });

    this.invalidExternalEmails = Array.from(invalidFormat);
    this.duplicateInputEmails = Array.from(duplicates);
    this.externalEmails = Array.from(seenInInput);
  }

  private hasShareFieldChanges(): boolean {
    if (this.shareExpireDate !== null) {
      return true;
    }
    if (this.selectedDepartment && this.selectedDepartment.length > 0) {
      return true;
    }
    if (this.selectedUsers && this.selectedUsers.length > 0) {
      return true;
    }
    if (this.selectedExternalUsers && this.selectedExternalUsers.length > 0) {
      return true;
    }
    if (this.externalEmailsText && this.externalEmailsText.trim().length > 0) {
      return true;
    }
    if (this.internalEmailsText && this.internalEmailsText.trim().length > 0) {
      return true;
    }
    return false;
  }

  private isShareValid(): boolean {
    if (!this.markAsSecure) {
      return true;
    }

    if (this.envConfig.isSharePermission !== false) {
      if (!this.sharePermissionLevel || this.sharePermissionLevel.length === 0) {
        return false;
      }
    }

    if (this.shareExpireDate) {
      const diffInDays = Math.round(
        (this.shareExpireDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      );
      if (diffInDays < 0) {
        return false;
      }
    }

    if (this.shareByDepartment) {
      return this.selectedDepartment && this.selectedDepartment.length > 0;
    }

    if (this.shareType === 'external') {
      this.validateExternalEmails();
      if (this.invalidExternalEmails.length > 0 || this.duplicateInputEmails.length > 0) {
        return false;
      }
      const hasExternalEmails = this.externalEmailsText && this.externalEmailsText.trim().length > 0;
      return (this.selectedExternalUsers && this.selectedExternalUsers.length > 0) ||
        (hasExternalEmails && this.externalEmails.length > 0);
    }

    this.validateInternalEmails();
    if (
      this.invalidInternalEmails.length > 0 ||
      this.nonInternalDomainEmails.length > 0 ||
      this.duplicateInternalEmails.length > 0 ||
      this.alreadySharedInternalEmails.length > 0
    ) {
      return false;
    }
    return (
      (this.selectedUsers && this.selectedUsers.length > 0) ||
      (this.internalEmails && this.internalEmails.length > 0)
    );
  }

  getShareUserIds(): number[] {
    if (this.shareByDepartment) {
      return [];
    }
    return this.shareType === 'external' ? (this.selectedExternalUsers || []) : (this.selectedUsers || []);
  }

  /** Share + document-detail recipients for upload/workflow initiation. */
  getMergedShareUserIdsForUpload(): number[] {
    return [
      ...new Set([...this.getShareUserIds(), ...this.getDocumentDetailShareUserIds()]),
    ];
  }

  getShareEmails(): string[] {
    if (this.shareByDepartment) {
      return [];
    }
    if (this.shareType === 'external') {
      return this.externalEmails || [];
    }
    return this.internalEmails || [];
  }

  getShareDepartmentIds(): number[] {
    return this.shareByDepartment ? (this.selectedDepartment || []) : [];
  }

  getShareExpireOn(): string | null {
    if (!this.shareExpireDate) {
      return null;
    }
    const d = new Date(this.shareExpireDate);
    d.setUTCHours(23, 59, 59, 999);
    return d.toISOString();
  }

  private appendShareDetailsToFormData(formData: FormData): void {
    if (!this.markAsSecure) {
      return;
    }
    const shareUserIds = this.getShareUserIds();
    const shareEmails = this.getShareEmails();
    const shareDepartmentIds = this.getShareDepartmentIds();
    const expireOn = this.getShareExpireOn();

    if (shareUserIds.length > 0) {
      shareUserIds.forEach(userId => formData.append('UserIds', userId.toString()));
    }
    if (shareEmails.length > 0) {
      shareEmails.forEach(email => formData.append('Emails', email));
    }
    if (shareDepartmentIds.length > 0) {
      shareDepartmentIds.forEach(departmentId => formData.append('DepartmentIds', departmentId.toString()));
    }
    if (expireOn) {
      formData.append('ExpireOn', expireOn);
    }
  }

  private toLocalUploadIso(d: Date): string {
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offset).toISOString().slice(0, 19);
  }

  private toSafeDate(value: any): Date | null {
    if (value === undefined || value === null) {
      return null;
    }
    const text = String(value).trim();
    if (
      !text ||
      text === '0' ||
      text.startsWith('0001-01-01') ||
      text.startsWith('1970-01-01')
    ) {
      return null;
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  private isExistingEventValue(eventValue: any): boolean {
    if (!eventValue || typeof eventValue !== 'object') {
      return false;
    }
    if (eventValue.isNew) {
      return false;
    }
    return Number(eventValue.eventId) > 0;
  }

  private applySelectedEventDateRange(eventValue: any): void {
    const start =
      this.toSafeDate(eventValue?.eventStartDate) ??
      this.toSafeDate(eventValue?.EventStartDate) ??
      this.toSafeDate(eventValue?.eventDate) ??
      this.toSafeDate(eventValue?.EventDate);
    if (!start) {
      // Existing event without a valid date (ex: 1970/0001/null) should not keep stale range.
      this.eventDateRange = null;
      this.date = new Date();
      return;
    }
    const end =
      this.toSafeDate(eventValue?.eventEndDate) ??
      this.toSafeDate(eventValue?.EventEndDate) ??
      start;

    this.eventDateRange = [start, end];
  }

  private hasValidDateInEvent(eventValue: any): boolean {
    if (!eventValue || typeof eventValue !== 'object') {
      return false;
    }
    return !!(
      this.toSafeDate(eventValue?.eventStartDate) ??
      this.toSafeDate(eventValue?.EventStartDate) ??
      this.toSafeDate(eventValue?.eventDate) ??
      this.toSafeDate(eventValue?.EventDate)
    );
  }

  private getNormalizedEventDateRange(): [Date, Date] | null {
    const start = this.eventDateRange?.[0];
    if (!start) {
      return null;
    }

    const end = this.eventDateRange?.[1] ?? start;
    return [start, end];
  }

  private getEventRangeStartIso(): string {
    const d = this.getNormalizedEventDateRange()?.[0] ?? this.date;
    return d ? this.toLocalUploadIso(d) : '';
  }

  private getEventRangeEndIso(): string {
    const d = this.getNormalizedEventDateRange()?.[1] ?? this.date;
    return d ? this.toLocalUploadIso(d) : '';
  }

  /** EventDate on API = range start (InsertEvent). */
  /** DevTools often hides multipart fields when files are large; logs keys/values in non-production builds. */
  private logFormDataEntries(formData: FormData, context: string): void {
    const rows: { key: string; value: string }[] = [];
    formData.forEach((value, key) => {
      const valueStr =
        value instanceof File ? `File(${value.name}, ${value.size} bytes)` : String(value);
      rows.push({ key, value: valueStr });
    });
    if (!this.envConfig?.production) {
      console.table(rows);
      console.debug(`[FormData:${context}]`, rows);
      console.info(
        `[Upload] ${context}: ${rows.length} multipart field(s). In DevTools pick the POST (not OPTIONS); Payload may list parts under "Form Data" or "Request payload", not as URL-encoded form.`
      );
    }
    if (rows.length === 0) {
      console.warn(`[Upload] FormData is empty (${context}) — upload request would have no body.`);
    }
  }

  private appendEventDatesToFormData(formData: FormData): void {
    const startIso = this.getEventRangeStartIso();
    const endIso = this.getEventRangeEndIso();
    if (startIso) {
      formData.append('EventStartDate', startIso);
      formData.append('EventDate', startIso);
    }
    if (endIso) {
      formData.append('EventEndDate', endIso);
    }
  }

  private resolveFileCreationDateIso(): string {
    const base = this.date;
    if (!base) {
      return this.toLocalUploadIso(new Date());
    }
    return this.toLocalUploadIso(base);
  }

  private resolveFileCreationDateFromEditedFile(file: any): Date | null {
    return (
      this.toSafeDate(file?.fileCreationDate) ||
      this.toSafeDate(file?.FileCreationDate) ||
      this.toSafeDate(file?.file_CreationDate) ||
      this.toSafeDate(file?.fileCreatedOn) ||
      this.toSafeDate(file?.createdOn) ||
      this.toSafeDate(file?.system_CreatedOn) ||
      this.toSafeDate(file?.systemCreatedOn) ||
      this.toSafeDate(file?.uploadedDate) ||
      this.toSafeDate(file?.submission_date) ||
      this.toSafeDate(file?.submissionDate)
    );
  }

  @ViewChild('eventDateRangeWrapper', { read: ElementRef }) eventDateRangeWrapper?: ElementRef<HTMLElement>;
  @ViewChild('eventDatePicker') eventDatePicker?: DocNgbDateRangeComponent;

  openEventDateRangePicker(): void {
    this.eventDatePicker?.openStart();
  }

  onEventDateRangeSelect(): void {
    // Keep file creation date independent from event duration selection.
  }

  onEventRangeCalendarShow(): void {
    this.eventRangeCalendarDefaultDate = new Date();
  }

  clearDateRange(): void {
    this.eventDateRange = null;
    this.date = new Date();
  }

  getResumableEventDateIso(): string {
    return this.getEventRangeStartIso();
  }

  getResumableEventStartIso(): string {
    return this.getEventRangeStartIso();
  }

  getResumableEventEndIso(): string {
    return this.getEventRangeEndIso();
  }

  getResumableLocationId(): number | null {
    const payload = this.resolveLocationPayload();
    return payload?.locationId ?? null;
  }

  getResumableNewLocation(): string | null {
    const payload = this.resolveLocationPayload();
    return payload?.newLocation ?? null;
  }

  getResumableFileCreationIso(): string {
    return this.resolveFileCreationDateIso();
  }

  private pendingMetadataExtras(): Pick<
    NonNullable<UploadDialogState['autoPopulate']>,
    'eventDateRangeStart' | 'eventDateRangeEnd' | 'selectedLocation' | 'selectedFileType' | 'selectedCategoryId' | 'categoryLocked'
  > {
    const normalizedRange = this.getNormalizedEventDateRange();
    const start = normalizedRange?.[0] ?? null;
    const end = normalizedRange?.[1] ?? null;
    return {
      eventDateRangeStart: start ? this.toLocalUploadIso(start) : null,
      eventDateRangeEnd: end ? this.toLocalUploadIso(end) : null,
      selectedLocation: this.selectedLocation ?? null,
      selectedFileType: this.selectedFileType,
      selectedCategoryId: this.selectedCategoryId,
      categoryLocked: this.isCategoryLocked
    };
  }

  private resetShareFields(): void {
    this.shareType = 'internal';
    this.shareByDepartment = false;
    this.sharePermissionLevel = [];
    this.selectedUsers = [];
    this.selectedExternalUsers = [];
    this.selectedDepartment = [];
    this.externalEmails = [];
    this.externalEmailsText = '';
    this.invalidExternalEmails = [];
    this.duplicateInputEmails = [];
    this.internalEmails = [];
    this.internalEmailsText = '';
    this.invalidInternalEmails = [];
    this.nonInternalDomainEmails = [];
    this.duplicateInternalEmails = [];
    this.alreadySharedInternalEmails = [];
    this.shareExpireDate = null;
  }

  getDepartments() {
    const body = {
      departmentId: 0,
    };
    this.departmentService.getDepartmentList(body).subscribe((res: any) => {
      if (res && res.data && res.data.items) {
        this.departments = res.data.items.map((dept: any) => ({
          ...dept,
          label: dept.departmentNameEn || dept.departmentNameAr,
          value: Number(dept.departmentId ?? dept.DepartmentId)
        }));

        this.departments = this.departments.filter((dept, index, self) =>
          index === self.findIndex((d) => d.value === dept.value)
        );
      }
    });
  }

  onDepartmentChange(event: any) {
    const departmentIds = event.value;

    if (departmentIds && departmentIds.length > 0) {
      this.getUsersByDepartment(departmentIds);
    } else {
      this.loadShareUserList();
    }
  }

  getUsersByDepartment(departmentIds: number[]) {
    const body = {
      departmentIds: departmentIds,
    };
    this.userService.getUsersByDepartments(body).subscribe((res: any) => {
      if (res && res.data && res.data.length > 0) {
        const filteredInternalUsers = res.data
          .filter((u: any) => !u.isExternalUser)
          .filter((u: any) => !u.isPendingInvitation && u.userId !== this.userId);
        this.users = filteredInternalUsers.map((u: any) => {
          const firstNameEn = u.firstNameEn || '';
          const lastNameEn = u.lastNameEn || '';
          const firstNameAr = u.firstNameAr || '';
          const lastNameAr = u.lastNameAr || '';
          const label = firstNameEn || firstNameAr
            ? `${firstNameEn || firstNameAr} ${lastNameEn || lastNameAr}`.trim()
            : u.email || 'Unknown User';
          return {
            label: label,
            value: u.userId,
            userId: u.userId,
            firstNameEn: u.firstNameEn,
            lastNameEn: u.lastNameEn,
            firstNameAr: u.firstNameAr,
            lastNameAr: u.lastNameAr,
            email: u.email,
            departmentId: u.departmentId,
            ...u
          };
        });

        const availableUserIds = this.users.map((u) => u.value);
        this.selectedUsers = this.selectedUsers.filter((userId) =>
          availableUserIds.includes(userId)
        );
      } else {
        this.users = [];
        this.selectedUsers = [];
      }
    });
  }

  loadShareUserList(searchTerm: string | null = null) {
    const body = {
      pageIndex: 0,
      pageSize: 0,
      searchTerm: searchTerm?.trim() || null,
    };

    this.userService.getAllUserList(body).subscribe((res: any) => {
      const items = Array.isArray(res?.data) ? res.data : [];
      const currentUserId = Number(this.userId || localStorage.getItem('userId') || 0);
      const eligible = items.filter((u: any) => Number(u.userId) !== currentUserId);

      this.users = eligible
        .filter((u: any) => !u.isExternalUser)
        .map((u: any) => this.mapShareUserListOption(u, false));
      this.externalUsers = eligible
        .filter((u: any) => u.isExternalUser)
        .map((u: any) => this.mapShareUserListOption(u, true));
    });
  }

  private mapShareUserListOption(u: any, externalDropdown: boolean) {
    const firstNameEn = u.firstNameEn || '';
    const lastNameEn = u.lastNameEn || '';
    const firstNameAr = u.firstNameAr || '';
    const lastNameAr = u.lastNameAr || '';
    const departmentNameEn = u.departmentNameEn || '';
    const departmentNameAr = u.departmentNameAr || '';
    const roleName = u.roleName || '';
    const email = u.email || '';

    const name =
      firstNameEn || firstNameAr
        ? `${firstNameEn || firstNameAr} ${lastNameEn || lastNameAr}`.trim()
        : email || 'Unknown User';

    const deptLabel = departmentNameEn || departmentNameAr || '';
    const label = externalDropdown
      ? name
      : deptLabel
        ? `${name} (${deptLabel})`
        : name;

    return {
      label,
      value: u.userId,
      userId: u.userId,
      email,
      firstNameEn,
      lastNameEn,
      firstNameAr,
      lastNameAr,
      departmentId: u.departmentId,
      departmentNameEn,
      departmentNameAr,
      roleName,
      isExternalUser: !!u.isExternalUser,
      isActivated: u.isActivated,
      ...u,
    };
  }

  isUserAlreadyShared(option: any): boolean {
    if (!option || !option.value) {
      return false;
    }
    const userId = option.value;
    if (!this.sharedUsersList || this.sharedUsersList.length === 0) {
      return false;
    }
    return this.sharedUsersList.some((sharedUser: any) =>
      sharedUser.sharedToUserId === userId || sharedUser.userId === userId
    );
  }

  isExternalUserAlreadyShared(option: any): boolean {
    if (!option || !option.value) {
      return false;
    }
    const userId = option.value;
    if (!this.sharedUsersList || this.sharedUsersList.length === 0) {
      return false;
    }
    const externalUser = this.externalUsers?.find((u: any) => u.userId === userId);
    if (!externalUser) {
      return false;
    }
    return this.sharedUsersList.some((sharedUser: any) =>
      sharedUser.sharedToUserId === userId ||
      sharedUser.userId === userId ||
      (sharedUser.email && externalUser.email && sharedUser.email.toLowerCase() === externalUser.email.toLowerCase())
    );
  }

  get uploadUserSelectItems(): any[] {
    return (this.users || []).map((user: any) => ({
      ...user,
      disabled: this.isUserAlreadyShared(user),
    }));
  }

  get uploadExternalUserSelectItems(): any[] {
    return (this.externalUsers || []).map((user: any) => ({
      ...user,
      disabled: this.isExternalUserAlreadyShared(user),
    }));
  }

  get uploadPermissionSelectItems(): any[] {
    return (this.fileFolderPermissions || []).map((item: any) => ({
      ...item,
      disabled: this.isOptionDisabled(item),
    }));
  }

  getItemLabel(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (item.label) return item.label;
    if (item.tag) return item.tag;
    if (item.name) return item.name;
    return String(item);
  }

  getRemainingItemsTooltip(items: any[], visibleCount: number = 2): string {
    if (!items || items.length <= visibleCount) return '';
    return items
      .slice(visibleCount)
      .map((item) => this.getItemLabel(item))
      .join(', ');
  }

  isUploadTagSelected(item: any): boolean {
    return (this.tags || []).some((tag) => this.compareTags(tag, item));
  }

  isUploadUserOptionSelected(item: any): boolean {
    const value = item?.value ?? item;
    return (this.selectedUsers || []).some((selected) => selected === value || selected?.value === value);
  }

  isUploadExternalUserOptionSelected(item: any): boolean {
    const value = item?.value ?? item;
    return (this.selectedExternalUsers || []).some((selected) => selected === value || selected?.value === value);
  }

  isUploadDepartmentSelected(item: any): boolean {
    const value = item?.value ?? item;
    return (this.selectedDepartment || []).some((selected) => selected === value || selected?.value === value);
  }

  isUploadPermissionSelected(item: any): boolean {
    const value = item?.permissions ?? item;
    return (this.sharePermissionLevel || []).some((selected) => selected === value || selected?.permissions === value);
  }

  selectAllUploadTags(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.tags = [...(this.tagSelectItems || [])];
    this.onTagsModelChange(this.tags);
  }

  clearAllUploadTags(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.tags = [];
    this.onTagsModelChange(this.tags);
  }

  selectAllUploadUsers(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.selectedUsers = (this.users || [])
      .filter((user: any) => !this.isUserAlreadyShared(user))
      .map((user: any) => user.value);
  }

  clearAllUploadUsers(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.selectedUsers = [];
  }

  selectAllUploadExternalUsers(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.selectedExternalUsers = (this.externalUsers || [])
      .filter((user: any) => !this.isExternalUserAlreadyShared(user))
      .map((user: any) => user.value);
  }

  clearAllUploadExternalUsers(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.selectedExternalUsers = [];
  }

  selectAllUploadDepartments(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.selectedDepartment = (this.departments || []).map((dept: any) => dept.value);
    this.onDepartmentChange({ value: this.selectedDepartment });
  }

  clearAllUploadDepartments(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.selectedDepartment = [];
    this.onDepartmentChange({ value: [] });
  }

  selectAllUploadPermissions(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.sharePermissionLevel = (this.fileFolderPermissions || [])
      .filter((item: any) => !this.isOptionDisabled(item))
      .map((item: any) => item.permissions);
    this.onPermissionChange({ value: this.sharePermissionLevel });
  }

  clearAllUploadPermissions(event?: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.sharePermissionLevel = [];
    this.onPermissionChange({ value: [] });
  }

  isUploadValid(): boolean {
    // Disable button if upload is in progress
    const hasActiveUploads = this.globalUploadQueue.getActiveUploads().length > 0;
    if (hasActiveUploads || this.isUploadActionInProgress) {
      return false; // Disable button when upload is active
    }

    if (
      !this.folderMetadataEditMode &&
      this.shareWithRecipients &&
      (!this.selectedShareRecipientIds || this.selectedShareRecipientIds.length === 0)
    ) {
      return false;
    }

    if (this.editMode) {
      if (!this.folderMetadataEditMode && (!this.editedFileName || this.editedFileName.trim().length === 0)) {
        return false;
      }
      if (this.resubmitWorkflowOnUpdate && !this.resubmitComment.trim()) {
        return false;
      }
      // Category is optional — keep check commented for easy restore.
      // if (!this.folderMetadataEditMode && !this.selectedCategoryId) {
      //   return false;
      // }
      if (this.envConfig.isEventsSecExpanded){
        if (!this.selectedEvent) {
          return false;
        }
        const normalizedEventRange = this.getNormalizedEventDateRange();
        if (!normalizedEventRange?.[0] || !normalizedEventRange?.[1]) {
          return false;     
        }
      }
      if (this.envConfig.isLocationSecExpanded && !this.hasValidLocationSelection()) {
        return false;
      }
      return this.resubmitWorkflowOnUpdate ? true : this.hasEditModeChanges();
    }

    // 1. File is mandatory
    if (!this.selectedFiles || this.selectedFiles.length === 0) {
      return false;
    }

    // File Type field was replaced by Document Type; Category is optional.
    // if (!this.selectedCategoryId) {
    //   return false;
    // }

    // 2. Title validation (1-150 chars)
    // if (!this.title || this.title.trim().length < 1 || this.title.trim().length > 150) {
    //   return false;
    // }

    // 7. For resumable uploads:
    //    - If uploads are completed, button is disabled (no submit needed - metadata already saved)
    //    - If uploads are not started yet (queued), allow start upload
    //    - If uploads are in progress, disable button
    if (this.useResumableUpload) {
      if (this.allUploadsCompleted || this.resumeMetadataLocked) {
        // Uploads completed - metadata already saved during initialize, no submit needed
        return false; // Disable button - no action needed
      } else {
        // Check if uploads are in progress
        const hasActiveUploads = this.globalUploadQueue.getActiveUploads().length > 0;
        if (hasActiveUploads) {
          // Uploads in progress - disable button
          return false;
        } else {
          // Uploads not started yet (queued) - allow start upload
          return true;
        }
      }
    }

    return true;
  }

  /**
   * Fetches the next auto-generated File Number and Document Number for a new upload and
   * binds them to the (read-only) inputs. Mirrors document-page's loadNextDocumentNumbers.
   */
  private loadNextDocumentNumbers(departmentId: number | null = this.libraryDepartmentId): void {
    const loadToken = ++this.documentNumbersLoadToken;
    this.isLoadingDocumentNumbers = true;

    const normalizedDepartmentId = Number(departmentId);
    const requestDepartmentId =
      Number.isFinite(normalizedDepartmentId) && normalizedDepartmentId > 0
        ? normalizedDepartmentId
        : undefined;

    this.documentService.getNextDocumentNumbers(requestDepartmentId).subscribe({
      next: (response: any) => {
        if (loadToken !== this.documentNumbersLoadToken) {
          return;
        }
        this.isLoadingDocumentNumbers = false;

        if ((response?.status ?? response?.Status) !== true) {
          return;
        }

        const data = response?.data ?? response?.Data ?? {};
        this.fileNumber = data.fileNumber ?? data.FileNumber ?? this.fileNumber;
        this.documentNumber = data.documentNumber ?? data.DocumentNumber ?? this.documentNumber;
      },
      error: () => {
        if (loadToken !== this.documentNumbersLoadToken) {
          return;
        }
        this.isLoadingDocumentNumbers = false;
      },
    });
  }

  /**
   * Loads all document types (DocumentType/getList) for the Document Type dropdown.
   */
  private loadDocumentTypeOptions(): void {
    if (this.documentTypeOptions.length > 0) {
      return; // Global list; load once per dialog instance.
    }
    const loadToken = ++this.documentTypesLoadToken;
    this.isLoadingDocumentTypes = true;

    this.documentCategoryService.getDocumentTypeList().subscribe({
      next: (response: any) => {
        if (loadToken !== this.documentTypesLoadToken) {
          return;
        }
        this.isLoadingDocumentTypes = false;
        const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
        this.documentTypeOptions = this.extractLookupItems(response)
          .map((item) => {
            const en = (item?.documentTypeEn ?? item?.DocumentTypeEn ?? '').trim();
            const ar = (item?.documentTypeAr ?? item?.DocumentTypeAr ?? '').trim();
            return {
              value: String(item?.id ?? item?.Id ?? ''),
              label: useArabic ? (ar || en) : (en || ar),
            };
          })
          .filter((opt) => opt.value && opt.value !== '0' && opt.label);
      },
      error: () => {
        if (loadToken === this.documentTypesLoadToken) {
          this.isLoadingDocumentTypes = false;
        }
      },
    });
  }

  private loadSigningAuthorityOptions(): void {
    if (this.signingAuthorities.length > 0) {
      return;
    }
    const loadToken = ++this.signingAuthoritiesLoadToken;
    this.isLoadingSigningAuthorities = true;
    // LookupType/getList → LookupItem/getByLookupTypeId/{id}
    // pageSize 500 matches settings Lookup admin (pageSize 0 can mean "none" on some APIs).
    const listRequest = { pageIndex: 0, pageSize: 500, searchTerm: '' };

    const loadItemsForType = (typeId: number) =>
      this.lookupMasterService.getLookupItemsByTypeId(typeId).pipe(
        switchMap((itemsResponse) => {
          if (this.extractLookupItems(itemsResponse).length > 0) {
            return of(itemsResponse);
          }
          // Empty by-type payload → try LookupItem/getList with type filter
          return this.lookupMasterService
            .getLookupItems({
              pageIndex: 0,
              pageSize: 500,
              searchTerm: '',
              lookupTypeId: typeId,
            })
            .pipe(catchError(() => of(itemsResponse)));
        }),
        catchError(() =>
          this.lookupMasterService
            .getLookupItems({
              pageIndex: 0,
              pageSize: 500,
              searchTerm: '',
              lookupTypeId: typeId,
            })
            .pipe(catchError(() => of(null)))
        )
      );

    this.lookupMasterService
      .getLookupTypes(listRequest)
      .pipe(
        switchMap((lookupTypesResponse) => {
          const lookupTypes = this.extractLookupItems(lookupTypesResponse);
          const signingAuthorityLookupTypeId = this.findLookupTypeId(
            lookupTypes,
            'Signing Authority'
          );
          if (!signingAuthorityLookupTypeId) {
            console.warn(
              'Signing Authority lookup type not found. Available types:',
              lookupTypes.map(
                (item) => item.nameEn ?? item.NameEn ?? item.nameAr ?? item.NameAr
              )
            );
            return of(null);
          }
          return loadItemsForType(signingAuthorityLookupTypeId);
        }),
        catchError((error) => {
          console.warn('Unable to load Signing Authority lookup items.', error);
          return of(null);
        }),
        finalize(() => {
          if (loadToken === this.signingAuthoritiesLoadToken) {
            this.isLoadingSigningAuthorities = false;
          }
        })
      )
      .subscribe((itemsResponse) => {
        if (loadToken !== this.signingAuthoritiesLoadToken) {
          return;
        }
        this.signingAuthorities = this.mapLookupNameItems(
          this.extractLookupItems(itemsResponse)
        );
        if (!this.signingAuthorityId && this.signingAuthority) {
          this.signingAuthorityId = this.resolveSigningAuthorityId({
            signingAuthority: this.signingAuthority,
          });
        }
        this.cdr.detectChanges();
      });
  }

  private loadWorkflowOptions(): void {
    if (this.workflows.length > 0) {
      return;
    }
    const loadToken = ++this.workflowsLoadToken;
    this.isLoadingWorkflows = true;

    // POST /api/Workflow/getList
    this.workflowService
      .workflowList({
        pageIndex: 0,
        pageSize: 0,
        userId: localStorage.getItem('userId'),
      })
      .pipe(
        catchError((error) => {
          console.warn('Unable to load Workflow list.', error);
          return of(null);
        }),
        finalize(() => {
          if (loadToken === this.workflowsLoadToken) {
            this.isLoadingWorkflows = false;
          }
        })
      )
      .subscribe((response: any) => {
        if (loadToken !== this.workflowsLoadToken) {
          return;
        }
        this.workflows = this.mapWorkflowItems(this.extractLookupItems(response));
        if (this.enableWorkflow) {
          this.prefillWorkflowFromCategoryOrDepartment();
        }
        this.cdr.detectChanges();
      });
  }

  private loadShareRecipientsByDepartment(): void {
    const preserveSelectedIds = [...(this.selectedShareRecipientIds || [])].filter(
      (id) => Number.isFinite(id) && id > 0
    );
    const departmentId = Number(
      this.selectedUploadDepartmentId ?? this.libraryDepartmentId
    );

    if (!Number.isFinite(departmentId) || departmentId <= 0) {
      this.shareRecipients = this.mergeShareRecipientOptions([], preserveSelectedIds);
      this.cdr.detectChanges();
      return;
    }

    const loadToken = ++this.shareRecipientsLoadToken;
    this.isLoadingShareRecipients = true;

    // Match document-page: response.data is the user array.
    this.userService.getUsersByDepartments({ departmentIds: [departmentId] }).subscribe({
      next: (response) => {
        if (loadToken !== this.shareRecipientsLoadToken) {
          return;
        }
        this.isLoadingShareRecipients = false;
        const items = Array.isArray(response?.data)
          ? response.data
          : this.extractLookupItems(response);
        const mapped = this.mapShareUserItems(items);
        this.shareRecipients = this.mergeShareRecipientOptions(mapped, preserveSelectedIds);
        this.selectedShareRecipientIds = preserveSelectedIds;
        this.cdr.detectChanges();
      },
      error: () => {
        if (loadToken !== this.shareRecipientsLoadToken) {
          return;
        }
        this.isLoadingShareRecipients = false;
        this.shareRecipients = this.mergeShareRecipientOptions([], preserveSelectedIds);
        this.selectedShareRecipientIds = preserveSelectedIds;
        this.cdr.detectChanges();
      },
    });
  }

  private mergeShareRecipientOptions(
    departmentUsers: Array<{ id: number; label: string }>,
    selectedIds: number[]
  ): Array<{ id: number; label: string }> {
    const merged = [...departmentUsers];
    const available = new Set(merged.map((user) => user.id));

    selectedIds.forEach((id) => {
      if (available.has(id)) {
        return;
      }
      const fromPending = this.pendingShareRecipients.find((user) => user.id === id);
      merged.push({
        id,
        label: fromPending?.label || `#${id}`,
      });
      available.add(id);
    });

    return merged;
  }

  private prefillWorkflowFromCategoryOrDepartment(): void {
    if (!this.enableWorkflow) {
      this.workflowId = null;
      return;
    }
    if (this.workflowId && this.workflows.some((w) => w.id === this.workflowId)) {
      return;
    }

    const categoryId = Number(this.selectedCategoryId);
    const departmentId = Number(this.selectedUploadDepartmentId);

    const byCategory =
      Number.isFinite(categoryId) && categoryId > 0
        ? this.workflows.find((workflow) =>
            (workflow.categoryIds || []).some((id) => id === categoryId)
          )
        : null;
    if (byCategory) {
      this.workflowId = byCategory.id;
      return;
    }

    const byDepartment =
      Number.isFinite(departmentId) && departmentId > 0
        ? this.workflows.find((workflow) => workflow.departmentId === departmentId)
        : null;
    this.workflowId = byDepartment?.id ?? null;
  }

  private findLookupTypeId(items: any[], nameEn: string): number | null {
    const target = nameEn.trim().toLowerCase();
    const arabicSigningAuthority = 'جهة التوقيع';
    const normalized = (value: any) => String(value ?? '').trim().toLowerCase();
    const typeIdOf = (item: any) =>
      Number(item?.id ?? item?.Id ?? item?.lookupTypeId ?? item?.LookupTypeId);

    // Exact match (same as document-page), plus Arabic label.
    const exact = items.find((item) => {
      const en = normalized(item.nameEn ?? item.NameEn);
      const ar = String(item.nameAr ?? item.NameAr ?? '').trim();
      return en === target || ar === arabicSigningAuthority || normalized(ar) === target;
    });
    const exactId = typeIdOf(exact);
    if (exactId > 0) {
      return exactId;
    }

    // Fallback: "Signing Authority" / "SigningAuthority" / localized variants.
    const compactTarget = target.replace(/\s+/g, '');
    const partial = items.find((item) => {
      const en = normalized(item.nameEn ?? item.NameEn);
      const ar = String(item.nameAr ?? item.NameAr ?? '').trim();
      const arNorm = normalized(ar);
      return (
        en.includes(target) ||
        en.replace(/\s+/g, '') === compactTarget ||
        (en.includes('signing') && en.includes('authority')) ||
        ar.includes(arabicSigningAuthority) ||
        arNorm.includes('signing')
      );
    });
    const id = typeIdOf(partial);
    return id > 0 ? id : null;
  }

  private mapLookupNameItems(items: any[]): Array<{ id: number; label: string }> {
    const list = Array.isArray(items) ? items : [];
    const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
    return list
      .map((item: any) => {
        const id = Number(item.id ?? item.Id ?? item.lookupItemId ?? item.LookupItemId);
        const nameEn = String(
          item.nameEn ??
            item.NameEn ??
            item.lookupItemNameEn ??
            item.LookupItemNameEn ??
            item.valueEn ??
            item.ValueEn ??
            item.name ??
            item.Name ??
            ''
        ).trim();
        const nameAr = String(
          item.nameAr ??
            item.NameAr ??
            item.lookupItemNameAr ??
            item.LookupItemNameAr ??
            item.valueAr ??
            item.ValueAr ??
            ''
        ).trim();
        return {
          id,
          label: useArabic ? nameAr || nameEn : nameEn || nameAr,
        };
      })
      .filter(
        (item, index, source) =>
          item.id > 0 &&
          !!item.label &&
          source.findIndex((entry) => entry.id === item.id) === index
      );
  }

  private mapWorkflowItems(
    items: any[]
  ): Array<{ id: number; label: string; departmentId?: number | null; categoryIds?: number[] }> {
    const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
    return items
      .map((item) => {
        const id = Number(item.workflowId ?? item.WorkflowId ?? item.id);
        const nameEn = (item.workflowNameEn ?? item.WorkflowNameEn ?? '').trim();
        const nameAr = (item.workflowNameAr ?? item.WorkflowNameAr ?? '').trim();
        const label = useArabic ? nameAr || nameEn : nameEn || nameAr;
        const departmentIdRaw = Number(
          item.departmentId ?? item.DepartmentId ?? item.ref_DepartmentId ?? 0
        );
        const departmentId =
          Number.isFinite(departmentIdRaw) && departmentIdRaw > 0 ? departmentIdRaw : null;
        const categoryIds = Array.isArray(item.categoryIds)
          ? item.categoryIds
              .map((cid: any) => Number(cid))
              .filter((n: number) => Number.isFinite(n) && n > 0)
          : Array.isArray(item.categories)
            ? item.categories
                .map((c: any) => Number(c?.categoryId ?? c?.category_Id ?? c?.id ?? c))
                .filter((n: number) => Number.isFinite(n) && n > 0)
            : [];
        return { id, label, departmentId, categoryIds };
      })
      .filter(
        (item, index, source) =>
          item.id > 0 &&
          !!item.label &&
          source.findIndex((entry) => entry.id === item.id) === index
      );
  }

  private mapShareUserItems(items: any[]): Array<{ id: number; label: string }> {
    const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
    const list = Array.isArray(items) ? items : [];
    const isFlagSet = (value: any) =>
      value === true || value === 1 || String(value).toLowerCase() === 'true';
    const cleanName = (value: any) => {
      const text = String(value ?? '').trim();
      return !text || text.toLowerCase() === 'null' ? '' : text;
    };

    return list
      .filter((item) => !isFlagSet(item?.isExternalUser) && !isFlagSet(item?.IsExternalUser))
      .filter(
        (item) => !isFlagSet(item?.isPendingInvitation) && !isFlagSet(item?.IsPendingInvitation)
      )
      .map((item) => {
        const id = Number(item.userId ?? item.UserId ?? item.id ?? item.Id);
        const firstNameEn = cleanName(item.firstNameEn ?? item.FirstNameEn);
        const lastNameEn = cleanName(item.lastNameEn ?? item.LastNameEn);
        const firstNameAr = cleanName(item.firstNameAr ?? item.FirstNameAr);
        const lastNameAr = cleanName(item.lastNameAr ?? item.LastNameAr);
        const firstName = useArabic
          ? firstNameAr || firstNameEn
          : firstNameEn || firstNameAr;
        const lastName = useArabic ? lastNameAr || lastNameEn : lastNameEn || lastNameAr;
        const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
        const email = cleanName(item.email ?? item.Email);
        // Same label strategy as getUsersByDepartment() in this component.
        const label = fullName || email || (id > 0 ? `User ${id}` : '');
        return { id, label };
      })
      .filter(
        (item, index, source) =>
          item.id > 0 &&
          !!item.label &&
          source.findIndex((entry) => entry.id === item.id) === index
      );
  }

  private resolveSigningAuthorityId(item: any): number | null {
    const id = Number(
      item?.signingAuthorityId ?? item?.SigningAuthorityId ?? item?.signingAuthority ?? item?.SigningAuthority
    );
    if (Number.isFinite(id) && id > 0) {
      if (
        this.signingAuthorities.length === 0 ||
        this.signingAuthorities.some((authority) => authority.id === id)
      ) {
        return id;
      }
    }
    const name = String(item?.signingAuthority || item?.SigningAuthority || '').trim();
    if (!name || /^\d+$/.test(name)) {
      return null;
    }
    return (
      this.signingAuthorities.find(
        (authority) => authority.label.toLowerCase() === name.toLowerCase()
      )?.id ?? null
    );
  }

  private extractShareUserIdsFromFile(file: any): number[] {
    const raw =
      file?.shareUserIds ??
      file?.ShareUserIds ??
      file?.sharedUserIds ??
      file?.SharedUserIds ??
      file?.recipientUserIds ??
      file?.RecipientUserIds;
    if (Array.isArray(raw)) {
      return raw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw
        .split(',')
        .map((id) => Number(id.trim()))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    return [];
  }

  private formatDisplayDate(value: Date): string {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = value.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private getSelectedSigningAuthorityLabel(): string {
    const selected = this.signingAuthorities.find(
      (authority) => authority.id === this.signingAuthorityId
    );
    return selected?.label || this.signingAuthority || '';
  }

  private getShareRecipientLabelsCsv(): string {
    if (!this.shareWithRecipients || !this.selectedShareRecipientIds.length) {
      return '';
    }
    return this.selectedShareRecipientIds
      .map((id) => this.shareRecipients.find((entry) => entry.id === id)?.label || `#${id}`)
      .filter(Boolean)
      .join(', ');
  }

  private getDocumentDetailShareUserIds(): number[] {
    if (!this.shareWithRecipients) {
      return [];
    }
    return (this.selectedShareRecipientIds || []).filter(
      (id) => Number.isFinite(id) && id > 0
    );
  }

  /** Appends document-page-aligned metadata fields to FormData builders. */
  private appendDocumentDetailFieldsToFormData(formData: FormData): void {
    const shareIds = this.getDocumentDetailShareUserIds();
    const recipientId = this.resolveReceipientId(shareIds);
    const signingAuthorityId =
      this.signingAuthorityId != null &&
      Number.isFinite(this.signingAuthorityId) &&
      this.signingAuthorityId > 0
        ? this.signingAuthorityId
        : null;

    // Backend expects Receipient / SigningAuthority as integer ids (not display names).
    formData.append('Receipient', recipientId != null ? String(recipientId) : '');
    if (recipientId != null) {
      formData.append('RecipientId', String(recipientId));
    }
    formData.append(
      'SigningAuthority',
      signingAuthorityId != null ? String(signingAuthorityId) : ''
    );
    formData.append('FileNumber', this.fileNumber || '');
    formData.append('DocumentNumber', this.documentNumber || '');
    formData.append('DocumentTypeId', this.documentTypeId || '');
    formData.append('SystemDate', new Date().toISOString());

    if (this.enableWorkflow && this.workflowId && this.isWorkflowAllowedForUpload) {
      formData.append('WorkflowId', String(this.workflowId));
      const selectedWorkflow = this.workflows.find((workflow) => workflow.id === this.workflowId);
      if (selectedWorkflow?.label) {
        formData.append('Workflow', selectedWorkflow.label);
      }
    }

    if (shareIds.length > 0) {
      shareIds.forEach((userId) => formData.append('ShareUserIds', String(userId)));
      formData.append('ShareUserIdsCsv', shareIds.join(','));
    }
  }

  /** First selected share recipient id, or a numeric legacy recipient value. */
  private resolveReceipientId(shareIds: number[] = this.getDocumentDetailShareUserIds()): number | null {
    if (shareIds.length > 0) {
      return shareIds[0];
    }
    const legacyId = Number(this.recipient);
    return Number.isFinite(legacyId) && legacyId > 0 ? legacyId : null;
  }

  /** Normalizes the various list-response envelopes returned by the lookup/list APIs. */
  private extractLookupItems(response: any): any[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    // DocumentType/getList returns { items: [...] } at the top level.
    const topItems = response?.items ?? response?.Items;
    if (Array.isArray(topItems)) {
      return topItems;
    }
    const data = response?.data ?? response?.Data;
    if (Array.isArray(data)) {
      return data;
    }
    if (data && typeof data === 'object') {
      const items = data.items ?? data.Items;
      if (Array.isArray(items)) {
        return items;
      }

      // Nested envelopes: { data: { data: [...] } } or { data: { data: { items: [...] } } }
      const nested = data.data ?? data.Data;
      if (Array.isArray(nested)) {
        return nested;
      }
      if (nested && typeof nested === 'object') {
        const nestedItems = nested.items ?? nested.Items;
        if (Array.isArray(nestedItems)) {
          return nestedItems;
        }
      }
    }
    return [];
  }

  private hasValidLocationSelection(): boolean {
    const locationPayload = this.resolveLocationPayload();
    if (!locationPayload) {
      return false;
    }
    return !!locationPayload.locationId || !!locationPayload.newLocation;
  }

  private resolveLocationPayload():
    | { locationId: number; newLocation: null }
    | { locationId: null; newLocation: string }
    | null {
    if (!this.selectedLocation) {
      return null;
    }

    if (typeof this.selectedLocation === 'string') {
      const name = this.selectedLocation.trim();
      return name ? { locationId: null, newLocation: name } : null;
    }

    if (typeof this.selectedLocation === 'object') {
      const id = Number(this.selectedLocation.locationId);
      const name = String(
        this.selectedLocation.locationNameEn ??
        this.selectedLocation.locationNameAr ??
        ''
      ).trim();

      if (!this.selectedLocation.isNew && Number.isFinite(id) && id > 0) {
        return { locationId: id, newLocation: null };
      }
      if (name) {
        return { locationId: null, newLocation: name };
      }
    }

    return null;
  }

  private async prepareFolderStructureTargets(): Promise<void> {
    this.folderPathCache.clear();
    this.folderStructureCreatedFolderIds.clear();
    this.folderStructureRootFolderIds.clear();
    this.folderStructureCreatedFolderIdsForUpload = [];
    this.perFileFolderIds = [];
    const baseFolderId = this.getResolvedFolderId();
    const desiredRelativePathBySourceKey = new Map<string, string>();
    this.uploadItemsForDisplay
      .filter(item => item.type === 'folder')
      .forEach(item => {
        item.files.forEach(rawFile => {
          const file = this.ensureUploadFileMetadata(rawFile);
          const sourceKey = this.getSourceKey(file);
          const originalRelativePath = this.getOriginalRelativePath(file);
          const originalParts = originalRelativePath.split('/').filter(Boolean);
          if (originalParts.length <= 1) {
            return;
          }
          const desiredPath = [item.displayName, ...originalParts.slice(1)].join('/');
          desiredRelativePathBySourceKey.set(sourceKey, desiredPath);
        });
      });

    // Normalize selected folder file paths with the latest displayed folder names
    // so stale top-level names can never leak into folder-creation payloads.
    this.selectedFiles = this.selectedFiles.map(rawFile => {
      const file = this.ensureUploadFileMetadata(rawFile);
      const sourceKey = this.getSourceKey(file);
      const desiredPath = desiredRelativePathBySourceKey.get(sourceKey);
      if (!desiredPath) {
        return file;
      }
      const currentPath = this.getNormalizedRelativePath(file);
      if (currentPath === desiredPath) {
        return file;
      }
      return this.cloneFileForRename(file, file.name, desiredPath);
    });
    this.syncUploadManagerFilesWithSelection();

    for (const file of this.selectedFiles) {
      const enhancedFile = this.ensureUploadFileMetadata(file);
      const normalizedRelativePath = this.getNormalizedRelativePath(enhancedFile);
      const normalizedParts = normalizedRelativePath.split('/').filter(Boolean);
      const folderPath = normalizedParts.length > 1 ? normalizedParts.slice(0, -1).join('/') : '';
      const targetId = await this.getOrCreateRecursiveFolder(baseFolderId, folderPath);
      this.perFileFolderIds.push(targetId);
    }

    for (const emptyFolderPath of Array.from(this.pendingEmptyFolderRelativePaths)) {
      await this.getOrCreateRecursiveFolder(baseFolderId, emptyFolderPath);
    }

    this.folderStructureCreatedFolderIdsForUpload = Array.from(this.folderStructureCreatedFolderIds);
  }

  private async getOrCreateRecursiveFolder(baseFolderId: number, relativePath: string): Promise<number> {
    const parts = relativePath.split('/').filter(p => !!p);
    if (parts.length === 0) return baseFolderId;
    
    let currentParentId = baseFolderId;
    for (let index = 0; index < parts.length; index++) {
      const part = parts[index];
      const cacheKey = `${currentParentId}_${part}`;
      if (this.folderPathCache.has(cacheKey)) {
        currentParentId = this.folderPathCache.get(cacheKey)!;
        this.folderStructureCreatedFolderIds.add(currentParentId);
        if (index === 0) {
          this.folderStructureRootFolderIds.add(currentParentId);
        }
        continue;
      }
      
      const effectiveLibraryType = this.getUploadDestinationLibraryType();
      const destinationParentId = currentParentId > 0 ? currentParentId : 0;
      const payload = {
        parentId: destinationParentId > 0 ? destinationParentId : -1,
        folderNameEn: part,
        folderNameAr: part,
        // Defer metadata assignment until the structure is fully created so
        // descendants can inherit from the stamped root folder.
        userId: Number(localStorage.getItem('userId')),
        folderId: destinationParentId,
        // Keep payload parity with main file-manager create-folder flow.
        libraryType: effectiveLibraryType,
        departmentId: this.getUploadDestinationDepartmentId(destinationParentId) ?? -1,
        categoryId: this.selectedCategoryId ?? null,
        IsUploaded: true,
      };
      
      const res = await firstValueFrom(this.documentService.createFolder(payload));
      if (res.httpStatus === 200) {
        currentParentId = res.data.FolderId;
        this.folderStructureCreatedFolderIds.add(currentParentId);
        if (index === 0) {
          this.folderStructureRootFolderIds.add(currentParentId);
        }
        await this.createShareEntryForFolderIfNeeded(currentParentId);
        this.folderPathCache.set(cacheKey, currentParentId);
      } else {
        throw new Error(res?.errorMessage || `Could not create subfolder '${part}'.`);
      }
    }
    return currentParentId;
  }

  private async createShareEntryForFolderIfNeeded(folderId: number): Promise<void> {
    if (!this.markAsSecure) {
      return;
    }

    const payload = {
      fileId: 0,
      folderId,
      userIds: this.getShareUserIds(),
      emails: this.getShareEmails(),
      permissions: this.getMergedSharePermissions(true),
      expireOn: this.getShareExpireOn()
    };

    const response = await firstValueFrom(this.documentService.shareFolder(payload));
    if (!response?.status) {
      throw new Error(response?.errorMessage || 'Unable to create share entry for folder.');
    }
  }

  /**
   * Get the button text based on current state
   */
  getUploadButtonText(): string {
    if (this.editMode) {
      return this.translateService.instant('MEDIALIBRARY.editUploadUpdate');
    }

    if (this.useResumableUpload) {
      return this.translateService.instant('MEDIALIBRARY.uploadStartUpload');
    }

    return this.translateService.instant('MEDIALIBRARY.uploadSave');
  }

  async uploadMedia(): Promise<void> {
    if (this.editMode) {
      if (this.folderMetadataEditMode || this.editEntityType === 'folder') {
        this.updateFolderMetadata();
        return;
      }
      this.updateFile();
      return;
    }
    if (!this.selectedFiles.length) {
      if (this.pendingEmptyFolderSelection) {
        this.showEmptyFolderSelectionDialog();
      } else {
        this.messageService.add({
          key: 'custom',
          severity: 'warn',
          summary: 'No Files Selected',
          detail: 'Please select at least one file to upload.',
          life: 5000
        });
      }
      return;
    }

    // Category is optional — keep check commented for easy restore.
    // if (!this.selectedCategoryId) {
    //   this.messageService.add({
    //     key: 'custom',
    //     severity: 'warn',
    //     summary: 'Required Details Missing',
    //     detail: 'Select a category before saving.',
    //     life: 5000
    //   });
    //   return;
    // }

    if (this.isUploadActionInProgress) {
      return;
    }

    const confirmed = await this.confirmationService.confirmAsync({
      header: 'Confirm Upload',
      message: 'Would you like to proceed?',
      icon: 'pi pi-cloud-upload',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      acceptButtonStyleClass: 'p-button-primary',
    });

    if (!confirmed) {
      return;
    }

    this.isUploadActionInProgress = true;
    try {
      this.syncUploadManagerFilesWithSelection();
      if (this.hasFolderStructureSelection()) {
        await this.prepareFolderStructureTargets();
        await this.applyMetadataToFolderStructureRoots();
      } else {
        this.perFileFolderIds = [];
        this.folderStructureCreatedFolderIds.clear();
        this.folderStructureRootFolderIds.clear();
        this.folderStructureCreatedFolderIdsForUpload = [];
      }
      await this.applyMetadataToNewlyCreatedUploadFolderIfNeeded();
      this.proceedWithUpload();
    } catch (error: any) {
      this.isUploadActionInProgress = false;
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Folder Processing Error',
        detail: error?.error?.errorMessage || error?.message || 'Unable to prepare folder structure for upload.',
        life: 8000
      });
    }
  }

  private proceedWithUpload(): void {
    // Check storage quota before upload
    const quotaCheck = this.checkStorageQuota();
    if (!quotaCheck.isValid) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Storage Quota Exceeded',
        detail: quotaCheck.message,
        life: 5000
      });
      return; // Prevent upload
    }

    // Requirement: close upload dialog 2s after click while APIs continue in background.
    this.scheduleUploadAutoCloseInBackground();

    // Handle resumable upload cases
    if (this.useResumableUpload) {
      // Case 1: Uploads are completed - metadata already saved during initialize, no action needed
      // (Removed submit functionality as metadata is submitted during initialize)
      if (this.completedResumableSessions.length > 0) {
        // All files are already completed and stored in File table via complete API
        // No need to submit metadata again
        return;
      }

      // Case 2: Uploads are queued but not started - start uploads (Start Upload button)
      const hasActiveUploads = this.globalUploadQueue.getActiveUploads().length > 0;
      if (!hasActiveUploads && this.resumableUploadComponent) {
        // Save metadata before starting upload
        const metadata: UploadDialogState['autoPopulate'] = {
          title: this.title,
          selectedEvent: this.selectedEvent,
          date: this.date,
          ...this.pendingMetadataExtras(),
          tags: this.tags,
          markAsSecure: this.markAsSecure,
          shareType: this.shareType,
          shareUserIds: this.getShareUserIds(),
          shareEmails: this.getShareEmails(),
          shareExpireDate: this.shareExpireDate,
          sharePermissionLevel: this.sharePermissionLevel
        };
        this.uploadDialogService.savePendingMetadata(metadata);
        console.log('[DIALOG] Saved pending metadata before resumable upload:', metadata);

        // Start the uploads
        this.resumableUploadComponent.startUploads();
        this.isUploadActionInProgress = true;
        this.messageService.add({
          key: 'custom',
          severity: 'info',
          summary: 'Upload Started',
          detail: 'Your files are being uploaded. You can close this dialog and the upload will continue in the background.',
          life: 5000
        });
        return;
      }

      // Case 3: Uploads are in progress - button should be disabled, but if somehow called, do nothing
      if (hasActiveUploads) {
        this.messageService.add({
          key: 'custom',
          severity: 'info',
          summary: 'Upload in Progress',
          detail: 'Please wait for the current upload to complete.',
          life: 3000
        });
        return;
      }
    }

    // Check if files exceed threshold - use resumable upload (if enabled)
    if (this.RESUMABLE_UPLOAD_ENABLED && !this.useResumableUpload) {
      const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalSize > this.RESUMABLE_UPLOAD_THRESHOLD) {
        // Enable resumable upload mode
        this.useResumableUpload = true;
        this.showResumableUpload = true;
        
        // Save metadata before starting upload
        const metadata: UploadDialogState['autoPopulate'] = {
          title: this.title,
          selectedEvent: this.selectedEvent,
          date: this.date,
          ...this.pendingMetadataExtras(),
          tags: this.tags,
          markAsSecure: this.markAsSecure,
          shareType: this.shareType,
          shareUserIds: this.getShareUserIds(),
          shareEmails: this.getShareEmails(),
          shareExpireDate: this.shareExpireDate,
          sharePermissionLevel: this.sharePermissionLevel
        };
        this.uploadDialogService.savePendingMetadata(metadata);
        console.log('[DIALOG] Saved pending metadata before resumable upload:', metadata);

        // Start resumable uploads manually when user clicks Upload button
        if (this.resumableUploadComponent) {
          this.resumableUploadComponent.startUploads();
          this.isUploadActionInProgress = true;
          this.messageService.add({
            key: 'custom',
            severity: 'info',
            summary: 'Upload Started',
            detail: 'Your files are being uploaded. You can close this dialog and the upload will continue in the background.',
            life: 5000
          });
          return;
        }
        
        // Show message about large file size
        this.messageService.add({
          key: 'custom',
          severity: 'warn',
          summary: 'Large File Detected',
          detail: 'This file is large. To avoid upload failure, please use a fast and stable internet connection.',
          life: 8000
        });
        
        // Also show info about resumable upload
        this.messageService.add({
          key: 'custom',
          severity: 'info',
          summary: '',
          detail: `Files exceed ${this.formatBytes(this.RESUMABLE_UPLOAD_THRESHOLD)}. Resumable upload will start automatically.`,
          life: 5000
        });
        return; // Don't proceed with regular upload, resumable upload will handle it
      }
    }

    const fileCreationDate = this.resolveFileCreationDateIso();

    if (!this.useResumableUpload && this.perFileFolderIds.length > 0 && this.perFileFolderIds.length === this.selectedFiles.length) {
      void this.uploadFilesByResolvedFolders(fileCreationDate);
      return;
    }

    if (!this.useResumableUpload) {
      this.perFileFolderIds = this.selectedFiles.map(() => this.getResolvedFolderId());
      void this.uploadFilesByResolvedFolders(fileCreationDate);
      return;
    }

    const formData = new FormData();
    formData.append('FileName', this.selectedFiles[0].name);

    const folderId = this.getUploadDestinationFolderId();
    formData.append('FolderId', folderId.toString());
    this.appendUploadCategoryIdToFormData(formData);
    if (this.selectedFileType) {
      formData.append('SelectedFileType', this.selectedFileType);
    }

    this.appendLibraryContextToFormData(formData, folderId);

    // formData.append('TitleEn', this.title);
    // formData.append('TitleAr', '');
if(this.envConfig.isEventsSecExpanded){
    // Handle selected event (existing or new)
    let eventId = '0';
    let eventName = '';
    
    if (this.selectedEvent) {
      if (typeof this.selectedEvent === 'string') {
        eventName = this.selectedEvent;
      } else if (this.selectedEvent.isNew) {
        eventName = this.selectedEvent.eventNameEn;
      } else {
        eventId = this.selectedEvent.eventId?.toString() || '0';
        eventName = this.selectedEvent.eventNameEn || '';
      }
    }

    if (eventId !== '0') {
      formData.append('EventId', eventId);
    }
    formData.append('NewEvent', eventName);
    this.appendEventDatesToFormData(formData);
  }
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      this.selectedFiles.forEach(file => {
        formData.append('Files', file);
      });
    }

    formData.append('IsMarkSecure', this.markAsSecure ? "true" : "false");
    this.appendDocumentDetailFieldsToFormData(formData);
    this.appendUploadDepartmentIdToFormData(formData);

    formData.append('FileCreationDate', fileCreationDate);
    if(this.envConfig.isLocationSecExpanded){
    // Handle Location
    const locationPayload = this.resolveLocationPayload();
    if (locationPayload?.locationId) {
      formData.append('LocationId', locationPayload.locationId.toString());
    } else if (locationPayload?.newLocation) {
      formData.append('NewLocation', locationPayload.newLocation);
    }
  }
    this.appendShareDetailsToFormData(formData);

    // Separate existing tags from new tags
    const existingTagIds: number[] = [];
    const newTags: string[] = [];

    this.tags.forEach((tagInput: any) => {
      const directTagId = Number(tagInput?.tagId ?? tagInput?.TagId ?? 0);
      if (Number.isFinite(directTagId) && directTagId > 0) {
        existingTagIds.push(directTagId);
        return;
      }

      // Handle both string and object inputs from autocomplete
      const tagName = typeof tagInput === 'string' ? tagInput : tagInput.tag;

      // Check if this tag exists in availableTags
      const existingTag = this.availableTags.find(
        (availableTag) => availableTag.tag === tagName
      );

      if (existingTag && existingTag.tagId) {
        // It's an existing tag - use tagId
        existingTagIds.push(existingTag.tagId);
      } else {
        // It's a new tag - add to NewTags
        newTags.push(tagName);
      }
    });

    // Append existing tag IDs
    if (existingTagIds.length > 0) {
      existingTagIds.forEach((id) => formData.append('TagIds', id.toString()));
    }

    // Append new tags
    console.log('NewTags to be sent:', newTags);
    if (newTags.length > 0) {
      newTags.forEach((tag) => formData.append('NewTags', tag));
    }

    this.logFormDataEntries(formData, 'uploadFile(single)');
    this.fileService.uploadFile(formData).subscribe({
      next: (response) => {
        console.log('Update response:', response);

        // Check if backend indicates resumable upload is required
        if (response.data && (response.data as any).RequiresResumableUpload) {
          console.log('Backend requires resumable upload');
          this.useResumableUpload = true;
          this.showResumableUpload = true;
          this.messageService.add({
            key: 'custom',
            severity: 'info',
            summary: '',
            detail: 'Files exceed 1GB. Switching to resumable upload mode.'
          });
          this.isUploadActionInProgress = false;
          return;
        }

        // Check for success response (either httpStatus 200 or status true)
        if (response.httpStatus === 200 || response.status === true) {
          // Check if response has partial success/failure details
          const responseData = response.data as any;
          
          // Check for partial success/failure indicators (check if properties exist)
          const hasSuccessCount = responseData && ('successCount' in responseData || 'SuccessCount' in responseData);
          const hasFailedCount = responseData && ('failedCount' in responseData || 'FailedCount' in responseData);
          
          if (hasSuccessCount || hasFailedCount) {
            const successCount = responseData.successCount || responseData.SuccessCount || 0;
            const failedCount = responseData.failedCount || responseData.FailedCount || 0;
            const failedFiles = responseData.failedFiles || responseData.FailedFiles || [];
            
            // Show success toast if any files succeeded
            if (successCount > 0) {
              const successMessage = this.buildUploadSuccessMessage(successCount);
              this.messageService.add({ 
                key: 'custom', 
                severity: 'success', 
                summary: 'Upload Successful', 
                detail: successMessage,
                life: 5000
              });
            }
            
            // Show error toast for failed files (separate toast)
            if (failedCount > 0 && failedFiles.length > 0) {
              // Extract filenames from error messages
              const failedFileNames = failedFiles.map((f: string) => {
                const match = f.match(/^([^:]+):/);
                return match ? match[1] : f;
              });
              
              // Build meaningful error message
              const fileList = failedFileNames.length === 1 
                ? failedFileNames[0]
                : failedFileNames.join(', ');

              // Extract error details (use first error as representative if multiple)
              const firstError = failedFiles[0];
              const errorDetail = firstError.includes(':') 
                ? firstError.split(':').slice(1).join(':').trim() 
                : firstError;
              
              const errorMessage = failedCount === 1
                ? `${fileList}: ${errorDetail}`
                : `The following ${failedCount} files failed to upload: ${fileList}. ${errorDetail}`;
              
              this.messageService.add({ 
                key: 'custom', 
                severity: 'error', 
                summary: 'Upload Failed', 
                detail: errorMessage,
                life: 8000
              });
            }
            
            // If all files failed, close dialog after showing error
            if (successCount === 0) {
              this.isUploadActionInProgress = false;
              setTimeout(() => {
                this.visible = false;
                this.resetForm();
                this.uploadDialogService.closeDialog();
              }, 2000);
              return;
            }
            
            // For partial success, delay close so toasts are visible (stay on current route — parent refreshes list)
            const folderId = this.getUploadDestinationFolderId();

            this.completeUploadAndClose(folderId, 2000);
            return; 
          } else {
            // No partial results, show generic success message
            this.messageService.add({ 
              key: 'custom', 
              severity: 'success', 
              summary: 'Upload Successful', 
              detail: 'File uploaded successfully.' 
            });
          }
          
          const folderId = this.getUploadDestinationFolderId();
          this.completeUploadAndClose(folderId, 2000);
        } else {
          this.messageService.add({ key: 'custom', severity: 'error', summary: 'Upload Error', detail: response.errorMessage });
          this.isUploadActionInProgress = false;
        }
      },
      error: (error) => {
        console.error('Update error:', error);
        // Check if error indicates resumable upload is required
        if (error?.error?.data?.RequiresResumableUpload || error?.error?.RequiresResumableUpload) {
          console.log('Backend requires resumable upload (from error)');
          this.useResumableUpload = true;
          this.showResumableUpload = true;
          this.messageService.add({
            key: 'custom',
            severity: 'info',
            summary: '',
            detail: 'Files exceed 1GB. Switching to resumable upload mode.'
          });
          this.isUploadActionInProgress = false;
        } else {
          // Display backend error message (e.g., file name conflict error)
          const errorMessage = error?.error?.errorMessage || error?.error?.message || error?.message || 'Error uploading file';
          this.messageService.add({ 
            key: 'custom', 
            severity: 'error', 
            summary: errorMessage.includes('File name conflict') ? 'File Name Conflict Error' : 'Upload Error',
            detail: errorMessage 
          });
          this.isUploadActionInProgress = false;
          setTimeout(() => {
            this.visible = false;
            this.resetForm();
            this.uploadDialogService.closeDialog();
          }, 2000);
        }
      },
    });
  }

  private buildUploadFormData(files: File[], folderId: number, fileCreationDate: string): FormData {
    const formData = new FormData();
    const inheritFolderStructureMetadata = this.folderStructureCreatedFolderIds.has(folderId);
    const shouldApplyFileMetadata =
      !inheritFolderStructureMetadata &&
      this.shouldApplyMetadataToUploadedItems(folderId);
    formData.append('FileName', files[0]?.name || '');
    formData.append('FolderId', folderId.toString());
    this.appendUploadCategoryIdToFormData(formData);
    if (this.selectedFileType) {
      formData.append('SelectedFileType', this.selectedFileType);
    }
    if(folderId == this.newlyCreatedFolderId){
      formData.append('IsNewFolder', 'true');
    }
    else{
      formData.append('IsNewFolder', 'false');
    }

    this.appendLibraryContextToFormData(formData, folderId);
    if(this.envConfig.isEventsSecExpanded){
    // Handle selected event (existing or new)
    let eventId = '0';
    let eventName = '';
    if (this.selectedEvent) {
      if (typeof this.selectedEvent === 'string') {
        eventName = this.selectedEvent;
      } else if (this.selectedEvent.isNew) {
        eventName = this.selectedEvent.eventNameEn;
      } else {
        eventId = this.selectedEvent.eventId?.toString() || '0';
        eventName = this.selectedEvent.eventNameEn || '';
      }
    }

    if (shouldApplyFileMetadata) {
      if (eventId !== '0') {
        formData.append('EventId', eventId);
      }
      formData.append('NewEvent', eventName);
      this.appendEventDatesToFormData(formData);
    }
  }
    files.forEach(file => formData.append('Files', file));

    formData.append('IsMarkSecure', this.markAsSecure ? 'true' : 'false');
    this.appendDocumentDetailFieldsToFormData(formData);
    this.appendUploadDepartmentIdToFormData(formData);
    formData.append('FileCreationDate', fileCreationDate);

       if(this.envConfig.isLocationSecExpanded){
    // Handle Location
    const locationPayload = this.resolveLocationPayload();
    if (shouldApplyFileMetadata) {
      if (locationPayload?.locationId) {
        formData.append('LocationId', locationPayload.locationId.toString());
      } else if (locationPayload?.newLocation) {
        formData.append('NewLocation', locationPayload.newLocation);
      }
    }
  }
    this.appendShareDetailsToFormData(formData);

    // Separate existing tags from new tags
    const existingTagIds: number[] = [];
    const newTags: string[] = [];
    this.tags.forEach((tagInput: any) => {
      const directTagId = Number(tagInput?.tagId ?? tagInput?.TagId ?? 0);
      if (Number.isFinite(directTagId) && directTagId > 0) {
        existingTagIds.push(directTagId);
        return;
      }

      const tagName = typeof tagInput === 'string' ? tagInput : tagInput.tag;
      const existingTag = this.availableTags.find((availableTag) => availableTag.tag === tagName);
      if (existingTag && existingTag.tagId) {
        existingTagIds.push(existingTag.tagId);
      } else {
        newTags.push(tagName);
      }
    });

    if (shouldApplyFileMetadata) {
      existingTagIds.forEach((id) => formData.append('TagIds', id.toString()));
      newTags.forEach((tag) => formData.append('NewTags', tag));
    }
    // this.appendRenamedItemsMapping(formData, files);
    return formData;
  }

  private appendRenamedItemsMapping(formData: FormData, files: File[]): void {
    const mapping = files.map((file, index) => ({
      index,
      sourceKey: this.getSourceKey(file),
      originalRelativePath: this.getOriginalRelativePath(file),
      updatedRelativePath: this.getNormalizedRelativePath(file),
      originalFileName: (file as any).__originalName || file.name,
      updatedFileName: file.name
    }));
    formData.append('RenamedItemsMap', JSON.stringify(mapping));
  }

  private completeUploadAndClose(folderId: number, delayMs: number = 2000): void {
    this.isUploadActionInProgress = false;
    if (this.uploadAutoCloseTimer) {
      clearTimeout(this.uploadAutoCloseTimer);
      this.uploadAutoCloseTimer = null;
    }
    this.visible = false;
    this.uploadDialogService.closeDialog();
    this.emitUploadSuccess(folderId);
    setTimeout(() => {
      this.resetForm();
      if(this.envConfig.isEventsSecExpanded){
        this.sidebarService.refreshEvents().subscribe();
      }
      this.sidebarService.refreshTags().subscribe();
      if(this.envConfig.isLocationSecExpanded){
        this.loadLocations();
      }
    }, delayMs);
  }

  private scheduleUploadAutoCloseInBackground(): void {
    if (this.uploadAutoCloseTimer) {
      return;
    }
    this.uploadAutoCloseTimer = setTimeout(() => {
      this.isUploadActionInProgress = false;
      if (this.visible) {
        this.visible = false;
        this.uploadDialogService.closeDialog();
      }
      this.uploadAutoCloseTimer = null;
    }, 2000);
  }

  private async uploadFilesByResolvedFolders(fileCreationDate: string): Promise<void> {
    const uploadQueue = this.selectedFiles.map((file, index) => ({
      file,
      folderId: this.perFileFolderIds[index] ?? this.getResolvedFolderId()
    }));

    this.setFilesState(uploadQueue.map(item => item.file), 'pending');
    const trackerMap = this.ensureNormalUploadTracking(
      uploadQueue.map(item => item.file),
      uploadQueue.map(item => item.folderId)
    );
    let successCount = 0;
    const failedFiles: string[] = [];

    for (const uploadItem of uploadQueue) {
      const { file, folderId } = uploadItem;
      this.setFilesState([file], 'uploading');
      this.setNormalUploadTrackerStatus([file], 'uploading', trackerMap);
      const formData = this.buildUploadFormData([file], folderId, fileCreationDate);
      this.logFormDataEntries(formData, `uploadFile(${file.name})`);
      try {
        const response = await firstValueFrom(this.fileService.uploadFile(formData));
        const responseData = response?.data || {};
        const responseFailedFiles: string[] = responseData.failedFiles || responseData.FailedFiles || [];
        const isSuccessful = (response?.httpStatus === 200 || response?.status === true) && responseFailedFiles.length === 0;
        if (isSuccessful) {
          successCount += 1;
          this.setFilesState([file], 'uploaded');
          this.setNormalUploadTrackerStatus([file], 'completed', trackerMap);
        } else {
          const errorMessage = response?.errorMessage || responseFailedFiles[0] || 'Upload failed';
          failedFiles.push(`${file.name}: ${errorMessage}`);
          this.setFilesState([file], 'failed');
          this.setNormalUploadTrackerStatus([file], 'failed', trackerMap);
        }
      } catch (error: any) {
        const errorMessage = error?.error?.errorMessage || error?.error?.message || error?.message || 'Upload failed';
        failedFiles.push(`${file.name}: ${errorMessage}`);
        this.setFilesState([file], 'failed');
        this.setNormalUploadTrackerStatus([file], 'failed', trackerMap);
      }
    }

    if (successCount > 0) {
      const successMessage = this.buildUploadSuccessMessage(successCount);
      this.messageService.add({
        key: 'custom',
        severity: 'success',
        summary: 'Upload Successful',
        detail: successMessage,
        life: 5000
      });
    }

    if (failedFiles.length > 0) {
      const firstError = failedFiles[0];
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Upload Failed',
        detail: failedFiles.length === 1
          ? firstError
          : `The following ${failedFiles.length} files failed to upload: ${failedFiles.map(f => f.split(':')[0]).join(', ')}.`,
        life: 8000
      });
    }

    if (successCount === 0) {
      this.isUploadActionInProgress = false;
      setTimeout(() => {
        this.visible = false;
        this.resetForm();
        this.uploadDialogService.closeDialog();
      }, 2000);
      return;
    }

    const folderId = this.getUploadDestinationFolderId();
    this.completeUploadAndClose(folderId, 2000);
  }

  updateFile(): void {
    void this.submitFileMetadataUpdate(this.finalizeEditFileName(this.editedFileName || ''));
  }

  private async submitFileMetadataUpdate(
    fileName: string,
    options: { skipPreDuplicateCheck?: boolean } = {}
  ): Promise<void> {
    fileName = this.finalizeEditFileName(fileName);
    if (this.resubmitWorkflowOnUpdate && !this.resubmitComment.trim()) {
      this.messageService.add({
        key: 'custom',
        severity: 'warn',
        summary: '',
        detail: this.translateService.instant('TASKS.CommentRequired'),
        life: 5000,
      });
      return;
    }

    if (fileName && fileName !== (this.editedFileName || '').trim()) {
      this.editedFileName = fileName;
    }

    if (!fileName) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'File name is required',
      });
      return;
    }

    if (!options.skipPreDuplicateCheck && this.shouldCheckEditFileNameDuplicate(fileName)) {
      const resolvedName = await this.resolveEditFileNameBeforeUpdate(fileName);
      if (!resolvedName) {
        return;
      }
      if (resolvedName !== fileName) {
        this.editedFileName = resolvedName;
        fileName = resolvedName;
      }
    }

    const formData = this.buildUpdateFileFormData(fileName);
    
  this.spinner.show();
    this.logFormDataEntries(formData, 'updateFile');
    this.fileService.updateFile(formData).subscribe({
      next: async (response) => {
        if (response.httpStatus === 200) {
          const folderId = this.getUploadDestinationFolderId();
          const updatedFileId = Number(this.editedFile?.fileId ?? this.editedFile?.FileId ?? 0);
          if (this.resubmitWorkflowOnUpdate && updatedFileId > 0) {
            this.resubmitWorkflowAfterFileUpdate(updatedFileId, folderId);
            return;
          }

          this.messageService.add({ key: 'custom', severity: 'success', summary: '', detail: 'File updated successfully' });
          this.emitUploadSuccess(folderId);
          this.resetForm();
          this.uploadDialogService.closeDialog();
          setTimeout(() => {
            if(this.envConfig.isEventsSecExpanded){
               this.sidebarService.refreshEvents().subscribe();
            }
            this.sidebarService.refreshTags().subscribe();
            if(this.envConfig.isLocationSecExpanded){
               this.loadLocations();
            }
          }, 200);
          return;
        }

        if (this.isFileRenameConflictPayload(response)) {
          await this.handleEditFileRenameConflict(fileName, response);
          return;
        }
        this.spinner.hide();
        this.messageService.add({ key: 'custom', severity: 'error', summary: '', detail: response.errorMessage });
      },
      error: async (error) => {
        console.error('Update error:', error);
        if (this.isFileRenameConflictPayload(error)) {
          await this.handleEditFileRenameConflict(fileName, error);
          return;
        }
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: '',
          detail: error?.error?.errorMessage || 'Error updating file',
        });
      },
    });
  }

  private resubmitWorkflowAfterFileUpdate(fileId: number, folderId: number): void {
    this.workflowService
      .processWorkflowAction({
        fileId,
        status: 6,
        comment: this.resubmitComment.trim(),
      })
      .subscribe({
        next: (response) => {
          if (response?.status === false) {
            this.spinner.hide();
            this.messageService.add({
              key: 'custom',
              severity: 'error',
              summary: '',
              detail: response?.errorMessage || 'File updated, but workflow resubmit failed.',
            });
            return;
          }

          this.messageService.add({ key: 'custom', severity: 'success', summary: '', detail: 'File updated successfully' });
          this.emitUploadSuccess(folderId);
          this.resetForm();
          this.uploadDialogService.closeDialog();
          setTimeout(() => {
            if(this.envConfig.isEventsSecExpanded){
               this.sidebarService.refreshEvents().subscribe();
            }
            this.sidebarService.refreshTags().subscribe();
            if(this.envConfig.isLocationSecExpanded){
               this.loadLocations();
            }
          }, 200);
        },
        error: (error) => {
          this.spinner.hide();
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: '',
            detail:
              error?.error?.errorMessage ||
              error?.message ||
              'File updated, but workflow resubmit failed.',
          });
        },
      });
  }

  private buildUpdateFileFormData(fileName: string): FormData {
    const formData = new FormData();

    formData.append('FileId', this.editedFile.fileId.toString());
    const folderId = this.getUploadDestinationFolderId();
    formData.append('FolderId', folderId.toString());
    formData.append('FileName', fileName);
    formData.append('IsUploaded', 'true');

    const requestTitle = this.getRequestTitle();
    formData.append('TitleEn', requestTitle);
    formData.append('TitleAr', requestTitle);
    formData.append('RenameOnly', (false).toString());
    if(this.envConfig.isEventsSecExpanded){
      let eventId = '0';
      let eventName = '';
      if (this.selectedEvent) {
        if (typeof this.selectedEvent === 'string') {
          eventName = this.selectedEvent;
        } else if (this.selectedEvent.isNew) {
          eventName = this.selectedEvent.eventNameEn;
        } else {
          eventId = this.selectedEvent.eventId?.toString() || '0';
          eventName = this.selectedEvent.eventNameEn || '';
        }
      }

      if (eventId !== '0') {
        formData.append('EventId', eventId);
      }
      formData.append('NewEvent', eventName);
      this.appendEventDatesToFormData(formData);
    }

    const existingTagIds: number[] = [];
    const newTags: string[] = [];

    this.tags.forEach((tagInput: any) => {
      const directTagId = Number(tagInput?.tagId ?? tagInput?.TagId ?? 0);
      if (Number.isFinite(directTagId) && directTagId > 0) {
        existingTagIds.push(directTagId);
        return;
      }

      const tagName = typeof tagInput === 'string' ? tagInput : tagInput.tag;
      const existingTag = this.availableTags.find(
        (availableTag) => availableTag.tag === tagName
      );

      if (existingTag && existingTag.tagId) {
        existingTagIds.push(existingTag.tagId);
      } else {
        newTags.push(tagName);
      }
    });

    if (existingTagIds.length > 0) {
      existingTagIds.forEach((id) => formData.append('TagIds', id.toString()));
    }

    if (newTags.length > 0) {
      newTags.forEach((tag) => formData.append('NewTags', tag));
    }
    if(this.envConfig.isLocationSecExpanded){
      const locationPayload = this.resolveLocationPayload();
      if (locationPayload?.locationId) {
        formData.append('LocationId', locationPayload.locationId.toString());
      } else if (locationPayload?.newLocation) {
        formData.append('NewLocation', locationPayload.newLocation);
      }     
    }
    formData.append('FileCreationDate', this.resolveFileCreationDateIso());
    formData.append('IsMarkSecure', this.markAsSecure ? 'true' : 'false');
    this.appendDocumentDetailFieldsToFormData(formData);
    this.appendUploadDepartmentIdToFormData(formData);
    this.appendUploadCategoryIdToFormData(formData);
    if (this.selectedFileType) {
      formData.append('SelectedFileType', this.selectedFileType);
    }
    return formData;
  }

  private getOriginalEditedFileName(): string {
    if (this.initialEditStateSnapshot) {
      try {
        const state = JSON.parse(this.initialEditStateSnapshot);
        if (state?.mode === 'file' && state.fileName) {
          return String(state.fileName).trim();
        }
      } catch {
        // Fall through to edited file metadata.
      }
    }

    return (
      this.editedFile?.fileName ||
      this.editedFile?.FileName ||
      this.editedFile?.displayName ||
      this.editedFile?.titleEn ||
      ''
    ).trim();
  }

  private shouldCheckEditFileNameDuplicate(fileName: string): boolean {
    const originalName = this.getOriginalEditedFileName();
    return fileName.toLowerCase() !== originalName.toLowerCase();
  }

  private async resolveEditFileNameBeforeUpdate(fileName: string): Promise<string | null> {
    const folderId = this.getUploadDestinationFolderId();
    const fileId = Number(this.editedFile?.fileId || this.fileId || 0);
    const payload = {
      folderId,
      fileNames: [fileName],
      folderNames: [] as string[],
      excludedFileId: fileId > 0 ? fileId : undefined,
    };

    try {
      const response = await firstValueFrom(
        this.fileService.checkDuplicateFileNames(payload).pipe(
          timeout(8000),
          catchError(() => of(null))
        )
      );

      if (!response) {
        return fileName;
      }

      const duplicates = this.normalizeDuplicateNames(response);
      const isDuplicate = duplicates.duplicateFiles.some(
        (name) => name.toLowerCase() === fileName.toLowerCase()
      );
      if (!isDuplicate) {
        return fileName;
      }

      const occupied = new Set(duplicates.existingFiles.map((name) => name.toLowerCase()));
      const suggestedName = this.getNextAvailableFileName(fileName, occupied);
      const confirmed = await this.confirmEditFileAutoRename(fileName, suggestedName);
      return confirmed ? suggestedName : null;
    } catch {
      return fileName;
    }
  }

  private async handleEditFileRenameConflict(
    attemptedName: string,
    responseOrError: any
  ): Promise<void> {
    const suggestedName =
      this.getFileRenameConflictSuggestedName(responseOrError) ||
      this.getNextAvailableFileName(attemptedName, new Set());
    const confirmed = await this.confirmEditFileAutoRename(attemptedName, suggestedName);
    if (!confirmed) {
      return;
    }

    this.editedFileName = suggestedName;
    await this.submitFileMetadataUpdate(suggestedName, { skipPreDuplicateCheck: true });
  }

  private confirmEditFileAutoRename(currentName: string, suggestedName: string): Promise<boolean> {
    const suggestedSuffix = suggestedName ? `\n\nSuggested name: "${suggestedName}"` : '';
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: 'Duplicate Name',
        message:
          `A file named "${currentName}" already exists in this folder.` +
          `${suggestedSuffix}\n\nDo you want to rename the file?`,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Rename',
        rejectLabel: 'Cancel',
        acceptButtonStyleClass: 'p-button-primary',
        rejectButtonStyleClass: 'p-button-text p-button-secondary',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  private isFileRenameConflictPayload(payload: any): boolean {
    const rawStatus =
      payload?.httpStatus ??
      payload?.status ??
      payload?.error?.httpStatus ??
      payload?.error?.error?.httpStatus ??
      (typeof payload?.status === 'number' ? payload.status : undefined);
    const status = Number(rawStatus || 0);
    const message = String(
      payload?.errorMessage ||
      payload?.error?.errorMessage ||
      payload?.message ||
      payload?.error?.message ||
      ''
    ).toLowerCase();

    return (
      status === 409 ||
      message.includes('already exists in this folder') ||
      message.includes('already exists at the target location') ||
      message.includes('file with this name already exists')
    );
  }

  private getFileRenameConflictSuggestedName(payload: any): string {
    return String(
      payload?.data?.suggestedName ||
      payload?.error?.data?.suggestedName ||
      payload?.error?.error?.data?.suggestedName ||
      ''
    ).trim();
  }

// called from <doc-resumable-upload (uploadComplete)="onResumableUploadComplete($event)">
onResumableUploadComplete(result: { filePath: string; fileName: string; fileSize: number }[]): void {
  console.log('✅ UPLOAD COMPLETE - onResumableUploadComplete fired with:', result);
  this.completedResumableSessions = result;

  // Update completed sessions from upload manager to ensure all are captured
  this.updateCompletedSessionsFromManager();
  
  // Invalidate cache and refresh incomplete upload count when uploads complete
  this.resumableUploadService.invalidateIncompleteUploadsCache();
  this.resumableUploadService.getIncompleteUploads(true).subscribe();

  // Store files from upload manager for restoration
  const allUploads = this.uploadManager.getAllUploads();
  this.uploadedFiles = allUploads
    .filter(upload => upload.status === 'completed' && upload.file)
    .map(upload => upload.file);

  const allCompletedNow = this.uploadManager.areAllActiveUploadsCompleted();
  if (!allCompletedNow || this.resumableSuccessToastShown) {
    return;
  }

  // Show completion message once when all resumable uploads finish
  this.messageService.add({
    key: 'custom',
    severity: 'success',
    summary: 'Upload Completed',
    detail: this.buildResumableCompletionMessage(result),
    life: 8000
  });
  this.resumableSuccessToastShown = true;

  const activeUploadsCount = this.uploadManager.getActiveUploadsCount();
  const shouldAutoClose = activeUploadsCount === 0;

  if (shouldAutoClose && !this.resumableAutoCloseTriggered) {
    this.resumableAutoCloseTriggered = true;
    const folderId = this.getUploadDestinationFolderId();
    this.completeUploadAndClose(folderId, 2000);
  }
}

/**
 * Ensure files are visible in file picker area
 */
private ensureFilesVisible(): void {
  const allUploads = this.uploadManager.getAllUploads();
  allUploads.forEach(upload => {
    if (upload.status !== 'cancelled' && upload.file) {
      const isDuplicate = this.selectedFiles.some(existing => 
        existing.name === upload.file.name &&
        existing.size === upload.file.size &&
        existing.lastModified === upload.file.lastModified
      );
      if (!isDuplicate) {
        this.selectedFiles.push(upload.file);
      }
    }
  });
}

  loadFoldersForMove(): void {
    const payload = {
      parentId: 0,
      pageIndex: 0,
      pageSize: 0,
    };

    this.documentService.getFolderNames(payload).subscribe(
      (res: any) => {
        if (res && res.data && res.data.items) {
          const transformedFolders = res.data.items.map((folder: any) => {
            return {
              label: folder.folderNameEn || folder.FolderNameEn || folder.folderNameAr || folder.FolderNameAr || 'Unnamed Folder',
              value: folder.folderId || folder.FolderId || folder.id || folder.Id
            };
          });

          this.allFolders = [
            ...transformedFolders
          ];
        } else {
          this.allFolders = [];
        }
      },
      (error) => {
        this.allFolders = [];
      }
    );
  }








  saveResumableUploadMetadata(): void {
    if (!this.title || this.title.trim().length < 3) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'Please enter a title (minimum 3 characters)'
      });
      return;
    }

    // Update completed sessions from upload manager to ensure we have all filePaths
    this.updateCompletedSessionsFromManager();

    if (this.completedResumableSessions.length === 0) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'No completed uploads found. Please wait for uploads to complete.'
      });
      return;
    }

    this.proceedWithResumableUploadMetadata(this.getUploadDestinationFolderId());
  }

  private proceedWithResumableUploadMetadata(folderId: number): void {
    const shouldApplyFileMetadata = this.shouldApplyMetadataToUploadedItems(folderId);
    const tagIds = this.getTagIdsArray();
    const newTags = this.getNewTagsArray();

    // Prepare FormData with filePaths (files already uploaded to GCS)
    const formData = new FormData();
    // Use first file name or concatenate all file names
    const fileNames = this.completedResumableSessions.map(s => s.fileName).join(', ');
    formData.append('FileName', fileNames);
    formData.append('FolderId', folderId.toString());
    const requestTitle = this.getRequestTitle();
    formData.append('TitleEn', requestTitle);
    formData.append('TitleAr', '');
  if(this.envConfig.isEventsSecExpanded){
    // Handle selected event (existing or new)
    let eventId = '0';
    let eventName = '';
    if (this.selectedEvent) {
      if (typeof this.selectedEvent === 'string') {
        eventName = this.selectedEvent;
      } else if (this.selectedEvent.isNew) {
        eventName = this.selectedEvent.eventNameEn;
      } else {
        eventId = this.selectedEvent.eventId?.toString() || '0';
        eventName = this.selectedEvent.eventNameEn || '';
      }
    }

    if (shouldApplyFileMetadata) {
      if (eventId !== '0') {
        formData.append('EventId', eventId);
      }
      formData.append('NewEvent', eventName);
      this.appendEventDatesToFormData(formData);
    }
  }
    formData.append('IsMarkSecure', this.markAsSecure ? "true" : "false");
    formData.append('IsUploaded', 'true');
    this.appendDocumentDetailFieldsToFormData(formData);
    this.appendUploadDepartmentIdToFormData(formData);
    this.appendUploadCategoryIdToFormData(formData);
    if (this.selectedFileType) {
      formData.append('SelectedFileType', this.selectedFileType);
    }
    this.appendShareDetailsToFormData(formData);

    // Add filePaths (already uploaded to GCS) - one entry per file
    this.completedResumableSessions.forEach(session => {
      formData.append('FilePaths', session.filePath);
      formData.append('FileNames', session.fileName);
      formData.append('FileSizes', session.fileSize.toString());
    });


    //   Promise.all(updatePromises).then(() => {
    //   this.messageService.add({
    //     key: 'custom',
    //     severity: 'success',
    //     summary: '',
    //     detail: 'File metadata saved successfully!'
    //   });
    //   // Priority: newly created folder > selected event folder > current folder > 0 (root)
    //   const folderId = this.newlyCreatedFolderId || this.selectedEvent?.folderId || this.currentFolderId || 0;
    //   this.uploadSuccess.emit(folderId);
    //   this.visible = false;
    //   this.resetForm();
    // }).catch((error) => {
    //   console.error('Error saving metadata:', error);
    //   this.messageService.add({
    //     key: 'custom',
    //     severity: 'error',
    //     summary: '',
    //     detail: error?.error?.errorMessage || 'Error saving file metadata'
    //   });


    // Add tags
    if (shouldApplyFileMetadata) {
      if (tagIds.length > 0) {
        tagIds.forEach(tagId => formData.append('TagIds', tagId.toString()));
      }
      console.log('NewTags to be sent:', newTags);
      if (newTags.length > 0) {
        newTags.forEach(tag => formData.append('NewTags', tag));
      }
    }

    formData.append('FileCreationDate', this.resolveFileCreationDateIso());
    if(this.envConfig.isLocationSecExpanded){
    // Handle Location
      const locationPayload = this.resolveLocationPayload();
      if (shouldApplyFileMetadata) {
        if (locationPayload?.locationId) {
          formData.append('LocationId', locationPayload.locationId.toString());
        } else if (locationPayload?.newLocation) {
          formData.append('NewLocation', locationPayload.newLocation);
        }
      }
    }

    // Call uploadFile API with filePaths
    this.logFormDataEntries(formData, 'uploadFile(resumable-metadata)');
    this.fileService.uploadFile(formData).subscribe({
      next: (response) => {
        console.log('File records created successfully:', response);
        
        // Check if response has partial success/failure details
        const responseData = response.data as any;
        
        // Check for partial success/failure indicators (check if properties exist)
        const hasSuccessCount = responseData && ('successCount' in responseData || 'SuccessCount' in responseData);
        const hasFailedCount = responseData && ('failedCount' in responseData || 'FailedCount' in responseData);
        
        if (hasSuccessCount || hasFailedCount) {
          const successCount = responseData.successCount || responseData.SuccessCount || 0;
          const failedCount = responseData.failedCount || responseData.FailedCount || 0;
          const failedFiles = responseData.failedFiles || responseData.FailedFiles || [];
          
          // Show success toast if any files succeeded
          if (successCount > 0) {
            const successMessage = this.buildUploadSuccessMessage(successCount);
            this.messageService.add({ 
              key: 'custom', 
              severity: 'success', 
              summary: 'Upload Successful', 
              detail: successMessage,
              life: 5000
            });
          }
          
          // Show error toast for failed files (separate toast)
          if (failedCount > 0 && failedFiles.length > 0) {
            // Extract filenames from error messages
            const failedFileNames = failedFiles.map((f: string) => {
              const match = f.match(/^([^:]+):/);
              return match ? match[1] : f;
            });
            
            // Build meaningful error message
            const fileList = failedFileNames.length === 1 
              ? failedFileNames[0]
              : failedFileNames.join(', ');
            
            // Extract error details (use first error as representative if multiple)
            const firstError = failedFiles[0];
            const errorDetail = firstError.includes(':') 
              ? firstError.split(':').slice(1).join(':').trim() 
              : firstError;
            
            const errorMessage = failedCount === 1
              ? `${fileList}: ${errorDetail}`
              : `The following ${failedCount} files failed to upload: ${fileList}. ${errorDetail}`;
            
            this.messageService.add({ 
              key: 'custom', 
              severity: 'error', 
              summary: 'Upload Failed', 
              detail: errorMessage,
              life: 8000
            });
          }
          
          // If all files failed, don't close dialog or navigate
          if (successCount === 0) {
            return;
          }
          
          // For partial success, delay closing dialog to allow toasts to be displayed
          const folderId = this.getUploadDestinationFolderId();

          setTimeout(() => {
            this.uploadManager.clearAll();

            this.completedResumableSessions = [];
            this.uploadedFiles = [];

            this.emitUploadSuccess(folderId);
            
            // Close dialog and reset form
            this.visible = false;
            this.uploadDialogService.closeDialog();
            this.resetForm();
            
            setTimeout(() => {
              if(this.envConfig.isEventsSecExpanded){
                 this.sidebarService.refreshEvents().subscribe();
              }
              this.sidebarService.refreshTags().subscribe();
              if(this.envConfig.isLocationSecExpanded){
                this.loadLocations();
              }
            }, 200);
          }, 2000);
          return; // Exit early to prevent code below from executing
        } else {
          // No partial results, show generic success message
          this.messageService.add({
            key: 'custom',
            severity: 'success',
            summary: 'Upload Successful',
            detail: 'File metadata saved successfully!'
          });
        }
        
        // Clear all uploads from manager (this will hide the indicator)
        this.uploadManager.clearAll();
        
        // Clear completed sessions
        this.completedResumableSessions = [];
        this.uploadedFiles = [];
        
        const folderId = this.getUploadDestinationFolderId();
        this.isUploadActionInProgress = false;
        this.emitUploadSuccess(folderId);
        setTimeout(() => {
          this.visible = false;
          this.uploadDialogService.closeDialog();
          this.resetForm();
          if(this.envConfig.isEventsSecExpanded){
             this.sidebarService.refreshEvents().subscribe();
          }
          this.sidebarService.refreshTags().subscribe();
          if(this.envConfig.isLocationSecExpanded){
             this.loadLocations();
          }
        }, 2000);
      },
      error: (error) => {
        console.error('Error saving file metadata:', error);
        // Display backend error message (e.g., file name conflict error)
        const errorMessage = error?.error?.errorMessage || error?.error?.message || error?.message || 'Error saving file metadata';
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: errorMessage.includes('File name conflict') ? 'File Name Conflict Error' : 'Upload Error',
          detail: errorMessage
        });
      }
    });
  }

  onSlowInternetDetected(): void {
    // Show message when slow internet is detected during resumable upload
    this.messageService.add({
      key: 'custom',
      severity: 'warn',
      summary: 'Slow Internet Detected',
      detail: 'Your internet connection is slow. To avoid upload failure, please use a fast and stable internet connection.',
      life: 8000
    });
  }

  onResumableUploadError(error: { file: File; error: string }): void {
    console.error('Resumable upload error:', error.file?.name, error.error);
    if (!this.resumableUploadErrorToastShown) {
      this.resumableUploadErrorToastShown = true;
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Upload error',
        detail:
          'Some files could not be uploaded. Check the upload panel for per-file status; failed items will retry when the connection is stable.',
        life: 10000,
      });
    }
    this.isUploadActionInProgress = false;
  }

  onResumableUploadStarted(): void {
    this.resumableUploadErrorToastShown = false;
    this.isUploadActionInProgress = true;
  }

  onAllUploadsCompleted(completed: boolean): void {
    this.allUploadsCompleted = completed;
    console.log('[DIALOG] All uploads completed:', completed);
    
    // Invalidate incomplete uploads cache when uploads complete
    if (completed) {
      this.resumableUploadService.invalidateIncompleteUploadsCache();
      // Refresh count to update badge
      this.resumableUploadService.getIncompleteUploads(true).subscribe();
      this.isUploadActionInProgress = false;
    } else {
      this.resumableSuccessToastShown = false;
    }
  }

  /**
   * Handle file removal from upload progress list
   * Also remove from file picker (selectedFiles)
   */
  onFileRemovedFromUpload(fileInfo: { fileName: string; fileSize: number; lastModified: number }): void {
    // Find and remove the file from selectedFiles array
    const index = this.selectedFiles.findIndex(file => 
      file.name === fileInfo.fileName &&
      file.size === fileInfo.fileSize &&
      file.lastModified === fileInfo.lastModified
    );

    if (index !== -1) {
      this.selectedFiles.splice(index, 1);
      console.log('[DIALOG] Removed file from picker:', fileInfo.fileName);
      
      // Update upload mode if needed
      this.checkFileSizeAndToggleUploadMode();
      this.syncUploadMetadataState();
    }
  }

  private hasFormChanges(): boolean {
    if (this.title !== '' && this.title !== null) {
      return true;
    }
    if (this.selectedFiles !== null && this.selectedFiles?.length > 0) {
      return true;
    }
    if (this.selectedFileType || this.selectedCategoryId) {
      return true;
    }
    if (this.selectedEvent !== null && this.selectedEvent?.eventId) {
      return true;
    }
    if (
      this.date &&
      new Date(this.date).toDateString() !== new Date().toDateString()
    ) {
      return true;
    }
    if (this.getNormalizedEventDateRange()) {
      return true;
    }
    if (this.tags.length > 0) {
      return true;
    }
    if (this.markAsSecure !== false) {
      return true;
    }
    if (this.hasShareFieldChanges()) {
      return true;
    }
    return false;
  }

  closeDialog(): void {
    // Check if there are active resumable uploads
    const activeUploads = this.globalUploadQueue.getActiveUploads();
    const hasActiveUploads = activeUploads.length > 0 ||
      (this.showResumableUpload && this.completedResumableSessions.length === 0);
    const hasUnsavedChanges = this.editMode ? this.hasEditModeChanges() : this.hasFormChanges();

    if (hasUnsavedChanges || hasActiveUploads) {
      const message = hasActiveUploads
        ? 'Uploads are in progress. They will continue in the background. Are you sure you want to close?'
        : 'Are you sure you want to close? All unsaved changes will be lost.';

      this.confirmationService.confirm({
        message,
        header: 'Confirm Close',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Close',
        rejectLabel: 'Keep Editing',
        acceptButtonStyleClass: 'p-button-primary',
        rejectButtonStyleClass: 'p-button-text p-button-secondary',
        accept: () => {
          // Uploads are already managed by global queue service, they will continue
          // No toast notification needed - user can see the upload indicator in top right
          this.uploadDialogService.closeDialog();
          // Don't reset form if uploads are active - preserve state including files
          if (!hasActiveUploads) {
            this.resetForm();
          } else {
            // Persist metadata so it can be restored when reopening the dialog
            this.uploadDialogService.savePendingMetadata({
              title: this.title,
              selectedEvent: this.selectedEvent,
              date: this.date,
              ...this.pendingMetadataExtras(),
              tags: this.tags,
              markAsSecure: this.markAsSecure,
              shareType: this.shareType,
              shareUserIds: this.getShareUserIds(),
              shareEmails: this.getShareEmails(),
              shareExpireDate: this.shareExpireDate,
              sharePermissionLevel: this.sharePermissionLevel
            });
            // Only reset form fields, but keep selectedFiles and resumable upload state
            // This allows uploads to continue when dialog reopens
            this.title = '';
            this.selectedEvent = {};
            this.date = new Date();
            this.eventDateRange = null;
            this.tags = [];
            this.markAsSecure = false;
            this.resetShareFields();
            // Keep selectedFiles - don't reset them so uploads can continue
            // this.selectedFiles = [];
            this.tagIds = [];
            this.newlyCreatedFolderId = null;
            // Keep resumable upload state visible
            // this.useResumableUpload = true;
            // this.showResumableUpload = true;
          }
        },
      });
    } else {
      this.uploadDialogService.closeDialog();
      this.resetForm();
    }
  }

  onSecureChange(event: any): void {
    const isChecked = event?.checked ?? event?.target?.checked ?? false;
    console.log('Secure checkbox changed:', isChecked);
    if (!isChecked) {
      this.resetShareFields();
    } else {
      this.loadShareLookups();
      this.updateSharePermissionOptions();
      // Ensure "all permissions" are selected by default for co-ownership.
      // updateSharePermissionOptions will call ensureSharePermissionDefaults which applies the same behavior.
    }
  }

  resetForm(): void {
    if (this.uploadAutoCloseTimer) {
      clearTimeout(this.uploadAutoCloseTimer);
      this.uploadAutoCloseTimer = null;
    }
    this.editedFileName = '';
    this.initialEditStateSnapshot = null;
    this.title = '';
    this.selectedFileType = null;
    this.selectedCategoryId = null;
    this.isCategoryLocked = false;
    this.recipient = '';
    this.signingAuthority = '';
    this.signingAuthorityId = null;
    this.enableWorkflow = false;
    this.shareWithRecipients = false;
    this.workflowId = null;
    this.selectedShareRecipientIds = [];
    this.shareRecipients = [];
    this.pendingShareRecipients = [];
    this.systemDate = this.formatDisplayDate(new Date());
    this.documentDate = null;
    this.fileNumber = '';
    this.documentNumber = '';
    this.documentTypeId = '';
    this.selectedUploadDepartmentId = null;
    this.selectedEvent = null;
    this.date = new Date();
    this.eventDateRange = null;
    this.eventRangeCalendarDefaultDate = new Date();
    this.tags = [];
    this.fileTagsHydrated = false;
    this.markAsSecure = false; // Reset to unchecked
    this.resetShareFields();
    this.selectedFiles = [];
    this.pendingEmptyFolderRelativePaths.clear();
    this.folderStructureCreatedFolderIds.clear();
    this.folderStructureRootFolderIds.clear();
    this.folderStructureCreatedFolderIdsForUpload = [];
    this.fileStatusMap.clear();
    this.renameDrafts = {};
    this.editingUploadItemKey = null;
    this.browseMenuOpen = false;
    this.tagIds = [];
    this.selectedFolderName = 'My Files';
    this.selectedFolderId = 0;
    this.folderPickerIsDepartment = false;
    this.pickerFilterDepartmentId = null;
    this.libraryType = 1;
    this.libraryDepartmentId = null;
    this.resetFolderPickerTreeForSession();
    this.useResumableUpload = false;
    this.showResumableUpload = false;
    this.completedResumableSessions = [];
    this.uploadedFiles = [];
    this.allUploadsCompleted = false;
    this.newlyCreatedFolderId = null; // Reset newly created folder tracking
    this.isNewFolderOnUpload = false;
    this.pickerCreatedFolderId = null;
    this.folderRenameDraft = '';
    this.selectedFolderId = 0;
    this.selectedNode = null;
    this.selectedFolderName = 'My Files';
    this.resumeMetadataLocked = false;
    this.pendingResumeSession = null;
    this.resumableAutoCloseTriggered = false;
    this.resumableSuccessToastShown = false;
    this.fileMetaCreatedBy = '-';
    this.fileMetaCreatedOn = '-';
    this.fileMetaLastUpdatedBy = '-';
    this.fileMetaLastUpdatedOn = '-';
    this.fileMetaTotalViews = '0';
    this.fileMetaTotalDownloads = '0';
    this.folderMetaCreatedBy = '-';
    this.folderMetaCreatedOn = '-';
    this.folderMetaLastUpdatedBy = '-';
    this.folderMetaLastUpdatedOn = '-';
    this.folderMetaTotalViews = '0';
    this.folderMetaTotalDownloads = '0';
    this.mediaActivityLogs = [];
    this.showMetadataLogDialog = false;
    setTimeout(() => {
      if (this.titleInput) {
        this.titleInput.reset();
      }
    });
  }

  private getRequestTitle(): string {
    const rawTitle =
      this.title?.trim() ||
      this.editedFile?.titleEn?.trim() ||
      this.editedFile?.fileName ||
      this.selectedFiles?.[0]?.name ||
      this.completedResumableSessions?.[0]?.fileName ||
      'Untitled';

    const sanitized = String(rawTitle).replace(/[*\?\/\\:|<>]/g, '').trim();
    const normalized = sanitized.length > 0 ? sanitized : 'Untitled';
    return normalized.length > 150 ? normalized.substring(0, 150) : normalized;
  }

  getTotalFileSize(): number {
    return this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
  }

  /**
   * Get user storage information from sessionStorage
   * @returns Storage data object or null if not available
   */
  getUserStorageFromSession(): any {
    try {
      const storageData = sessionStorage.getItem('userStorage');
      if (storageData) {
        return JSON.parse(storageData);
      }
    } catch (error) {
      console.error('Error reading storage from sessionStorage:', error);
    }
    return null;
  }

 
  checkStorageQuota(): { isValid: boolean; message: string } {
    const storage = this.getUserStorageFromSession();

    console.log("storage: ",storage);
    
    // If storage data is not available, allow upload (graceful degradation)
    if (!storage || !storage.usedValue || !storage.totalValue || storage.totalValue === -1) {
      return { isValid: true, message: '' };
    }

    // Calculate available space in Bytes
    const usedValueBytes = this.convertToBytes(storage.usedValue,storage.usedUnit) || 0;
    const totalValueBytes = this.convertToBytes(storage.totalValue,storage.totalUnit) || 0;
    const availableSpaceBytes = totalValueBytes - usedValueBytes;

    console.log("used: ",usedValueBytes,"total: ",totalValueBytes,"availableSpaceBytes: ",availableSpaceBytes)

    // Calculate total file size in byte
    const totalFileSizeBytes = this.getTotalFileSize();

    // const totalFileSizeGB = totalFileSizeBytes / (1024 * 1024 * 1024);

    // Check if upload will exceed quota
    if (totalFileSizeBytes > availableSpaceBytes) {
      const usedPercentage = ((usedValueBytes / totalValueBytes) * 100).toFixed(1);
      return {
        isValid: false,
        message: `Storage quota exceeded. You have used ${this.getQuotaPartsFromBytes(usedValueBytes)} of ${this.getQuotaPartsFromBytes(totalValueBytes)} (${usedPercentage}%). Available space: ${this.getQuotaPartsFromBytes(availableSpaceBytes)}. Selected files: ${this.getQuotaPartsFromBytes(totalFileSizeBytes)} .`
      };
    }

    // Check if approaching quota limit (warning at 90%)
    const usageAfterUpload = ((usedValueBytes + totalFileSizeBytes) / totalValueBytes) * 100;
    if (usageAfterUpload > 90) {
      return {
        isValid: true,
        message: `Warning: ${((usageAfterUpload).toFixed(1))}% of your storage quota is already in use.`
      };
    }

    return { isValid: true, message: '' };
  }

  formatBytes(bytes: number, decimals: number = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  private getSelectedLocationId(): number | null {
    if (!this.selectedLocation) {
      return null;
    }
    if (typeof this.selectedLocation === 'object' && this.selectedLocation.locationId) {
      return Number(this.selectedLocation.locationId);
    }
    return null;
  }

  private populateFolderMetadataForEdit(): void {
    const folder = this.existingFolderData || {};
    this.applyFolderMetaDetails(folder);
if(this.envConfig.isEventsSecExpanded){
    if (folder.eventId) {
      const matchingEvent =
        this.events.find((event: any) => event.eventId === folder.eventId) ||
        this.allEventsIncludingInactive.find((event: any) => event.eventId === folder.eventId);
      if (matchingEvent) {
        this.selectedEvent = matchingEvent;
      }
    }
  }
if(this.envConfig.isLocationSecExpanded){
    if (folder.locationId && this.locations.length > 0) {
      const matchingLocation = this.locations.find((location: any) => location.locationId === folder.locationId);
      if (matchingLocation) {
        this.selectedLocation = matchingLocation;
      }
    } else {
      this.selectedLocation = null;
    }
  }
    if (folder.tags && Array.isArray(folder.tags)) {
      this.tags = folder.tags.map((tag: any) => {
        const tagName = typeof tag === 'string' ? tag : tag.tag;
        const matchingTag = this.availableTags.find((available: any) => available.tag === tagName);
        return matchingTag || { tag: tagName };
      });
    }

    this.captureInitialEditState();
  }

  private hasSelectedFolderMetadataForInheritance(): boolean {
    const locationPayload = this.resolveLocationPayload();
    return !!(
      this.getSelectedEventId() ||
      this.getNewEventName()?.trim() ||
      locationPayload?.locationId ||
      locationPayload?.newLocation ||
      this.getTagIdsArray().length > 0 ||
      this.getNewTagsArray().length > 0
    );
  }

  private buildFolderMetadataPayload(folderId: number, folder: any): Record<string, unknown> {
    const resolvedFolderName =
      folder?.folderNameEn ||
      folder?.FolderNameEn ||
      folder?.folderNameAr ||
      folder?.FolderNameAr ||
      folder?.entityName ||
      folder?.EntityName ||
      folder?.name ||
      this.folderMetadataName ||
      '';

    const payload: Record<string, unknown> = {
      folderId,
      folderNameEn: resolvedFolderName,
      folderNameAr: resolvedFolderName,
      parentId: folder?.parentId ?? folder?.ParentId ?? this.parentId ?? 0,
      userId: Number(localStorage.getItem('userId')),
      tagIds: this.getTagIdsArray(),
      renameOnly:false
    };
    const newTags = this.getNewTagsArray();
    if(this.envConfig.isLocationSecExpanded){
    const locationPayload = this.resolveLocationPayload();
     if (locationPayload?.locationId) {
      payload.locationId = locationPayload.locationId;
    }
    if (locationPayload?.newLocation) {
      payload.newLocation = locationPayload.newLocation;
    }
    }
    if(this.envConfig.isEventsSecExpanded){
    const resolvedEventId = this.getSelectedEventId();
    const newEventName = this.getNewEventName()?.trim() || '';
    const hasSelectedEvent = !!(resolvedEventId != null && resolvedEventId > 0) || !!newEventName;
    const startIso = this.getEventRangeStartIso();
    const endIso = this.getEventRangeEndIso();
    if (resolvedEventId != null && resolvedEventId > 0) {
      payload.eventId = resolvedEventId;
    }
    if (hasSelectedEvent) {
      if (startIso) {
        payload.eventStartDate = startIso;
        payload.eventDate = startIso;
      }
      if (endIso) {
        payload.eventEndDate = endIso;
      }
    }
    if (newEventName) {
      payload.newEvent = newEventName;
    }
  }
    if (newTags.length > 0) {
      payload.newTags = newTags;
    }

    return payload;
  }

  private async applyMetadataToFolderStructureRoots(): Promise<void> {
    if (!this.folderStructureRootFolderIds.size || !this.hasSelectedFolderMetadataForInheritance()) {
      return;
    }

    for (const folderId of Array.from(this.folderStructureRootFolderIds)) {
      const folderResponse = await firstValueFrom(this.documentService.getFolderById(folderId));
      if (folderResponse?.httpStatus !== 200 || !folderResponse?.data) {
        throw new Error(folderResponse?.errorMessage || `Unable to load folder ${folderId} for metadata update.`);
      }

      const payload = this.buildFolderMetadataPayload(folderId, folderResponse.data);
      const updateResponse = await firstValueFrom(this.documentService.updateFolder(folderId, payload));
      if (updateResponse?.httpStatus !== 200) {
        throw new Error(updateResponse?.errorMessage || `Unable to update metadata for folder ${folderId}.`);
      }
    }
  if(this.envConfig.isLocationSecExpanded){
    if (this.getNewEventName()?.trim()) {
      this.sidebarService.refreshEvents().subscribe();
    }
  }
    if (this.getNewTagsArray().length > 0) {
      this.sidebarService.refreshTags().subscribe();
    }
    if(this.envConfig.isLocationSecExpanded){
      this.loadLocations();
    }
  }

  private updateFolderMetadata(): void {
    const folderId = Number(
      this.folderMetadataId ||
      this.existingFolderData?.folderId ||
      this.existingFolderData?.entityId
    );

    if (!folderId) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'Folder id is missing for metadata update.',
      });
      return;
    }

    const payload = this.buildFolderMetadataPayload(folderId, this.existingFolderData);
    this.spinner.show();
    this.documentService.updateFolder(folderId, payload).subscribe({
      next: (res: any) => {
        if (res.httpStatus === 200) {
          this.messageService.add({
            key: 'custom',
            severity: 'success',
            summary: '',
            detail: 'Folder metadata updated successfully',
          });
          if(this.envConfig.isEventsSecExpanded){
          this.sidebarService.refreshEvents().subscribe();
          }
          this.sidebarService.refreshTags().subscribe();
          if(this.envConfig.isLocationSecExpanded){
          this.loadLocations();
          }
          this.emitUploadSuccess(this.currentFolderId || 0);
          this.uploadDialogService.closeDialog();
        } else {
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: '',
            detail: res.errorMessage || 'Error updating folder metadata',
          });
        }
        this.spinner.hide();
      },
      error: (error: any) => {
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: '',
          detail: error?.error?.errorMessage || 'Error updating folder metadata',
        });
      }
    });
  }

  private loadFolderMetadataFromApi(): void {
    const folderId = Number(
      this.folderMetadataId ||
      0
    );
    const resolvedFolderId = folderId || Number(
      this.folderMetadataId ||
      this.existingFolderData?.folderId ||
      this.existingFolderData?.entityId ||
      this.currentFolderId
    );
    if (!resolvedFolderId) {
      return;
    }
    // Fetch folder details
    this.documentService.getFolderById(resolvedFolderId).subscribe({
      next: (res: any) => {
        const folder = res?.data;
        if (!folder) return;
        this.applyFolderMetaDetails(folder);
        if(this.envConfig.isEventsSecExpanded){
        // Map event — check active first, then all (including inactive)
        if (folder.eventId) {
          const matchingEvent =
            this.events.find((e: any) => e.eventId === folder.eventId) ||
            this.allEventsIncludingInactive.find((e: any) => e.eventId === folder.eventId);
          if (matchingEvent) {
            this.selectedEvent = matchingEvent;
          }
        }
      }
        if(this.envConfig.isLocationSecExpanded){
        // Map location
        if (folder.locationId && this.locations.length > 0) {
          const matchingLocation = this.locations.find((l: any) => l.locationId === folder.locationId);
          if (matchingLocation) {
            this.selectedLocation = matchingLocation;
          }
        } else {
          this.selectedLocation = null;
        }
      }
        // Prefer strong ids from TagIds if provided, fallback to tags array
        const tagIdsFromResponse: number[] = Array.isArray(folder.tagIds) ? folder.tagIds : [];
        if (tagIdsFromResponse.length > 0) {
          this.tags = tagIdsFromResponse.map((id: number) => {
            const match = this.availableTags.find((av: any) => av.tagId === id);
            return match || { tagId: id, tag: (match?.tag || '') };
          });
        } else {
          const tagList = folder.tags || [];
          if (Array.isArray(tagList)) {
            this.tags = tagList.map((t: any) => {
              const matching = this.availableTags.find((av: any) => av.tagId === t.tagId || av.tag === t.tag);
              return matching || { tagId: t.tagId, tag: t.tag };
            });
          }
        }
        this.captureInitialEditState();
      },
      error: (error: any) => {
        console.warn('Failed to load folder metadata from API; keeping existing folder snapshot.', error);
        this.captureInitialEditState();
      }
    });
  }

  private captureInitialEditState(): void {
    if (!this.editMode) {
      this.initialEditStateSnapshot = null;
      return;
    }
    this.initialEditStateSnapshot = this.serializeCurrentEditState();
  }

  private hasEditModeChanges(): boolean {
    if (!this.editMode) {
      return false;
    }
    if (!this.initialEditStateSnapshot) {
      // If baseline is not captured yet, avoid false confirm popups.
      return false;
    }
    return this.initialEditStateSnapshot !== this.serializeCurrentEditState();
  }

  private serializeCurrentEditState(): string {
    if(this.envConfig.isEventsSecExpanded){
      const eventId = this.getSelectedEventId();
    }
    if(this.envConfig.isLocationSecExpanded){
      const locationId = this.getSelectedLocationId();
    }
    const tagIds = this.getTagIdsArray().slice().sort((a, b) => a - b);
    const newTags = this.getNewTagsArray()
      .map((tag) => String(tag || '').trim().toLowerCase())
      .filter(Boolean)
      .sort();
    const dateOnly = this.date ? new Date(this.date).toDateString() : '';
    const normalizedEventRange = this.getNormalizedEventDateRange();
    const eventRangeStart = normalizedEventRange?.[0]?.getTime() ?? 0;
    const eventRangeEnd = normalizedEventRange?.[1]?.getTime() ?? 0;

    if (this.folderMetadataEditMode || this.editEntityType === 'folder') {
      return JSON.stringify({
        mode: 'folder',
        folderId: Number(this.folderMetadataId || this.existingFolderData?.folderId || this.existingFolderData?.entityId || 0),
        // eventId: eventId ?? 0,
        // locationId: locationId ?? 0,
        tagIds,
        newTags,
        dateOnly,
        eventRangeStart,
        eventRangeEnd
      });
    }

    return JSON.stringify({
      mode: 'file',
      fileId: Number(this.editedFile?.fileId || this.fileId || 0),
      fileName: (this.editedFileName || '').trim(),
      // eventId: eventId ?? 0,
      // locationId: locationId ?? 0,
      tagIds,
      newTags,
      dateOnly,
      eventRangeStart,
      eventRangeEnd,
      recipient: (this.getShareRecipientLabelsCsv() || this.recipient || '').trim(),
      signingAuthority: this.getSelectedSigningAuthorityLabel().trim(),
      signingAuthorityId: this.signingAuthorityId ?? null,
      enableWorkflow: this.enableWorkflow,
      workflowId: this.workflowId ?? null,
      shareWithRecipients: this.shareWithRecipients,
      selectedShareRecipientIds: [...(this.selectedShareRecipientIds || [])],
      systemDate: this.systemDate || '',
      fileNumber: (this.fileNumber || '').trim(),
      documentNumber: (this.documentNumber || '').trim(),
      documentTypeId: this.documentTypeId ?? '',
      departmentId: this.selectedUploadDepartmentId ?? null,
      categoryId: this.selectedCategoryId ?? null,
      fileType: this.selectedFileType || '',
      documentDate: this.documentDate ? new Date(this.documentDate).toDateString() : ''
    });
  }

  private applyFolderMetaDetails(folder: any): void {
    if (!folder) {
      return;
    }

    this.folderMetadataName =
      folder.folderNameEn ||
      folder.folderNameAr ||
      folder.entityName ||
      folder.name ||
      this.folderMetadataName ||
      'Folder';

    this.folderMetaCreatedBy = this.resolveMetaPerson(folder, 'created');
    this.folderMetaCreatedOn = formatAmmViewLogDateTime(
      folder.system_CreatedOn || folder.systemCreatedOn || folder.createdOn,
      'folder'
    );
    this.folderMetaLastUpdatedBy = this.resolveMetaPerson(folder, 'updated');
    this.folderMetaLastUpdatedOn = formatAmmViewLogDateTime(
      folder.system_ModifiedOn || folder.systemModifiedOn || folder.modifiedOn,
      'folder'
    );
    this.folderMetaTotalViews = this.resolveMetaCount(folder, ['totalViewCount', 'folderViewCount', 'viewCount']);
    this.folderMetaTotalDownloads = this.resolveMetaCount(folder, ['totalDownloadCount', 'downloadedCount', 'downloadCount']);
  }

  private applyFileMetaDetails(file: any): void {
    if (!file) {
      return;
    }

    const fullName = [file?.firstName, file?.lastName]
      .filter((v: any) => v !== undefined && v !== null && String(v).trim() !== '')
      .map((v: any) => String(v).trim())
      .join(' ')
      .trim();
    const creatorName = fullName || file?.nameEn || file?.createdByName || file?.system_CreatedByName;
    this.fileMetaCreatedBy = creatorName ? String(creatorName) : this.resolveMetaPerson(file, 'created');
    this.fileMetaCreatedOn = formatAmmViewLogDateTime(
      file.system_CreatedOn ||
      file.systemCreatedOn ||
      file.createdOn ||
      file.uploadedDate ||
      file.submission_date ||
      file.submissionDate,
      'file'
    );
    this.fileMetaLastUpdatedBy = this.resolveMetaPerson(file, 'updated');
    this.fileMetaLastUpdatedOn = formatAmmViewLogDateTime(
      file.system_ModifiedOn ||
      file.systemModifiedOn ||
      file.modifiedOn ||
      file.submission_modified_date ||
      file.submissionModifiedDate,
      'file'
    );
    this.fileMetaTotalViews = this.resolveMetaCount(file, ['fileViewCount', 'totalViewCount', 'viewCount']);
    this.fileMetaTotalDownloads = this.resolveMetaCount(file, ['downloadCount', 'downloadedCount', 'totalDownloadCount']);
  }

  private loadViewDownloadLogsForEdit(entityType: 'file' | 'folder', entityId: number): void {
    if (!entityId || entityId <= 0) {
      return;
    }

    this.documentService.getViewDownloadLogs(entityType, entityId).subscribe({
      next: (res: any) => {
        const logs = res?.data || res || [];
        this.mediaActivityLogs = this.buildTimelineLogsFromDedicatedLogs(logs);
      },
      error: (error: any) => {
        console.warn('Failed to load dedicated view/download timeline logs; falling back to shared users', error);
        this.loadSharedUserViewDownloadFallback(entityType, entityId);
      },
    });
  }

  private loadSharedUserViewDownloadFallback(entityType: 'file' | 'folder', entityId: number): void {
    const payload = {
      pageIndex: 0,
      pageSize: 100,
    };

    const request$ = entityType === 'folder'
      ? this.documentService.getSharedFolderUser(entityId, payload)
      : this.documentService.getSharedFileUser(entityId, payload);

    request$.subscribe({
      next: (res: any) => {
        const data = res?.data || {};
        const sharedUsers = data?.users?.items || data?.items || [];
        this.mediaActivityLogs = this.buildViewDownloadLogs(sharedUsers, entityType);
        this.applyViewDownloadCounts(data, entityType);
      },
      error: (error: any) => {
        console.warn('Failed to load shared-user view/download timeline fallback', error);
        this.mediaActivityLogs = [];
      },
    });
  }

  private buildTimelineLogsFromDedicatedLogs(logs: any[]): ViewLogItem[] {
    return (logs || []).map((log: any) => {
      const actionType = log?.actionType || log?.ActionType || '';
      const actorName =
        log?.actorName ||
        log?.ActorName ||
        log?.actorEmail ||
        log?.ActorEmail ||
        (log?.userId || log?.UserId ? `User ${log?.userId || log?.UserId}` : '-');

      return {
        actionType,
        actorName,
        actionOn: log?.actionOn || log?.ActionOn,
        description: log?.description || log?.Description || '',
      };
    }).filter((log: ViewLogItem) => !!log.actionType && !!log.actionOn);
  }

  private buildViewDownloadLogs(sharedUsers: any[], entityType: 'file' | 'folder'): ViewLogItem[] {
    const itemLabel = entityType === 'folder' ? 'Folder' : 'File';

    return (sharedUsers || []).reduce((logs: ViewLogItem[], user: any) => {
      const actorName = this.resolveLogActorName(user);
      const viewedOn = user?.lastViewedOn || user?.viewedOn || user?.openedOn || user?.lastOpenedOn;
      const downloadedOn = user?.downloadedOn || user?.lastDownloadedOn || user?.downloadOn || user?.lastDownloadOn;

      if (viewedOn) {
        logs.push({
          actionType: 'Viewed',
          actorName,
          actionOn: viewedOn,
          description: `${itemLabel} viewed`,
        });
      }

      if (user?.isDownloaded || user?.downloaded || downloadedOn) {
        logs.push({
          actionType: 'Downloaded',
          actorName,
          actionOn: downloadedOn || viewedOn,
          description: `${itemLabel} downloaded`,
        });
      }

      return logs;
    }, []);
  }

  private applyViewDownloadCounts(data: any, entityType: 'file' | 'folder'): void {
    const viewCount = entityType === 'folder'
      ? data?.folderViewCount ?? data?.totalViewCount ?? data?.viewCount
      : data?.fileViewCount ?? data?.totalViewCount ?? data?.viewCount;
    const downloadCount = data?.downloadedCount ?? data?.downloadCount ?? data?.totalDownloadCount;

    if (viewCount !== undefined && viewCount !== null) {
      if (entityType === 'folder') {
        this.folderMetaTotalViews = String(viewCount);
      } else {
        this.fileMetaTotalViews = String(viewCount);
      }
    }

    if (downloadCount !== undefined && downloadCount !== null) {
      if (entityType === 'folder') {
        this.folderMetaTotalDownloads = String(downloadCount);
      } else {
        this.fileMetaTotalDownloads = String(downloadCount);
      }
    }
  }

  private resolveLogActorName(user: any): string {
    const fullName = [user?.firstName, user?.lastName]
      .filter((value: any) => value !== undefined && value !== null && String(value).trim() !== '')
      .map((value: any) => String(value).trim())
      .join(' ')
      .trim();

    return fullName ||
      user?.fullName ||
      user?.userName ||
      user?.name ||
      user?.email ||
      '-';
  }

  private resolveMetaPerson(folder: any, mode: 'created' | 'updated'): string {
    const possibleKeys =
      mode === 'created'
        ? ['createdByName', 'system_CreatedByName', 'systemCreatedByName', 'createdBy', 'system_CreatedBy', 'systemCreatedBy']
        : ['lastUpdatedByName', 'modifiedByName', 'system_ModifiedByName', 'systemModifiedByName', 'modifiedBy', 'lastUpdatedBy', 'system_ModifiedBy', 'systemModifiedBy'];

    for (const key of possibleKeys) {
      const value = folder?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value);
      }
    }

    return '-';
  }

  private resolveMetaCount(folder: any, keys: string[]): string {
    for (const key of keys) {
      const raw = folder?.[key];
      if (raw === undefined || raw === null || raw === '') {
        continue;
      }

      const n = Number(raw);
      if (!Number.isNaN(n) && Number.isFinite(n)) {
        return Math.max(0, Math.trunc(n)).toString();
      }
      return String(raw);
    }
    return '0';
  }

  getTagIdsArray(): number[] {
    const existingTagIds: number[] = [];
    this.tags.forEach((tagInput: any) => {
      const directTagId = Number(tagInput?.tagId ?? tagInput?.TagId ?? 0);
      if (Number.isFinite(directTagId) && directTagId > 0) {
        existingTagIds.push(directTagId);
        return;
      }

      const tagName = typeof tagInput === 'string' ? tagInput : tagInput.tag;
      const existingTag = this.availableTags.find(
        (availableTag) => availableTag.tag === tagName
      );
      if (existingTag && existingTag.tagId) {
        existingTagIds.push(existingTag.tagId);
      }
    });
    return existingTagIds;
  }

  getNewTagsArray(): string[] {
    const newTags: string[] = [];
    this.tags.forEach((tagInput: any) => {
      const directTagId = Number(tagInput?.tagId ?? tagInput?.TagId ?? 0);
      if (Number.isFinite(directTagId) && directTagId > 0) {
        return;
      }

      const tagName = typeof tagInput === 'string' ? tagInput : tagInput.tag;
      const existingTag = this.availableTags.find(
        (availableTag) => availableTag.tag === tagName
      );
      if (!existingTag || !existingTag.tagId) {
        newTags.push(tagName);
      }
    });
    return newTags;
  }

  openCreateFolderDialog(): void {
    if (this.isCreatingFolder) {
      return;
    }
    // On create click, keep UX smooth by creating a unique default label
    // and avoid showing duplicate toast for "New Folder".
    const parentId =
      this.parentId === null || this.parentId === undefined
        ? 0
        : Number(this.parentId);
    const uniqueName = this.buildUniqueFolderName('New Folder', parentId);
    this.immediateCreateFolder(uniqueName);
  }

  private immediateCreateFolder(name: string): void {
    if (this.isCreatingFolder) {
      return;
    }

    const trimmedName = name.trim();
    if (!trimmedName) return;

    // Validation: Check for restricted characters
    if (!this.validateFolderName(trimmedName)) {
      return;
    }

    const parentId = this.getParentIdForNewFolder();
    const effectiveLibraryType = this.getUploadDestinationLibraryType();

    const destinationParentId = parentId && parentId > 0 ? parentId : 0;
    const payload = {
      parentId: destinationParentId > 0 ? destinationParentId : -1,
      folderNameEn: trimmedName,
      folderNameAr: trimmedName,
      // Folder metadata is applied after the first upload, not on create.
      userId: Number(localStorage.getItem('userId')),
      folderId: destinationParentId,
      libraryType: effectiveLibraryType,
      departmentId: this.getUploadDestinationDepartmentId(destinationParentId) ?? -1,
      categoryId: this.selectedCategoryId ?? null,
      IsUploaded: true,
    };

    this.isCreatingFolder = true;
    this.documentService.createFolder(payload).pipe(
      finalize(() => {
        this.isCreatingFolder = false;
      })
    ).subscribe({
      next: (res: any) => {
        if (res.httpStatus === 200) {
          const newFolderId = res.data.FolderId;
          this.rememberOptimisticCreatedFolderName(destinationParentId, trimmedName);
          this.newlyCreatedFolderId = newFolderId;
          this.isNewFolderOnUpload = true;
          this.messageService.add({
            key: 'custom',
            severity: 'success',
            summary: '',
            detail: 'Folder created successfully',
          });
          
          // Call GetMyFolders API after folder creation
          const getMyFoldersPayload = {
            pageIndex: 0,
            pageSize: 0,
            parentId: destinationParentId,
            userId: Number(localStorage.getItem('userId')),
          };
          this.documentService.getFolderByCategory(getMyFoldersPayload).subscribe({
            next: () => {
              // API called successfully
            },
            error: (error: any) => {
              console.error('Error calling GetMyFolders after folder create:', error);
            }
          });
          
          if (!this.hasFormChanges()) {
            setTimeout(() => {
              this.sidebarService.triggerRename(newFolderId);
              this.sidebarService.triggerFolderHierarchyRefresh();
              const baseRoute = (this.router.url || '').includes('/main/home/dept-assets')
                ? '/main/home/dept-assets'
                : '/main/home/files';
              this.router.navigate([baseRoute, newFolderId]);
              this.uploadDialogService.closeDialog();
            }, 1000);
          } else {
            this.sidebarService.triggerFolderHierarchyRefresh();
          }
        } else {
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: '',
            detail: res.errorMessage,
          });
        }
      },
      error: (error: any) => {
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: '',
          detail: error?.error?.errorMessage || 'Error creating folder',
          life: 8000,
        });
      },
    });
  }

  private validateFolderName(name: string): boolean {
    if (name.length > this.folderNameMaxLength) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Invalid Name',
        detail: `Folder name cannot exceed ${this.folderNameMaxLength} characters`,
        life: 5000,
      });
      return false;
    }

    const restrictedChars = /[*\?\/\\:|<>]/;
    if (restrictedChars.test(name)) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: 'Invalid Name',
        detail: 'Folder name cannot contain *, ?, /, \\, :, |, <, >',
        life: 5000,
      });
      return false;
    }
    return true;
  }

  private hasFullAccess(): boolean {
    return this.accessService.hasFullAccess();
  }

  /** Users who may browse Department Assets in the upload picker. */
  private canUseDepartmentLibraryInUpload(): boolean {
    return this.accessService.hasMultiplePermission(['VIEW_ASSETS']);
  }

  /** Org Assets / Department Assets destination toggle is not used in upload. */
  get showFolderLibrarySourceToggle(): boolean {
    return false;
  }

  get canEnablePersonalFolderPickerSource(): boolean {
    return this.canEnableFolderPickerSourceForLibrary(1);
  }

  get canEnableDepartmentFolderPickerSource(): boolean {
    return (
      this.canUseDepartmentLibraryInUpload() &&
      this.canEnableFolderPickerSourceForLibrary(2)
    );
  }

  private canEnableFolderPickerSourceForLibrary(libraryType: 1 | 2): boolean {
    const hasFolderItems = this.uploadItemsForDisplay.some((item) => item.type === 'folder');
    const hasFileItems = this.uploadItemsForDisplay.some((item) => item.type === 'file');
    const filePermission =
      libraryType === 1 ? 'UPLOAD_MY_FILE' : 'UPLOAD_DEPT_FILE';
    const folderPermission =
      libraryType === 1 ? 'UPLOAD_MY_FOLDER' : 'UPLOAD_DEPT_FOLDER';

    if (!hasFolderItems && !hasFileItems) {
      return (
        this.accessService.hasPermission(filePermission) ||
        (this.IS_FOLDER_UPLOAD_ENABLED &&
          this.accessService.hasPermission(folderPermission))
      );
    }

    if (hasFolderItems && !this.accessService.hasPermission(folderPermission)) {
      return false;
    }
    if (hasFileItems && !this.accessService.hasPermission(filePermission)) {
      return false;
    }
    return true;
  }

  /** Keep the picker on Department Assets for every permitted upload. */
  private syncFolderPickerSourceWithUploadPermissions(): void {
    if (!this.folderPickerIsDepartment && this.canEnableDepartmentFolderPickerSource) {
      this.setFolderPickerSource('department');
    }
  }

  get showDepartmentDropdownForPicker(): boolean {
    // Department filter is no longer needed for Org Assets picker.
    // Super admins can browse the full hierarchy from the Org Assets root.
    return false;
  }

  get departmentTreeBlockedBySelection(): boolean {
    return (
      this.folderPickerIsDepartment &&
      this.showDepartmentDropdownForPicker &&
      (!this.pickerFilterDepartmentId || this.pickerFilterDepartmentId <= 0)
    );
  }

  private resolveLoginDepartmentId(): number {
    const login = JSON.parse(localStorage.getItem('loginObject') || '{}');
    const d = Number(login.departmentId ?? login.DepartmentId ?? 0);
    return Number.isFinite(d) && d > 0 ? d : 0;
  }

  private syncPickerDepartmentFilterFromContext(): void {
    if (!this.folderPickerIsDepartment) {
      this.pickerFilterDepartmentId = null;
      return;
    }
    if (this.hasFullAccess()) {
      // For full-access users, do not pre-filter by department.
      // The picker tree already contains department root folders.
      this.pickerFilterDepartmentId = null;
    } else {
      const d = this.resolveLoginDepartmentId();
      this.pickerFilterDepartmentId = d > 0 ? d : null;
    }
  }

  private syncLibraryContextFromPicker(): void {
    if (this.folderPickerIsDepartment) {
      this.libraryType = 2;
      if (this.pickerFilterDepartmentId && this.pickerFilterDepartmentId > 0) {
        this.libraryDepartmentId = this.pickerFilterDepartmentId;
      } else if (this.libraryDepartmentId && this.libraryDepartmentId > 0) {
        // Keep department passed by the opener (file-manager / sidebar).
      } else if (!this.hasFullAccess()) {
        const d = this.resolveLoginDepartmentId();
        this.libraryDepartmentId = d > 0 ? d : null;
      } else {
        this.libraryDepartmentId = null;
      }
    } else {
      this.libraryType = 1;
      this.libraryDepartmentId = null;
    }
  }

  /** Drop the cached folder tree when a dialog session starts. */
  private resetFolderPickerTreeForSession(): void {
    this.folderNodes = [];
    this.filteredFolderNodes = [];
    this.folderSearchTerm = '';
    this.isFolderTreeVisible = false;
    this.selectedNode = null;
    this.selectedFolderId = 0;
    this.selectedFolderName = this.getFolderPickerRootLabel();
    this.cachedPickerDepartmentRootFolderId = null;
    this.isLoadingFolders = false;
  }

  setFolderPickerSource(mode: 'personal' | 'department'): void {
    if (mode === 'personal' && !this.canEnablePersonalFolderPickerSource) {
      return;
    }
    if (mode === 'department' && !this.canEnableDepartmentFolderPickerSource) {
      return;
    }
    if (mode === 'personal') {
      this.folderPickerIsDepartment = false;
    } else {
      this.folderPickerIsDepartment = true;
      this.syncPickerDepartmentFilterFromContext();
      if (this.showDepartmentDropdownForPicker && this.departments.length === 0) {
        this.getDepartments();
      }
    }
    this.syncLibraryContextFromPicker();
    this.folderNodes = [];
    this.filteredFolderNodes = [];
    this.folderSearchTerm = '';
    this.selectedNode = null;
    this.selectedFolderId = 0;
    this.selectedFolderName = this.getFolderPickerRootLabel();
    if (this.isFolderTreeVisible) {
      this.loadFolderHierarchy(0);
    }
  }

  onPickerFilterDepartmentChange(): void {
    this.syncLibraryContextFromPicker();
    this.folderNodes = [];
    this.filteredFolderNodes = [];
    this.folderSearchTerm = '';
    this.selectedNode = null;
    this.selectedFolderId = 0;
    this.selectedFolderName = this.getFolderPickerRootLabel();
    if (this.isFolderTreeVisible) {
      this.loadFolderHierarchy(0);
    }
  }

  private transformDepartmentLibraryToTreeNodes(folders: any[]): any[] {
    const departmentId =
      this.pickerFilterDepartmentId && this.pickerFilterDepartmentId > 0
        ? this.pickerFilterDepartmentId
        : this.resolveLoginDepartmentId() || null;
    const root: any = {
      label: this.getFolderPickerRootLabel(),
      expanded: true,
      selectable: this.canUploadToDepartmentLibraryRoot(),
      data: {
        entityId: 0,
        folderId: 0,
        libraryType: 2,
        departmentId
      },
      children: [],
      parent: null as any,
    };

    const mapFoldersToNodes = (folderList: any[], parentNode: any | null): any[] => {
      return folderList.map((f) => {
        const id = f.folderId ?? f.FolderId ?? f.entityId ?? f.EntityId;
        const sub = f.subFolders ?? f.SubFolders;
        const en = String(f.folderNameEn ?? f.FolderNameEn ?? '').trim();
        const ar = String(f.folderNameAr ?? f.FolderNameAr ?? '').trim();
        const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
        const node: any = {
          label: useArabic ? (ar || en) : (en || ar),
          data: {
            ...f,
            entityId: id ?? f.entityId ?? f.EntityId,
            isCategoryRoot: f.isCategoryRoot === true || f.IsCategoryRoot === true,
            hasChildren: f.hasChildren === true || f.HasChildren === true
          },
          children: [] as any[],
          parent: parentNode,
        };
        node.children =
          sub && Array.isArray(sub) && sub.length > 0 ? mapFoldersToNodes(sub, node) : [];
        return node;
      });
    };

    root.children = mapFoldersToNodes(folders, root);
    return [root];
  }

  private maybeAutoSelectFirstDepartmentFolder(): void {
    // Root-level upload is allowed (folderId 0). Do not auto-select the first
    // department folder — that previously forced FolderId to a department root
    // (e.g. 283) even when the user left Department unselected.
    return;
  }

  toggleFolderTree(): void {
    this.isFolderTreeVisible = !this.isFolderTreeVisible;
    if (this.isFolderTreeVisible && this.folderNodes.length === 0 && !this.isLoadingFolders) {
      this.loadFolderHierarchy();
    }
  }

  // private getSelectedFilesForDuplicateCheck(): File[] {
  //   return this.visibleSelectedFiles.map(file => this.ensureUploadFileMetadata(file));
  // }

  // private recheckDuplicatesForCurrentSelection(): void {
  //   const files = this.getSelectedFilesForDuplicateCheck();
  //   if (files.length === 0) {
  //     this.duplicateItemKeys.clear();
  //     this.existingDuplicateFileNames.clear();
  //     this.existingDuplicateFolderNames.clear();
  //     return;
  //   }
  //   this.executeDuplicateCheck(files, false);
  // }

  loadFolderHierarchy(newIdToSelect?: number): void {
    this.isLoadingFolders = true;
    const userId = Number(localStorage.getItem('userId'));

    if (this.folderPickerIsDepartment) {
      if (this.departmentTreeBlockedBySelection) {
        this.folderNodes = [];
        this.filteredFolderNodes = [];
        this.selectedFolderName = 'Select a department first';
        this.selectedNode = null;
        this.selectedFolderId = 0;
        this.isLoadingFolders = false;
        return;
      }

      const filterDept =
        this.pickerFilterDepartmentId && this.pickerFilterDepartmentId > 0
          ? this.pickerFilterDepartmentId
          : null;

      this.documentService.getDepartmentLibraryFolderHierarchy(undefined, filterDept).subscribe({
        next: (res: any) => {
          this.optimisticCreatedFolderNamesByParent.clear();
          const rawFolders = Array.isArray(res?.data) ? res.data : [];
          this.cachedPickerDepartmentRootFolderId =
            this.extractDepartmentRootFolderIdFromHierarchy(rawFolders, filterDept) ||
            null;
          this.folderNodes = this.transformDepartmentLibraryToTreeNodes(rawFolders);
          this.applyFolderSearchFilter();

          if (newIdToSelect !== undefined && newIdToSelect !== null && newIdToSelect > 0) {
            this.selectedNode = this.findNodeById(this.folderNodes, newIdToSelect);
            if (this.selectedNode) {
              this.selectedFolderName = this.getNodePath(this.selectedNode);
              this.selectedFolderId = this.selectedNode.data?.entityId ?? 0;
              this.expandNodeParents(this.folderNodes, newIdToSelect);
              this.autoSelectDeptAndCategoryFromNode(this.selectedNode);
              if (
                this.pickerCreatedFolderId !== null &&
                Number(newIdToSelect) === this.pickerCreatedFolderId
              ) {
                this.folderRenameDraft = this.selectedNode.label ?? '';
                this.focusPickerFolderRenameInput();
              }
            } else {
              // Hierarchy is often shallow/lazy — resolve opener folder via API.
              this.resolveMissingFolderSelection(newIdToSelect, rawFolders);
            }
          } else {
            this.syncTreeSelectionAfterReload();
            this.maybeAutoSelectFirstDepartmentFolder();
          }
          this.isLoadingFolders = false;
        },
        error: () => {
          this.isLoadingFolders = false;
          if (this.folderNodes.length === 0) {
            this.folderNodes = this.transformDepartmentLibraryToTreeNodes([]);
            this.applyFolderSearchFilter();
            this.syncTreeSelectionAfterReload();
          }
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load department folder hierarchy',
          });
        },
      });
      return;
    }

    this.documentService.getFolderHierarchy(userId).subscribe({
      next: (res: any) => {
        this.optimisticCreatedFolderNamesByParent.clear();
        // Always keep the My Files root node so the picker never renders blank.
        const rawFolders = Array.isArray(res?.data) ? res.data : [];
        this.folderNodes = this.transformToTreeNodes(rawFolders);
        this.applyFolderSearchFilter();

        if (newIdToSelect !== undefined && newIdToSelect !== null) {
          this.selectedNode = this.findNodeById(this.folderNodes, newIdToSelect);
          if (this.selectedNode) {
            this.selectedFolderName =
              this.selectedNode.data?.entityId === 0
                ? this.getFolderPickerRootLabel()
                : this.getNodePath(this.selectedNode);
            this.selectedFolderId = this.selectedNode.data?.entityId ?? 0;
            this.expandNodeParents(this.folderNodes, newIdToSelect);
            if (
              this.pickerCreatedFolderId !== null &&
              Number(newIdToSelect) === this.pickerCreatedFolderId
            ) {
              this.folderRenameDraft = this.selectedNode.label ?? '';
              this.focusPickerFolderRenameInput();
            }
          } else if (newIdToSelect === 0) {
            this.applyMyFilesSelection();
          }
        } else {
          this.syncTreeSelectionAfterReload();
        }
        this.isLoadingFolders = false;
      },
      error: () => {
        this.isLoadingFolders = false;
        // Keep the My Files root available so the user can still create/select a folder.
        if (this.folderNodes.length === 0) {
          this.folderNodes = this.transformToTreeNodes([]);
          this.applyFolderSearchFilter();
          this.applyMyFilesSelection();
        }
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load folder hierarchy',
        });
      },
    });
  }

  /** After folderNodes are replaced, restore selectedNode from selectedFolderId (PrimeNG drops stale references). */
  private syncTreeSelectionAfterReload(): void {
    const id =
      this.selectedFolderId !== null && this.selectedFolderId !== undefined
        ? Number(this.selectedFolderId)
        : 0;
    const node = this.findNodeById(this.folderNodes, id);
    if (node) {
      this.selectedNode = node;
      this.selectedFolderName =
        id === 0 ? this.getFolderPickerRootLabel() : this.getNodePath(node);
      this.selectedFolderId = id;
      if (id !== 0) {
        this.expandNodeParents(this.folderNodes, id);
      }
    } else if (id > 0) {
      // Keep opener destination; tree may be shallow until subfolders are hydrated.
      this.resolveMissingFolderSelection(id);
    } else if (this.folderPickerIsDepartment) {
      this.selectedFolderName = this.isElevatedOrgAssetsPickerUser()
        ? ''
        : this.getFolderPickerRootLabel();
      this.selectedFolderId = 0;
      this.selectedNode = null;
    } else {
      this.applyMyFilesSelection();
    }
  }

  private applyMyFilesSelection(): void {
    this.selectedFolderName = this.getFolderPickerRootLabel();
    this.selectedFolderId = 0;
    this.selectedNode = this.folderNodes.length > 0 ? this.findNodeById(this.folderNodes, 0) : null;
  }

  private getFolderPickerRootLabel(): string {
    return this.folderPickerIsDepartment ? this.orgAssetsRootLabel : 'My Files';
  }

  onFolderSearchInput(value: string): void {
    this.folderSearchTerm = String(value || '');
    this.applyFolderSearchFilter();
  }

  private applyFolderSearchFilter(): void {
    const term = this.folderSearchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredFolderNodes = [];
      return;
    }
    this.filteredFolderNodes = this.buildFilteredFolderTree(term);
  }

  private buildFilteredFolderTree(term: string): any[] {
    const filterNode = (node: any, parent: any): any | null => {
      const label = String(node?.label || '');
      const matchesSelf = label.toLowerCase().includes(term);

      const filteredChildren = (node?.children || [])
        .map((child: any) => filterNode(child, null))
        .filter((child: any) => !!child);

      // Keep node if it matches or has matching descendants.
      if (!matchesSelf && filteredChildren.length === 0) {
        return null;
      }

      const clonedNode: any = {
        ...node,
        parent,
        children: filteredChildren as any[],
        // While searching, expand matched branches so it still behaves like a normal open/close tree.
        expanded: filteredChildren.length > 0,
      };

      clonedNode.children = clonedNode.children.map((child: any) => ({ ...child, parent: clonedNode }));
      return clonedNode;
    };

    return (this.folderNodes || [])
      .map((node: any) => filterNode(node, null))
      .filter((node: any) => !!node);
  }

  /** Reset folder picker to library root. */
  clearFolderSelection(event?: Event): void {
    event?.stopPropagation();
    if (this.folderPickerIsDepartment) {
      this.selectedFolderName = this.isElevatedOrgAssetsPickerUser()
        ? ''
        : this.getFolderPickerRootLabel();
      this.selectedFolderId = 0;
      this.selectedNode = null;
    } else {
      this.applyMyFilesSelection();
    }
  }

  get showClearFolderSelection(): boolean {
    return this.selectedFolderId !== null && this.selectedFolderId !== 0;
  }

  get isFolderSearchActive(): boolean {
    return !!this.folderSearchTerm?.trim();
  }

  get activeFolderNodes(): any[] {
    return this.isFolderSearchActive ? this.filteredFolderNodes : this.folderNodes;
  }

  get showFolderTreeEmptyState(): boolean {
    return (
      !this.isLoadingFolders &&
      (!this.activeFolderNodes || this.activeFolderNodes.length === 0)
    );
  }

  get showFolderTreeLoadingState(): boolean {
    return this.isLoadingFolders && this.activeFolderNodes.length === 0;
  }

  get folderTreeEmptyMessage(): string {
    if (this.departmentTreeBlockedBySelection) {
      return this.translateService.instant('MEDIALIBRARY.uploadSelectDepartmentFirst');
    }
    if (this.isFolderSearchActive) {
      return this.translateService.instant('MEDIALIBRARY.uploadNoMatchingFolders');
    }
    if (this.folderPickerIsDepartment) {
      return this.translateService.instant('MEDIALIBRARY.uploadCreateAFolder');
    }
    return this.translateService.instant('MEDIALIBRARY.uploadNoFoldersYet');
  }

  private getResolvedDepartmentIdForUpload(): number {
    if (this.pickerFilterDepartmentId && this.pickerFilterDepartmentId > 0) {
      return this.pickerFilterDepartmentId;
    }
    if (this.libraryDepartmentId && this.libraryDepartmentId > 0) {
      return this.libraryDepartmentId;
    }
    return this.resolveLoginDepartmentId();
  }

  /** Full-access users: Org Assets root is browse-only; pick a department folder. */
  private isElevatedOrgAssetsPickerUser(): boolean {
    return this.hasFullAccess();
  }

  get needsDepartmentFolderSelectionInPicker(): boolean {
    return (
      this.folderPickerIsDepartment &&
      this.isElevatedOrgAssetsPickerUser() &&
      Number(this.selectedFolderId ?? 0) <= 0
    );
  }

  get displayFolderSelectionLabel(): string {
    if (this.needsDepartmentFolderSelectionInPicker) {
      return this.translateService.instant('MEDIALIBRARY.uploadChooseDepartmentFolder');
    }
    return String(this.selectedFolderName || this.getFolderPickerRootLabel() || '').trim();
  }

  /** Department library root (folderId 0) is a valid upload target. */
  private canUploadToDepartmentLibraryRoot(): boolean {
    return this.folderPickerIsDepartment === true;
  }

  private extractDepartmentRootFolderIdFromHierarchy(
    folders: any[],
    departmentId?: number | null
  ): number {
    for (const folder of folders || []) {
      const folderId = Number(
        folder?.folderId ?? folder?.FolderId ?? folder?.entityId ?? 0
      );
      const folderDepartmentId = Number(
        folder?.departmentId ?? folder?.DepartmentId ?? 0
      );
      const isDepartmentRoot =
        folder?.isDepartmentRoot === true || folder?.IsDepartmentRoot === true;

      if (
        isDepartmentRoot &&
        folderId > 0 &&
        (!departmentId || !folderDepartmentId || folderDepartmentId === departmentId)
      ) {
        return folderId;
      }

      const subFolders = folder?.subFolders ?? folder?.SubFolders ?? [];
      const nested = this.extractDepartmentRootFolderIdFromHierarchy(
        subFolders,
        departmentId
      );
      if (nested > 0) {
        return nested;
      }
    }

    return 0;
  }

  private resolveDepartmentRootFolderIdForUpload(): number {
    const cached = Number(this.cachedPickerDepartmentRootFolderId || 0);
    if (cached > 0) {
      return cached;
    }

    if (!this.folderPickerIsDepartment || this.folderNodes.length === 0) {
      return 0;
    }

    const root = this.folderNodes[0];
    for (const child of root?.children || []) {
      const isDepartmentRoot =
        child?.data?.isDepartmentRoot === true ||
        child?.data?.IsDepartmentRoot === true;
      const folderId = Number(
        child?.data?.entityId ?? child?.data?.folderId ?? child?.data?.FolderId ?? 0
      );
      if (isDepartmentRoot && folderId > 0) {
        return folderId;
      }
    }

    const firstChild = root?.children?.[0];
    const firstChildId = Number(firstChild?.data?.entityId ?? 0);
    if (
      !this.hasFullAccess() &&
      firstChildId > 0 &&
      root?.children?.length === 1
    ) {
      return firstChildId;
    }

    return 0;
  }

  private hasDepartmentUploadFolderSelection(): boolean {
    if (!this.folderPickerIsDepartment) {
      return true;
    }
    if (Number(this.selectedFolderId ?? 0) > 0) {
      return true;
    }
    if (this.isElevatedOrgAssetsPickerUser()) {
      return false;
    }
    return this.canUploadToDepartmentLibraryRoot();
  }

  /** Parent folder id for API: null = under My Files root. */
  private getParentIdForNewFolder(): number | null {
    const fromState =
      this.selectedFolderId !== null && this.selectedFolderId !== undefined
        ? Number(this.selectedFolderId)
        : NaN;
    if (!Number.isNaN(fromState)) {
      return fromState === 0 ? null : fromState;
    }
    const raw =
      this.selectedNode?.data?.entityId ??
      this.selectedNode?.data?.folderId ??
      this.selectedNode?.data?.FolderId;
    if (raw === undefined || raw === null) {
      const p = this.parentId;
      return p === null || p === undefined || p === 0 ? null : p;
    }
    const n = Number(raw);
    return n === 0 ? null : n;
  }

  private findNodeById(nodes: any[], id: number): any {
    const target = Number(id);
    for (const node of nodes) {
      if (node.data) {
        const eid = node.data.entityId ?? node.data.folderId ?? node.data.FolderId;
        if (eid !== undefined && eid !== null && Number(eid) === target) {
          return node;
        }
      }
      if (node.children) {
        const found = this.findNodeById(node.children, id);
        if (found) return found;
      }
    }
    return null;
  }

  /** True when folder payload is a Category root folder. */
  private isCategoryRootFolderData(data: any): boolean {
    if (!data) {
      return false;
    }
    return data.isCategoryRoot === true || data.IsCategoryRoot === true;
  }

  private getFolderIdFromData(data: any): number | null {
    const folderId = Number(data?.entityId ?? data?.folderId ?? data?.FolderId);
    return Number.isFinite(folderId) && folderId > 0 ? folderId : null;
  }

  private getFolderDisplayLabel(folder: any): string {
    const en = String(folder?.folderNameEn ?? folder?.FolderNameEn ?? '').trim();
    const ar = String(folder?.folderNameAr ?? folder?.FolderNameAr ?? '').trim();
    const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
    return (useArabic ? ar || en : en || ar) || `Folder`;
  }

  /**
   * Opener folder is often missing from the shallow hierarchy response (children load lazily).
   * Resolve path + synthetic selectedNode via getFolderById so Category/Department still bind.
   */
  private resolveMissingFolderSelection(folderId: number, rawFolders?: any[]): void {
    const id = Number(folderId);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }

    this.selectedFolderId = id;
    this.currentFolderId = id;

    const rawLabel = rawFolders?.length
      ? this.findFolderLabelInRawHierarchy(rawFolders, id)
      : null;
    if (rawLabel) {
      this.selectedFolderName = `${this.getFolderPickerRootLabel()} / ${rawLabel}`;
    }

    this.sidebarService.setContext('org-assets');
    this.sidebarService
      .resolveFolderPathLabel$(id)
      .pipe(take(1), catchError(() => of('')))
      .subscribe((path) => {
        const label = String(path || '').trim();
        if (label) {
          this.selectedFolderName = label;
        } else if (!this.selectedFolderName) {
          this.selectedFolderName = this.getFolderPickerRootLabel();
        }
      });

    this.buildSyntheticNodeChainFromApi$(id)
      .pipe(take(1))
      .subscribe((leaf) => {
        if (leaf) {
          this.selectedNode = leaf;
          if (!String(this.selectedFolderName || '').trim() || this.selectedFolderName === this.getFolderPickerRootLabel()) {
            this.selectedFolderName = this.getNodePath(leaf);
          }
          this.mergeSyntheticNodeChainIntoTree(leaf);
          this.autoSelectDeptAndCategoryFromNode(leaf);
          return;
        }

        this.selectedNode = null;
        this.autoSelectCategoryFromFolderId(id, rawFolders);
      });
  }

  /** Fetch folder → parents (root-first) via getFolderById. */
  private fetchFolderAncestorChain$(folderId: number): Observable<any[]> {
    const maxDepth = 25;
    const loadOne = (id: number, depth: number): Observable<any[]> => {
      if (!Number.isFinite(id) || id <= 0 || depth > maxDepth) {
        return of([]);
      }
      return this.documentService.getFolderById(id).pipe(
        take(1),
        switchMap((res: any) => {
          const folder = res?.data;
          if (!folder) {
            return of([]);
          }
          const parentId = Number(folder.parentId ?? folder.ParentId ?? 0);
          if (Number.isFinite(parentId) && parentId > 0) {
            return loadOne(parentId, depth + 1).pipe(
              map((ancestors) => [...ancestors, folder])
            );
          }
          return of([folder]);
        }),
        catchError(() => of([]))
      );
    };
    return loadOne(folderId, 0);
  }

  private buildSyntheticNodeChainFromApi$(folderId: number): Observable<any | null> {
    return this.fetchFolderAncestorChain$(folderId).pipe(
      map((folders) => {
        if (!folders.length) {
          return null;
        }
        let parent: any = null;
        let leaf: any = null;
        for (const folder of folders) {
          const id = Number(
            folder?.folderId ?? folder?.FolderId ?? folder?.entityId ?? folder?.EntityId ?? 0
          );
          if (!Number.isFinite(id) || id <= 0) {
            continue;
          }
          const node: any = {
            label: this.getFolderDisplayLabel(folder),
            data: {
              ...folder,
              entityId: id,
              folderId: id,
              isCategoryRoot:
                folder?.isCategoryRoot === true || folder?.IsCategoryRoot === true
            },
            children: [] as any[],
            parent,
            expanded: true
          };
          if (parent) {
            parent.children = [node];
          }
          parent = node;
          leaf = node;
        }
        return leaf;
      })
    );
  }

  /** Attach the synthetic ancestor chain under the picker root so the tree can highlight it. */
  private mergeSyntheticNodeChainIntoTree(leaf: any): void {
    if (!leaf || !this.folderNodes?.length) {
      return;
    }
    const root = this.folderNodes[0];
    if (!root) {
      return;
    }

    const chain: any[] = [];
    let current = leaf;
    while (current) {
      chain.unshift(current);
      current = current.parent;
    }
    if (!chain.length) {
      return;
    }

    let parentNode = root;
    for (const synthetic of chain) {
      const id = Number(synthetic?.data?.entityId ?? 0);
      if (!Number.isFinite(id) || id <= 0) {
        continue;
      }
      let existing = (parentNode.children || []).find((child: any) => {
        const childId = Number(
          child?.data?.entityId ?? child?.data?.folderId ?? child?.data?.FolderId ?? 0
        );
        return childId === id;
      });
      if (!existing) {
        existing = {
          ...synthetic,
          parent: parentNode,
          children: synthetic.children ? [...synthetic.children] : [],
          expanded: true
        };
        parentNode.children = [...(parentNode.children || []), existing];
      } else {
        existing.expanded = true;
        existing.data = {
          ...existing.data,
          ...synthetic.data,
          isCategoryRoot:
            existing.data?.isCategoryRoot === true ||
            synthetic.data?.isCategoryRoot === true
        };
      }
      parentNode = existing;
    }

    this.selectedNode = parentNode;
    this.expandNodeParents(this.folderNodes, Number(leaf?.data?.entityId ?? 0));
    this.applyFolderSearchFilter();
  }

  /**
   * Walk selected folder → ancestors and collect folder ids where isCategoryRoot === true
   * (nearest first).
   */
  private getCategoryRootFolderIdsFromSelection(): number[] {
    let node = this.selectedNode;
    if (!node) {
      const selectedId = Number(this.selectedFolderId);
      if (Number.isFinite(selectedId) && selectedId > 0) {
        node = this.findNodeById(this.folderNodes, selectedId);
      }
    }
    if (!node) {
      return [];
    }

    const ids: number[] = [];
    let current = node;
    while (current) {
      const data = current.data;
      if (data && this.isCategoryRootFolderData(data)) {
        const id = this.getFolderIdFromData(data);
        if (id !== null && !ids.includes(id)) {
          ids.push(id);
        }
      }
      current = current.parent;
    }
    return ids;
  }

  /** Categories whose entityId matches one of the given category-root folder ids. */
  private getCategoriesLinkedToFolderIds(
    folderIds: number[]
  ): Array<{ label: string; value: number; departmentId: number | null; entityId: number }> {
    if (!folderIds?.length || !this.allUploadCategories?.length) {
      return [];
    }
    const idSet = new Set(folderIds.map(Number));
    return this.allUploadCategories.filter(
      (cat) => cat.entityId && idSet.has(Number(cat.entityId))
    );
  }

  private autoSelectDeptAndCategoryFromNode(node: any): void {
    if (!node) {
      return;
    }

    let current = node;
    let foundDeptId: number | null = null;
    const ancestorFolderIds: number[] = [];
    const categoryRootFolderIds: number[] = [];
    const ancestorCategoryIds: number[] = [];
    const ancestorFolderNames: string[] = [];

    while (current) {
      const data = current.data;
      if (data) {
        const deptId = data.departmentId ?? data.DepartmentId;
        if (deptId && Number(deptId) > 0 && foundDeptId === null) {
          foundDeptId = Number(deptId);
        }
        const folderId = this.getFolderIdFromData(data);
        if (folderId !== null) {
          ancestorFolderIds.push(folderId);
          if (this.isCategoryRootFolderData(data) && !categoryRootFolderIds.includes(folderId)) {
            categoryRootFolderIds.push(folderId);
          }
        }
        const categoryId = this.extractCategoryIdFromFolderData(data);
        if (categoryId !== null) {
          ancestorCategoryIds.push(categoryId);
        }
      }
      const label = String(current.label ?? '').trim();
      if (label) {
        ancestorFolderNames.push(label.toLowerCase());
      }
      current = current.parent;
    }

    if (foundDeptId !== null) {
      this.selectedUploadDepartmentId = foundDeptId;
      this.onUploadDepartmentChange();
    } else if (
      !this.selectedUploadDepartmentId &&
      this.libraryDepartmentId &&
      this.libraryDepartmentId > 0
    ) {
      this.selectedUploadDepartmentId = this.libraryDepartmentId;
      this.onUploadDepartmentChange();
    }

    const foundCategoryId = this.resolveCategoryIdFromFolderChain(
      ancestorCategoryIds,
      ancestorFolderIds,
      ancestorFolderNames,
      categoryRootFolderIds
    );

    if (foundCategoryId !== null) {
      this.setSelectedCategoryIdFromFolderContext(foundCategoryId);
      if (this.enableWorkflow) {
        this.prefillWorkflowFromCategoryOrDepartment();
      }
    }
  }

  /**
   * When the tree node is missing, still map Category from the destination folder id.
   * Prefer isCategoryRoot flags from the tree or raw hierarchy before matching entityId.
   */
  private autoSelectCategoryFromFolderId(folderId: number, rawFolders?: any[]): void {
    const id = Number(folderId);
    if (!Number.isFinite(id) || id <= 0 || !this.allUploadCategories?.length) {
      return;
    }

    const node = this.findNodeById(this.folderNodes, id);
    if (node) {
      this.autoSelectDeptAndCategoryFromNode(node);
      return;
    }

    if (this.selectedNode && this.getFolderIdFromData(this.selectedNode.data) === id) {
      this.autoSelectDeptAndCategoryFromNode(this.selectedNode);
      return;
    }

    const categoryRootFolderIds = this.getCategoryRootFolderIdsFromRawHierarchy(rawFolders || [], id);
    const foundCategoryId = this.resolveCategoryIdFromFolderChain(
      [],
      [id],
      [],
      categoryRootFolderIds
    );

    if (foundCategoryId !== null) {
      this.applyResolvedCategorySelection(foundCategoryId);
      return;
    }

    // Shallow hierarchy: walk parents via getFolderById for isCategoryRoot.
    this.resolveCategoryFromFolderApi$(id)
      .pipe(take(1))
      .subscribe((categoryId) => {
        if (categoryId !== null) {
          this.applyResolvedCategorySelection(categoryId);
        }
      });
  }

  private applyResolvedCategorySelection(foundCategoryId: number): void {
    this.setSelectedCategoryIdFromFolderContext(foundCategoryId);
    const matched = this.allUploadCategories.find((cat) => cat.value === foundCategoryId);
    if (
      matched?.departmentId &&
      matched.departmentId > 0 &&
      !this.selectedUploadDepartmentId
    ) {
      this.selectedUploadDepartmentId = matched.departmentId;
      this.onUploadDepartmentChange();
    }
    if (this.enableWorkflow) {
      this.prefillWorkflowFromCategoryOrDepartment();
    }
  }

  /** Walk folder → parents via API until isCategoryRoot, then match category.entityId. */
  private resolveCategoryFromFolderApi$(folderId: number): Observable<number | null> {
    return this.fetchFolderAncestorChain$(folderId).pipe(
      map((folders) => {
        if (!folders.length || !this.allUploadCategories?.length) {
          return null;
        }
        // folders are root-first; prefer nearest category root (scan from end).
        for (let i = folders.length - 1; i >= 0; i--) {
          const folder = folders[i];
          if (!this.isCategoryRootFolderData(folder)) {
            continue;
          }
          const rootId = Number(
            folder?.folderId ?? folder?.FolderId ?? folder?.entityId ?? folder?.EntityId ?? 0
          );
          if (!Number.isFinite(rootId) || rootId <= 0) {
            continue;
          }
          const linked = this.allUploadCategories.find(
            (cat) => cat.entityId && Number(cat.entityId) === rootId
          );
          if (linked) {
            return linked.value;
          }
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  private findFolderLabelInRawHierarchy(folders: any[], folderId: number): string | null {
    const path = this.findFolderPathInRawHierarchy(folders, folderId);
    if (!path?.length) {
      return null;
    }
    const folder = path[path.length - 1];
    const en = String(folder?.folderNameEn ?? folder?.FolderNameEn ?? '').trim();
    const ar = String(folder?.folderNameAr ?? folder?.FolderNameAr ?? '').trim();
    const useArabic = (this.translateService.currentLang || 'en').toLowerCase().startsWith('ar');
    return (useArabic ? ar || en : en || ar) || null;
  }

  /** Path from root → target folder in raw hierarchy (inclusive). */
  private findFolderPathInRawHierarchy(
    folders: any[],
    folderId: number,
    path: any[] = []
  ): any[] | null {
    const target = Number(folderId);
    for (const folder of folders || []) {
      const nextPath = [...path, folder];
      const id = Number(
        folder?.folderId ?? folder?.FolderId ?? folder?.entityId ?? folder?.EntityId ?? 0
      );
      if (Number.isFinite(id) && id === target) {
        return nextPath;
      }
      const sub = folder?.subFolders ?? folder?.SubFolders;
      if (Array.isArray(sub) && sub.length) {
        const nested = this.findFolderPathInRawHierarchy(sub, target, nextPath);
        if (nested) {
          return nested;
        }
      }
    }
    return null;
  }

  /** Nearest-first category-root folder ids along the raw hierarchy path to folderId. */
  private getCategoryRootFolderIdsFromRawHierarchy(folders: any[], folderId: number): number[] {
    const path = this.findFolderPathInRawHierarchy(folders, folderId);
    if (!path?.length) {
      return [];
    }
    const ids: number[] = [];
    for (let i = path.length - 1; i >= 0; i--) {
      const folder = path[i];
      if (!this.isCategoryRootFolderData(folder)) {
        continue;
      }
      const id = Number(folder?.folderId ?? folder?.FolderId ?? 0);
      if (Number.isFinite(id) && id > 0 && !ids.includes(id)) {
        ids.push(id);
      }
    }
    return ids;
  }

  /** Category id carried on a folder payload, under any of the API's field spellings. */
  private extractCategoryIdFromFolderData(data: any): number | null {
    const categoryId = Number(
      data?.categoryId ??
        data?.CategoryId ??
        data?.ref_Category_Id ??
        data?.refCategoryId ??
        data?.documentCategoryId ??
        data?.DocumentCategoryId
    );
    return Number.isFinite(categoryId) && categoryId > 0 ? categoryId : null;
  }

  /**
   * Resolve the Category dropdown value for a selected folder, nearest ancestor first:
   * 1) isCategoryRoot folder id ↔ category.entityId → categoryId
   * 2) folder's own categoryId
   * 3) plain entityId match
   * 4) name match
   */
  private resolveCategoryIdFromFolderChain(
    ancestorCategoryIds: number[],
    ancestorFolderIds: number[],
    ancestorFolderNames: string[],
    categoryRootFolderIds: number[] = []
  ): number | null {
    if (!this.allUploadCategories?.length) {
      return null;
    }

    for (const folderId of categoryRootFolderIds) {
      const linked = this.allUploadCategories.find(
        (cat) => cat.entityId && Number(cat.entityId) === folderId
      );
      if (linked) {
        return linked.value;
      }
    }

    for (const categoryId of ancestorCategoryIds) {
      if (this.allUploadCategories.some((cat) => cat.value === categoryId)) {
        return categoryId;
      }
    }

    for (const folderId of ancestorFolderIds) {
      const linked = this.allUploadCategories.find(
        (cat) => cat.entityId && Number(cat.entityId) === folderId
      );
      if (linked) {
        return linked.value;
      }
    }

    for (const folderName of ancestorFolderNames) {
      const byName = this.allUploadCategories.find(
        (cat) => String(cat.label || '').trim().toLowerCase() === folderName
      );
      if (byName) {
        return byName.value;
      }
    }

    return null;
  }

  /**
   * Reverse of folder → category: pick Category in the dropdown and select its
   * linked department folder in the Select a Folder tree (category.entityId).
   */
  private autoSelectFolderFromCategory(categoryId: number | null): void {
    if (this.editMode || this.folderMetadataEditMode || this.isCategoryLocked) {
      return;
    }

    const id = Number(categoryId);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }

    const category = this.allUploadCategories.find((cat) => cat.value === id);
    if (!category) {
      return;
    }

    const linkedFolderId = Number(category.entityId);
    const hasLinkedFolder = Number.isFinite(linkedFolderId) && linkedFolderId > 0;

    // Already on the category folder — keep path/selection in sync without reloading.
    if (hasLinkedFolder && Number(this.selectedFolderId ?? 0) === linkedFolderId && this.selectedNode) {
      return;
    }

    if (category.departmentId && category.departmentId > 0) {
      this.selectedUploadDepartmentId = category.departmentId;
      this.libraryDepartmentId = category.departmentId;
      if (!this.editMode) {
        this.loadNextDocumentNumbers(this.libraryDepartmentId);
      }
      if (this.shareWithRecipients) {
        this.loadShareRecipientsByDepartment();
      }
    }

    if (this.folderNodes.length === 0) {
      if (!hasLinkedFolder) {
        return;
      }
      // Tree not loaded yet — load hierarchy and select the category folder.
      this.loadFolderHierarchy(linkedFolderId);
      if (!this.isFolderTreeVisible) {
        this.isFolderTreeVisible = true;
      }
      return;
    }

    const matchedNode = this.findCategoryFolderNode(category, hasLinkedFolder ? linkedFolderId : null);
    if (!matchedNode) {
      if (!hasLinkedFolder) {
        return;
      }
      // Folder may be outside the currently filtered tree; reload and select.
      this.loadFolderHierarchy(linkedFolderId);
      if (!this.isFolderTreeVisible) {
        this.isFolderTreeVisible = true;
      }
      return;
    }

    const folderId = Number(
      matchedNode.data?.entityId ?? matchedNode.data?.folderId ?? matchedNode.data?.FolderId ?? 0
    );
    if (!Number.isFinite(folderId) || folderId <= 0) {
      return;
    }

    this.selectedNode = matchedNode;
    this.selectedFolderId = folderId;
    this.currentFolderId = folderId;
    this.selectedFolderName = this.getNodePath(matchedNode);
    this.expandNodeParents(this.folderNodes, folderId);
    if (this.newlyCreatedFolderId > 0 && folderId !== Number(this.newlyCreatedFolderId)) {
      this.isNewFolderOnUpload = false;
    }
    this.recheckDuplicatesForCurrentSelection();
  }

  /** Locate the folder backing a category: by linked folder id, folder categoryId, then name. */
  private findCategoryFolderNode(
    category: { label: string; value: number; entityId: number },
    linkedFolderId: number | null
  ): any | null {
    if (linkedFolderId) {
      const byId = this.findNodeById(this.folderNodes, linkedFolderId);
      if (byId) {
        return byId;
      }
    }

    const byCategoryId = this.findNodeByPredicate(
      this.folderNodes,
      (node) => this.extractCategoryIdFromFolderData(node?.data) === category.value
    );
    if (byCategoryId) {
      return byCategoryId;
    }

    const label = String(category.label || '').trim().toLowerCase();
    if (!label) {
      return null;
    }
    return this.findNodeByPredicate(
      this.folderNodes,
      (node) => String(node?.label ?? '').trim().toLowerCase() === label
    );
  }

  private findNodeByPredicate(nodes: any[], predicate: (node: any) => boolean): any | null {
    for (const node of nodes || []) {
      if (predicate(node)) {
        return node;
      }
      if (node?.children?.length) {
        const found = this.findNodeByPredicate(node.children, predicate);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private applyInitialFolderSelection(folderIdFromState?: number): void {
    const normalizedFolderId =
      typeof folderIdFromState === 'number' && Number.isFinite(folderIdFromState) && folderIdFromState > 0
        ? folderIdFromState
        : 0;

    this.currentFolderId = normalizedFolderId;
    this.selectedFolderId = normalizedFolderId;

    if (normalizedFolderId === 0) {
      this.selectedFolderName =
        this.folderPickerIsDepartment && this.isElevatedOrgAssetsPickerUser()
          ? ''
          : this.getFolderPickerRootLabel();
      this.selectedNode =
        !this.folderPickerIsDepartment && this.folderNodes.length > 0
          ? this.findNodeById(this.folderNodes, 0)
          : null;
      if (this.folderNodes.length === 0) {
        this.loadFolderHierarchy(0);
      }
      return;
    }

    if (this.folderNodes.length > 0) {
      const matchedNode = this.findNodeById(this.folderNodes, normalizedFolderId);
      if (matchedNode) {
        this.selectedNode = matchedNode;
        this.selectedFolderName = this.getNodePath(matchedNode);
        this.expandNodeParents(this.folderNodes, normalizedFolderId);
        return;
      }
    }

    // Load tree and resolve selected folder label/path when opened from a nested folder.
    this.loadFolderHierarchy(normalizedFolderId);
  }

  private expandNodeParents(nodes: any[], targetId: number): boolean {
    const target = Number(targetId);
    for (const node of nodes) {
      if (node.data) {
        const eid = node.data.entityId ?? node.data.folderId ?? node.data.FolderId;
        if (eid !== undefined && eid !== null && Number(eid) === target) {
          return true;
        }
      }
      if (node.children) {
        if (this.expandNodeParents(node.children, targetId)) {
          node.expanded = true;
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Build full folder path like "My Files / birds / test-folder" from a tree node
   */
  private getNodePath(node: any): string {
    const labels: string[] = [];
    let current: any = node;

    while (current) {
      if (current.label) {
        labels.unshift(current.label);
      }
      current = current.parent;
    }

    return labels.join(' / ');
  }

  onNodeSelect(event: any): void {
    const node = event?.node;
    if (!node) {
      return;
    }
    const rawId = node.data?.entityId ?? node.data?.folderId ?? node.data?.FolderId;
    const id = rawId === undefined || rawId === null ? 0 : Number(rawId);
    if (
      this.folderPickerIsDepartment &&
      this.isElevatedOrgAssetsPickerUser() &&
      (Number.isNaN(id) || id <= 0)
    ) {
      this.selectedNode = null;
      this.selectedFolderId = 0;
      this.selectedFolderName = '';
      this.messageService.add({
        key: 'custom',
        severity: 'warn',
        summary: 'Select Folder',
        detail: `Choose a department folder in ${this.orgAssetsRootLabel} before uploading.`,
        life: 5000
      });
      return;
    }
    this.selectedFolderId = Number.isNaN(id) ? 0 : id;
    this.currentFolderId = this.selectedFolderId;
    if (this.newlyCreatedFolderId > 0 && Number(this.selectedFolderId || 0) !== Number(this.newlyCreatedFolderId)) {
      this.isNewFolderOnUpload = false;
    }
    this.selectedFolderName =
      this.selectedFolderId === 0
        ? this.getFolderPickerRootLabel()
        : this.getNodePath(node);
    if (
      this.pickerCreatedFolderId !== null &&
      this.selectedFolderId === this.pickerCreatedFolderId
    ) {
      this.folderRenameDraft = node.label ?? '';
    }
    if (this.folderPickerIsDepartment) {
      this.autoSelectDeptAndCategoryFromNode(node);
    }
    this.recheckDuplicatesForCurrentSelection();
  }

  private getTreeNodeFolderId(node: any): number {
    const raw = node?.data?.entityId ?? node?.data?.folderId ?? node?.data?.FolderId;
    if (raw === undefined || raw === null) {
      return 0;
    }
    const n = Number(raw);
    return Number.isNaN(n) ? 0 : n;
  }

  /** Inline rename input for the folder currently being edited in picker tree. */
  showPickerFolderRenameInput(node: any): boolean {
    if (this.pickerCreatedFolderId === null) {
      return false;
    }
    const nid = this.getTreeNodeFolderId(node);
    return nid === this.pickerCreatedFolderId;
  }

  canRenameFolderNode(node: any): boolean {
    return this.getTreeNodeFolderId(node) > 0 && !this.isFolderSearchActive;
  }

  // startPickerFolderRename(node: any, event?: Event): void {
  //   event?.stopPropagation();
  //   if (!this.canRenameFolderNode(node)) {
  //     return;
  //   }
  //   this.selectedNode = node;
  //   this.selectedFolderId = this.getTreeNodeFolderId(node);
  //   this.currentFolderId = this.selectedFolderId;
  //   this.selectedFolderName = this.selectedFolderId === 0 ? 'My Files' : this.getNodePath(node);
  //   this.pickerCreatedFolderId = this.selectedFolderId;
  //   this.folderRenameDraft = node?.label ?? '';
  // }

  isPickerFolderSelected(node: any): boolean {
    return this.getTreeNodeFolderId(node) === Number(this.selectedFolderId ?? 0);
  }

  canShowPickerRenameButton(node: any): boolean {
    const folderId = this.getTreeNodeFolderId(node);
    return folderId > 0 && this.isPickerFolderSelected(node) && !this.showPickerFolderRenameInput(node);
  }

  startPickerFolderRename(node: any, event?: Event): void {
    event?.stopPropagation();
    const folderId = this.getTreeNodeFolderId(node);
    if (folderId <= 0) {
      return;
    }

    this.selectedNode = node;
    this.selectedFolderId = folderId;
    this.currentFolderId = folderId;
    this.selectedFolderName = this.getNodePath(node);
    this.pickerCreatedFolderId = folderId;
    this.folderRenameDraft = node?.label ?? '';
    this.focusPickerFolderRenameInput();
  }

  private focusPickerFolderRenameInput(): void {
    setTimeout(() => {
      const input = this.pickerFolderRenameInputs?.first?.nativeElement;
      if (!input) {
        return;
      }
      input.focus();
      input.select();
    });
  }

  private getParentFolderIdForTreeNode(node: any): number {
    const parentNode = node?.parent;
    if (!parentNode?.data) {
      return 0;
    }
    const pe =
      parentNode.data.entityId ??
      parentNode.data.folderId ??
      parentNode.data.FolderId;
    if (pe === undefined || pe === null) {
      return 0;
    }
    const n = Number(pe);
    return Number.isNaN(n) ? 0 : n;
  }

  private escapeRegex(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private getAutoSuffixBaseName(name: string): string {
    return (name || '').trim().replace(/\s\(\d+\)$/i, '');
  }

  private rememberOptimisticCreatedFolderName(parentId: number, folderName: string): void {
    const normalizedParentId = Number(parentId || 0);
    const normalizedName = (folderName || '').trim();
    if (!normalizedName) {
      return;
    }

    const existing = this.optimisticCreatedFolderNamesByParent.get(normalizedParentId) ?? new Set<string>();
    existing.add(normalizedName);
    this.optimisticCreatedFolderNamesByParent.set(normalizedParentId, existing);
  }

  private getSiblingFolderLabels(parentId: number): string[] {
    const normalizedParentId = Number(parentId || 0);
    const parentNode = this.findNodeById(this.folderNodes, normalizedParentId);
    const siblings = parentNode?.children ?? [];
    return siblings
      .map((child: any) => (child?.label || '').toString().trim())
      .filter((label: string) => !!label);
  }

  private hasDuplicateFolderNameInParent(name: string, parentId: number, excludeFolderId?: number): boolean {
    const normalizedName = (name || '').trim().toLowerCase();
    if (!normalizedName) {
      return false;
    }

    const normalizedExcludeId = excludeFolderId ?? null;
    const siblings = (this.findNodeById(this.folderNodes, parentId)?.children ?? [])
      .filter((child: any) => {
        if (normalizedExcludeId === null) {
          return true;
        }
        return this.getTreeNodeFolderId(child) !== normalizedExcludeId;
      })
      .map((child: any) => (child?.label || '').toString().trim().toLowerCase())
      .filter((label: string) => !!label);
    const optimisticSiblings = Array.from(
      this.optimisticCreatedFolderNamesByParent.get(Number(parentId || 0)) ?? []
    )
      .map((label: string) => label.trim().toLowerCase())
      .filter((label: string) => !!label);

    return [...siblings, ...optimisticSiblings].includes(normalizedName);
  }

  private buildUniqueFolderName(
    desiredName: string,
    parentId: number,
    excludeFolderId?: number
  ): string {
    const normalizedDesired = desiredName.trim();
    const baseName = this.getAutoSuffixBaseName(normalizedDesired) || normalizedDesired;
    const normalizedExcludeId = excludeFolderId ?? null;
    const persistedSiblings = (this.findNodeById(this.folderNodes, parentId)?.children ?? [])
      .filter((child: any) => {
        if (normalizedExcludeId === null) return true;
        const childId = this.getTreeNodeFolderId(child);
        return childId !== normalizedExcludeId;
      })
      .map((child: any) => (child?.label || '').toString().trim())
      .filter((label: string) => !!label);
    const optimisticSiblings = Array.from(
      this.optimisticCreatedFolderNamesByParent.get(Number(parentId || 0)) ?? []
    )
      .map((label: string) => label.trim())
      .filter((label: string) => !!label);
    const effectiveSiblings = [...persistedSiblings, ...optimisticSiblings];

    const escapedBase = this.escapeRegex(baseName);
    const suffixRegex = new RegExp(`^${escapedBase}(?: \\((\\d+)\\))?$`, 'i');
    const existingIndexes = new Set<number>();

    effectiveSiblings.forEach((name: string) => {
      const match = name.match(suffixRegex);
      if (!match) return;
      if (!match[1]) {
        existingIndexes.add(0);
        return;
      }
      existingIndexes.add(Number(match[1]));
    });

    if (!existingIndexes.has(0)) {
      return baseName;
    }

    let nextIndex = 1;
    while (existingIndexes.has(nextIndex)) {
      nextIndex++;
    }
    return `${baseName} (${nextIndex})`;
  }

  syncPickerFolderRenameDraftFromNode(): void {
    if (this.selectedNode) {
      this.folderRenameDraft = this.selectedNode.label ?? '';
    }
  }

  cancelPickerFolderRename(): void {
    this.syncPickerFolderRenameDraftFromNode();
    this.pickerCreatedFolderId = null;
  }

  /** Collapse folder tree after a successful rename (e.g. user clicked Select). */
  onFolderTreeSelectButtonClick(event: Event): void {
    event.stopPropagation();
    if (!this.selectedNode || this.needsDepartmentFolderSelectionInPicker) {
      return;
    }
    const renaming = this.pickerCreatedFolderId !== null;
    if (renaming) {
      const trimmed = this.folderRenameDraft.trim();
      const currentLabel = (this.selectedNode.label ?? '').trim();
      if (trimmed !== currentLabel) {
        this.commitPickerFolderRename({ closeTreeOnSuccess: true });
        return;
      }
      this.pickerCreatedFolderId = null;
    }
    this.isFolderTreeVisible = false;
  }

  commitPickerFolderRename(options?: { closeTreeOnSuccess?: boolean }): void {
    if (this.pickerCreatedFolderId === null || !this.selectedNode) {
      return;
    }
    const closeTreeOnSuccess = options?.closeTreeOnSuccess === true;
    const folderId = this.pickerCreatedFolderId;
    const trimmed = this.folderRenameDraft.trim();
    if (!trimmed) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'Folder name cannot be empty',
        life: 4000,
      });
      this.folderRenameDraft = this.selectedNode.label ?? '';
      return;
    }
    const currentLabel = (this.selectedNode.label ?? '').trim();
    if (trimmed === currentLabel) {
      this.pickerCreatedFolderId = null;
      if (closeTreeOnSuccess) {
        this.isFolderTreeVisible = false;
      }
      return;
    }
    const parentId = this.getParentFolderIdForTreeNode(this.selectedNode);
    if (!this.validateFolderName(trimmed)) {
      this.folderRenameDraft = this.selectedNode.label ?? '';
      return;
    }
    if (this.hasDuplicateFolderNameInParent(trimmed, parentId, folderId)) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'A folder with this name already exists in this location.',
        life: 5000,
      });
      return;
    }
    const payload = {
      folderId,
      folderNameEn: trimmed,
      folderNameAr: trimmed,
      userId: Number(localStorage.getItem('userId')),
      parentId,
      renameOnly:true
    };
    this.documentService.updateFolder(folderId, payload).subscribe({
      next: (res: any) => {
        if (res.httpStatus === 200) {
          this.messageService.add({
            key: 'custom',
            severity: 'success',
            summary: '',
            detail: 'Folder renamed successfully',
            life: 3000,
          });
          this.pickerCreatedFolderId = null;
          this.folderRenameDraft = trimmed;
          this.sidebarService.triggerFolderHierarchyRefresh();
          this.loadFolderHierarchy(folderId);
          if (closeTreeOnSuccess) {
            this.isFolderTreeVisible = false;
          }
        } else {
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: '',
            detail: res.errorMessage || 'Could not rename folder',
            life: 5000,
          });
          this.folderRenameDraft = this.selectedNode?.label ?? '';
        }
      },
      error: (error: any) => {
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: '',
          detail: error?.error?.errorMessage || 'Could not rename folder',
          life: 5000,
        });
        this.folderRenameDraft = this.selectedNode?.label ?? '';
      },
    });
  }

  openNewFolderDialog(): void {
    if (this.isCreatingFolder) {
      return;
    }
    // Create instantly with default label, then keep row in inline rename mode.
    const parentId = this.getParentIdForNewFolder();
    const normalizedParentId = parentId ?? 0;
    this.folderNameDraft = this.buildUniqueFolderName('New Folder', normalizedParentId);
    this.saveNewFolder();
  }

  private transformToTreeNodes(folders: any[]): any[] {
    const root: any = {
      label: 'My Files',
      expanded: true,
      data: { entityId: 0 },
      children: [],
      parent: null as any,
    };

    const mapFoldersToNodes = (folderList: any[], parentNode: any): any[] => {
      return folderList.map((f) => {
        const id = f.folderId ?? f.FolderId;
        const sub = f.subFolders ?? f.SubFolders;
        const node: any = {
          label: f.folderNameEn,
          data: { ...f, entityId: id },
          children: [] as any[],
          parent: parentNode,
        };
        node.children =
          sub && Array.isArray(sub) && sub.length > 0 ? mapFoldersToNodes(sub, node) : [];
        return node;
      });
    };

    root.children = mapFoldersToNodes(folders, root);
    return [root];
  }

  private getSelectedEventId(): number | null {
    if (!this.selectedEvent) {
      return null;
    }
    if (typeof this.selectedEvent === 'object' && !Array.isArray(this.selectedEvent)) {
      const id = Number(this.selectedEvent.eventId);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }
    return null;
  }

  saveNewFolder(): void {
    if (this.isCreatingFolder) {
      return;
    }

    const trimmedName = this.folderNameDraft.trim();
    if (!trimmedName) return;

    const parentId = this.getParentIdForNewFolder();
    const destinationParentId = parentId && parentId > 0 ? parentId : 0;
    if (!this.validateFolderName(trimmedName)) {
      return;
    }
    if (this.hasDuplicateFolderNameInParent(trimmedName, destinationParentId)) {
      this.messageService.add({
        key: 'custom',
        severity: 'error',
        summary: '',
        detail: 'A folder with this name already exists in this location.',
      });
      return;
    }

    const effectiveLibraryType = this.getUploadDestinationLibraryType();
    const payload = {
      parentId: destinationParentId > 0 ? destinationParentId : -1,
      folderNameEn: trimmedName,
      folderNameAr: trimmedName,
      // Folder metadata is applied after the first upload, not on create.
      userId: Number(localStorage.getItem('userId')),
      folderId: destinationParentId,
      libraryType: effectiveLibraryType,
      departmentId: this.getUploadDestinationDepartmentId(destinationParentId) ?? -1,
      categoryId: this.selectedCategoryId ?? null,
      IsUploaded: true,
    };

    this.isCreatingFolder = true;
    this.documentService.createFolder(payload).pipe(
      finalize(() => {
        this.isCreatingFolder = false;
      })
    ).subscribe({
      next: (res: any) => {
        if (res.httpStatus === 200) {
          const newId = res.data.FolderId;
          this.rememberOptimisticCreatedFolderName(destinationParentId, trimmedName);
          this.showNewFolderDialog = false;
          this.newlyCreatedFolderId = newId;
          this.isNewFolderOnUpload = true;
          this.folderNameDraft = trimmedName;
          this.pickerCreatedFolderId = newId;
          this.messageService.add({
            key: 'custom',
            severity: 'success',
            summary: '',
            detail: 'Folder created successfully',
          });
          this.sidebarService.triggerFolderHierarchyRefresh(); // Sidebar getFolderHierarchy in background
          this.loadFolderHierarchy(newId); // Dialog tree: refresh and select new folder
        } else {
          this.messageService.add({
            key: 'custom',
            severity: 'error',
            summary: '',
            detail: res.errorMessage,
          });
        }
      },
      error: (error: any) => {
        this.messageService.add({
          key: 'custom',
          severity: 'error',
          summary: '',
          detail: error?.error?.errorMessage || 'Error creating folder',
        });
      },
    });
  }


  private getQuotaPartsFromBytes(bytes: number) {
    const eb = bytes / this.bytesPerEb;
    if (eb >= 1) return this.formatQuotaNumber(eb)+" " +'EB' ;
    const pb = bytes / this.bytesPerPb;
    if (pb >= 1) return this.formatQuotaNumber(pb)+" " +'PB' ;
    const tb = bytes / this.bytesPerTb;
    if (tb >= 1) return this.formatQuotaNumber(tb)+" " + 'TB' ;
    const gb = bytes / this.bytesPerGb;
    if (gb >= 1) return this.formatQuotaNumber(gb)+" " + 'GB' ;
    const mb = bytes / this.bytesPerMb;
    return this.formatQuotaNumber(mb) +" " + 'MB' ;
  }

  private formatQuotaNumber(value: number): string {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? `${rounded}` : `${rounded}`;
  }

  private convertToBytes(valueRaw: string, unit: 'MB' | 'GB' | 'TB' | 'PB' | 'EB'): number {
    const value = Number(valueRaw);
    if (!Number.isFinite(value) || value < 0) return -1;
    const multiplier = unit === 'EB'
      ? this.bytesPerEb
      : unit === 'PB'
      ? this.bytesPerPb
      : unit === 'TB'
      ? this.bytesPerTb
      : unit === 'MB'
      ? this.bytesPerMb
      : this.bytesPerGb;
    return Math.round(value * multiplier);
  }
}
